import React, { FC, useState, useMemo, useEffect, useRef } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import AddRestHourModal from './AddRestHourModal'
import {
  getCrewList,
  getCrewUnit,
  getCrewDocument,
  getCourseCertificates,
  getSeaServices,
  getRestHourEntriesInRange,
  getRollingMatrixSummaries,
  getVesselList,
  saveRestHourEntries,
  deleteWorkingSlots,
  approveRestDay,
  getCompanyList,
  getCompanyAdminList,
  getRanksforList,
  patchSummaryRemarks,
  recomputeDailySummary,
  Regulation,
  previewRestHourViolations,
  getSignedOnCrewList,
} from '../core/_requests'
import { useAuth } from '../../auth'
import {
  CREWING_READ_PERMISSIONS,
  useCrewingCanRead,
  useCrewingPermissionRevision,
} from '../../Crewing/core/crewingPermissionAccess'
import {
  Crew,
  CrewDocumentResponse,
  CourseCertificateResponse,
  Vessel,
  ActiveSignOn,
  RollingDaySummary,
  RollingViolationWindow,
  SeaServiceResponse,
  WorkType,
} from '../core/_models'
import { toast } from 'react-toastify'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { showErrorToast, getApiErrorMessage } from '../../../shared/utils/apiErrorMessage'

// build YYYY-MM-DD array (UTC)
const buildDateKeys = (startISO: string, endISO: string) => {
  const out: string[] = []
  const cur = new Date(startISO + 'T00:00:00Z')
  const end = new Date(endISO + 'T00:00:00Z')
  while (cur <= end) {
    out.push(cur.toISOString().slice(0, 10))
    cur.setUTCDate(cur.getUTCDate() + 1)
  }
  return out
}

const forEachWithConcurrency = async <T,>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<void>
) => {
  let next = 0
  await Promise.all(
    Array.from({length: Math.min(limit, items.length)}, async () => {
      while (next < items.length) {
        const item = items[next++]
        await task(item)
      }
    })
  )
}

const crewDisplayName = (c: any) => c?.crewName || c?.name || `#${c?.crewId || c?.id || ''}`

const windowSummary = (v: any) => {
  if (!v) return ''
  const ws = v.windowStart || ''
  const we = v.windowEnd || ''
  const restH = v.restMinutes != null ? (Number(v.restMinutes) / 60).toFixed(1) : 'n/a'
  const workH =
    v.restMinutes != null ? ((24 * 60 - Number(v.restMinutes)) / 60).toFixed(1) : 'n/a'
  const range = `${ws} -> ${we}`
  return `${range} | rest=${restH}h work=${workH}h code=${v.ruleCode ?? ''}`
}

const windowDebugLines = (dayKey: string, windows: any[]) => {
  const lines: string[] = []
  windows.forEach((w: any) => {
    const ws = w.windowStart || ''
    const we = w.windowEnd || ''
    const restH = w.restMinutes != null ? (Number(w.restMinutes) / 60).toFixed(1) : 'n/a'
    const workH =
      w.restMinutes != null ? ((24 * 60 - Number(w.restMinutes)) / 60).toFixed(1) : 'n/a'
    const startsToday = ws.startsWith(dayKey)
    const overlaps =
      (w.impactedCrewDays && w.impactedCrewDays.includes(dayKey)) ||
      (w.impactedDates && w.impactedDates.includes(dayKey))
    lines.push(
      [
        `DATE: ${dayKey}`,
        `RULE: ${w.ruleCode ?? ''}`,
        `WINDOW: ${ws} -> ${we}`,
        `REST_HOURS: ${restH}`,
        `WORK_HOURS: ${workH}`,
        `MAPPED_TO_DAY: ${dayKey}`,
        `REASON_RED: rollingWindowsByDay has active violation`,
        `STARTS_THIS_DAY: ${startsToday}`,
        `OVERLAPS_THIS_DAY: ${overlaps}`,
      ].join('\n')
    )
  })
  return lines.join('\n---\n')
}

const getNestedRecordValue = (record: any, path: string) => {
  return path.split('.').reduce<any>((acc, key) => {
    if (acc === null || acc === undefined) return undefined
    return acc[key]
  }, record)
}

const readFirstAvailableValue = (record: any, paths: string[]) => {
  for (const path of paths) {
    const value = getNestedRecordValue(record, path)
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return String(value)
    }
  }
  return '-'
}

type MatrixProfileFieldKey =
  | 'nationality'
  | 'certificateOfCompetency'
  | 'issuingCountry'
  | 'administrationAcceptance'
  | 'tankerCertification'
  | 'specializeTankerTraining'
  | 'radioQualification'
  | 'yearsWithOperator'
  | 'yearsInRank'
  | 'yearsOnThisTypeOfTanker'
  | 'yearsAsWatchKeepingOfficer'
  | 'dateJoinedThisVessel'

type MatrixProfileFieldDef = {
  key: MatrixProfileFieldKey
  label: string
  manualEntry?: boolean
  inputType?: 'text' | 'date'
  options?: string[]
}

const MATRIX_PROFILE_FIELDS: MatrixProfileFieldDef[] = [
  { key: 'nationality', label: 'Nationality' },
  { key: 'certificateOfCompetency', label: 'Certificate of Competency' },
  { key: 'issuingCountry', label: 'Issuing Country' },
  { key: 'administrationAcceptance', label: 'Administration Acceptance' },
  { key: 'tankerCertification', label: 'Tanker Certification' },
  {
    key: 'specializeTankerTraining',
    label: 'Specialize Tanker Training',
    options: ['Basic', 'Advance'],
  },
  { key: 'radioQualification', label: 'Radio qualification', manualEntry: true, inputType: 'text' },
  { key: 'yearsWithOperator', label: 'Years with Operator', manualEntry: true, inputType: 'text' },
  { key: 'yearsInRank', label: 'Years in rank', manualEntry: true, inputType: 'text' },
  {
    key: 'yearsOnThisTypeOfTanker',
    label: 'Years on this type of Tanker',
    manualEntry: true,
    inputType: 'text',
  },
  {
    key: 'yearsAsWatchKeepingOfficer',
    label: 'Years as Watch keeping officer',
    manualEntry: true,
    inputType: 'text',
  },
  {
    key: 'dateJoinedThisVessel',
    label: 'Date Joined this Vessel',
    manualEntry: true,
    inputType: 'date',
  },
]

const normalizeText = (value: any) => String(value ?? '').trim()

const normalizeRankToken = (value: any) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const parseMonthsDaysToTotalDays = (value: any) => {
  const raw = normalizeText(value)
  if (!raw) return 0

  const monthMatch = raw.match(/(\d+)\s*(month\(s\)|months|month|mon|mth|m)/i)
  const dayMatch = raw.match(/(\d+)\s*(day\(s\)|days|day|d)/i)

  let months = monthMatch ? Number(monthMatch[1]) : 0
  let days = dayMatch ? Number(dayMatch[1]) : 0

  if (!monthMatch && !dayMatch) {
    const nums = (raw.match(/\d+/g) || []).map(Number)
    if (nums.length >= 2) {
      months = nums[0]
      days = nums[1]
    } else if (nums.length === 1) {
      months = nums[0]
    }
  }

  return months * 30 + days
}

const dateDiffInDaysInclusive = (fromDate?: string | null, toDate?: string | null) => {
  if (!fromDate || !toDate) return 0
  const from = new Date(fromDate)
  const to = new Date(toDate)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) return 0
  return Math.floor((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000)) + 1
}

const formatMonthsDays = (totalDays: number) => {
  if (!totalDays || totalDays <= 0) return '-'
  const months = Math.floor(totalDays / 30)
  const days = totalDays % 30
  return `${months} Months ${days} Days`
}

const deriveInRankExperience = (record: any, _currentRankLabel: string, seaServiceRows?: any[]) => {
  const rows = Array.isArray(seaServiceRows) && seaServiceRows.length > 0
    ? seaServiceRows
    : Array.isArray(record?.seaServiceHistory)
      ? record.seaServiceHistory
      : Array.isArray(record?.crewData?.seaServiceHistory)
        ? record.crewData.seaServiceHistory
        : []
  const firstRankEntry = rows.find((entry: any) => normalizeRankToken(entry?.rank))
  const targetRank = normalizeRankToken(firstRankEntry?.rank)
  if (!targetRank) return '-'

  let totalDays = 0

  rows.forEach((entry: any) => {
    const entryRank = normalizeRankToken(entry?.rank)
    if (!entryRank) return
    if (!(entryRank === targetRank || entryRank.includes(targetRank) || targetRank.includes(entryRank))) {
      return
    }

    const fromTotalText = parseMonthsDaysToTotalDays(entry?.totalMonthsDays)
    if (fromTotalText > 0) {
      totalDays += fromTotalText
      return
    }

    totalDays += dateDiffInDaysInclusive(entry?.fromDate, entry?.toDate)
  })

  return formatMonthsDays(totalDays)
}

const deriveCertificateOfCompetency = (record: any) => {
  const structured = readFirstAvailableValue(record, [
    'crewDocuments.cocNumber',
    'crewData.crewDocuments.cocNumber',
    'documents.cocNumber',
    'crewData.documents.cocNumber',
    'cocNumber',
    'crewDocuments.cocGradeLevel',
    'crewData.crewDocuments.cocGradeLevel',
    'documents.cocGradeLevel',
    'crewData.documents.cocGradeLevel',
    'certificateOfCompetency',
  ])
  if (structured !== '-') return structured

  const docs = Array.isArray(record?.documents) ? record.documents : []
  const cocDoc = docs.find((doc: any) => {
    const slot = String(doc?.slot ?? '').toUpperCase()
    const name = String(doc?.documentName ?? '').toLowerCase()
    return slot === 'COC' || name.includes('certificate of competency') || name.includes('coc')
  })
  if (!cocDoc) return '-'

  const cocNumber = normalizeText(cocDoc?.number ?? cocDoc?.certificateNumber ?? cocDoc?.cocNumber)
  if (cocNumber) return cocNumber

  const grade = normalizeText(cocDoc?.gradeOrLevel ?? cocDoc?.gradeLevel ?? cocDoc?.level)
  return grade || '-'
}

const deriveTankerCertification = (record: any) => {
  const courses = Array.isArray(record?.courses) ? record.courses : []
  const tankerCourseCerts = courses
    .filter((course: any) => {
      const hay = `${course?.certificateName ?? ''} ${course?.category ?? ''}`.toLowerCase()
      return /oil|chemical|chem|gas|tanker/.test(hay)
    })
    .map((course: any) => normalizeText(course?.certificateNumber))
    .filter(Boolean)

  if (tankerCourseCerts.length > 0) {
    return Array.from(new Set(tankerCourseCerts)).join(', ')
  }

  const docs = record?.crewDocuments ?? record?.documents ?? {}
  const docCerts = [docs?.oilEndorsementNumber, docs?.chemEndorsementNumber, docs?.gasEndorsementNumber]
    .map((value: any) => normalizeText(value))
    .filter(Boolean)

  const docUploads = Array.isArray(record?.documents)
    ? record.documents
      .filter((doc: any) => {
        const slot = String(doc?.slot ?? '').toUpperCase()
        const name = String(doc?.documentName ?? '').toLowerCase()
        return (
          slot === 'OIL_ENDORSEMENT' ||
          slot === 'CHEM_ENDORSEMENT' ||
          slot === 'GAS_ENDORSEMENT' ||
          /oil|chemical|chem|gas|tanker/.test(name)
        )
      })
      .map((doc: any) => normalizeText(doc?.certificateNumber ?? doc?.documentName))
      .filter(Boolean)
    : []

  const merged = Array.from(new Set([...docCerts, ...docUploads]))
  return merged.length ? merged.join(', ') : '-'
}

const deriveSpecializedTraining = (record: any) => {
  const direct = readFirstAvailableValue(record, [
    'specializeTankerTraining',
    'additionalInfo.specializeTankerTraining',
  ])
  if (direct !== '-') return direct

  const courses = Array.isArray(record?.courses) ? record.courses : []
  const hasAdvance = courses.some((course: any) =>
    `${course?.certificateName ?? ''} ${course?.category ?? ''}`.toLowerCase().includes('advance')
  )
  if (hasAdvance) return 'Advance'
  const hasBasic = courses.some((course: any) =>
    `${course?.certificateName ?? ''} ${course?.category ?? ''}`.toLowerCase().includes('basic')
  )
  if (hasBasic) return 'Basic'
  return '-'
}

const deriveAdministrationAcceptance = (record: any) => {
  const explicit = readFirstAvailableValue(record, [
    'administrationAcceptance',
    'additionalInfo.administrationAcceptance',
  ])
  if (explicit !== '-') return explicit

  const docValid = record?.additionalInfo?.declarationDocumentsValid
  if (typeof docValid === 'boolean') return docValid ? 'Yes' : 'No'
  return 'Yes'
}

const deriveMatrixProfileFieldValue = (record: any, key: MatrixProfileFieldKey) => {
  switch (key) {
    case 'nationality':
      return readFirstAvailableValue(record, ['nationality'])
    case 'certificateOfCompetency':
      return deriveCertificateOfCompetency(record)
    case 'issuingCountry':
      return readFirstAvailableValue(record, ['nationality'])
    case 'administrationAcceptance':
      return deriveAdministrationAcceptance(record)
    case 'tankerCertification':
      return deriveTankerCertification(record)
    case 'specializeTankerTraining':
      return deriveSpecializedTraining(record)
    case 'radioQualification':
      return readFirstAvailableValue(record, [
        'radioQualification',
        'crewDocuments.gmdssGradeLevel',
        'crewDocuments.gmdssEndorsementGradeLevel',
      ])
    case 'yearsWithOperator':
      return readFirstAvailableValue(record, ['yearsWithOperator', 'additionalInfo.yearsWithOperator'])
    case 'yearsInRank':
      return readFirstAvailableValue(record, ['yearsInRank', 'additionalInfo.yearsInRank'])
    case 'yearsOnThisTypeOfTanker':
      return readFirstAvailableValue(record, [
        'yearsOnThisTypeOfTanker',
        'additionalInfo.yearsOnThisTypeOfTanker',
      ])
    case 'yearsAsWatchKeepingOfficer':
      return readFirstAvailableValue(record, [
        'yearsAsWatchKeepingOfficer',
        'additionalInfo.yearsAsWatchKeepingOfficer',
      ])
    case 'dateJoinedThisVessel':
      return readFirstAvailableValue(record, [
        'dateOfAvailability',
        'additionalInfo.availabilityDate',
        'dateJoinedThisVessel',
      ])
    default:
      return '-'
  }
}

// flatten violations for Excel sheet
const flattenViolations = (crewList: any[], dateKeys: string[], hoursMap: Record<string, any>) => {
  const rows: Array<{ Crew: string; Date: string; Violation: string }> = []
  for (const c of crewList) {
    for (const d of dateKeys) {
      const cell = hoursMap[`${c.crewId}-${d}`]
      if (!cell || !Array.isArray(cell.violations) || !cell.violations.length) continue
      cell.violations.forEach((desc: string) => {
        rows.push({ Crew: crewDisplayName(c), Date: d, Violation: desc })
      })
    }
  }
  return rows
}

// read working flag from api entry
const entryIsWorking = (e: any) => Boolean(e?.isWorking ?? e?.working)

const overlapDates = (ws?: string | Date | null, we?: string | Date | null): string[] => {
  if (!ws || !we) return []
  const start = ws instanceof Date ? ws : new Date(ws)
  const end = we instanceof Date ? we : new Date(we)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return []
  const out: string[] = []
  // iterate by day, include a day if window overlaps that day interval [dayStart, dayEnd)
  const cursor = new Date(start)
  cursor.setHours(0, 0, 0, 0)
  while (cursor < end) {
    const dayStart = new Date(cursor)
    const dayEnd = new Date(cursor)
    dayEnd.setDate(dayEnd.getDate() + 1)
    if (start < dayEnd && end > dayStart) {
      out.push(dayStart.toISOString().slice(0, 10))
    }
    cursor.setDate(cursor.getDate() + 1)
  }
  return out
}

type AnchoredTotalsMap = Record<
  string,
  {
    work: number
    rest: number
    compliant: boolean
    violationCount: number
    violations: string[]
    violationCodes?: string[]
    approved: boolean
    summaryId: number | null
    remarks: string
  }
>

// Build anchor-aware day totals using backend preview API (same logic as Update tab)
const deriveAnchoredDayTotals = async ({
  apiEntries,
  fromDate,
  toDate,
  reportingMode,
  customAnchorHHmm,
  signOnStartISO,
  crewId,
  vesselId,
  regulation,
  timeSlots,
}: {
  apiEntries: any[]
  fromDate: string
  toDate: string
  reportingMode: ReportingAnchorMode
  customAnchorHHmm: string
  signOnStartISO: string | null
  crewId: number
  vesselId: number
  regulation: Regulation
  timeSlots: any[]
}): Promise<AnchoredTotalsMap> => {
  const dateKeys = buildDateKeys(fromDate, toDate)
  const extendedKeys = new Set<string>()
  dateKeys.forEach((dk) => {
    extendedKeys.add(dk)
    const dt = new Date(dk + 'T00:00:00Z')
    const prev = new Date(dt)
    prev.setUTCDate(prev.getUTCDate() - 1)
    const next = new Date(dt)
    next.setUTCDate(next.getUTCDate() + 1)
    extendedKeys.add(prev.toISOString().slice(0, 10))
    extendedKeys.add(next.toISOString().slice(0, 10))
  })
  // cover 7d context
  if (dateKeys.length) {
    const coverStart = new Date(dateKeys[0] + 'T00:00:00Z')
    coverStart.setUTCDate(coverStart.getUTCDate() - 6)
    const coverEnd = new Date(dateKeys[dateKeys.length - 1] + 'T00:00:00Z')
    coverEnd.setUTCDate(coverEnd.getUTCDate() + 1)
    const cursor = new Date(coverStart)
    while (cursor <= coverEnd) {
      extendedKeys.add(cursor.toISOString().slice(0, 10))
      cursor.setUTCDate(cursor.getUTCDate() + 1)
    }
  }
  const allKeys = Array.from(extendedKeys).sort()
  const slots: { entryDate: string; slotIndex: number; workType?: WorkType }[] = []
  allKeys.forEach((dk) => {
    for (let i = 0; i < timeSlots.length; i++) {
      const apiMatch = apiEntries.find((e) => e.entryDate === dk && e.slotIndex === i)
      if (apiMatch && entryIsWorking(apiMatch)) {
        slots.push({ entryDate: dk, slotIndex: i, workType: 'NORMAL' })
      }
    }
  })

  const payload = {
    start: allKeys[0] ?? fromDate,
    end: allKeys[allKeys.length - 1] ?? toDate,
    crewId,
    vesselId,
    regulation,
    slots,
  }
  const preview = await previewRestHourViolations(payload)
  const out: AnchoredTotalsMap = {}
  const summaries = preview?.daySummaries ?? []
  summaries.forEach((ds: any) => {
    if (ds.date < fromDate || ds.date > toDate) return
    out[`${crewId}-${ds.date}`] = {
      work: Number(ds.workMinutes ?? 0) / 60,
      rest: Number(ds.restMinutes ?? 0) / 60,
      compliant: (ds.violationCount ?? 0) === 0,
      violationCount: ds.violationCount ?? 0,
      violations: [],
      violationCodes: [],
      approved: false,
      summaryId: null,
      remarks: '',
    }
  })
  return out
}

// Format in reporting timezone (vessel/sign-on/custom anchor) to match calendar grid
const formatInReportingTz = (value?: string | Date | null, timeZone?: string | null) => {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  const tz = timeZone && timeZone.trim() ? timeZone : undefined
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz,
    }).format(date)
  } catch {
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }
}

type ViolationFormatOptions = {
  reportingTz?: string | null
  reportingMode?: ReportingAnchorMode
  customAnchorHHmm?: string | null
  signOnStartISO?: string | null
}

const humanizeViolation = (v: RollingViolationWindow, opts?: ViolationFormatOptions): string => {
  const code = (v?.ruleCode ?? '').toUpperCase()
  const start = v?.windowStart || ''
  const end = v?.windowEnd || ''
  const windowLabel = start && end ? `${start} -> ${end}` : ''
  const restHours = v?.restMinutes != null ? (v.restMinutes / 60).toFixed(1) : null
  const workHours =
    v?.restMinutes != null ? ((24 * 60 - Number(v.restMinutes)) / 60).toFixed(1) : null
  const longestRest = v?.maxRestBlockMinutes != null ? (v.maxRestBlockMinutes / 60).toFixed(1) : null

  switch (code) {
    case 'REST_24H_MIN_10':
      return `Minimum 10 hours rest required in this 24-hour window. Found ${restHours ?? 'insufficient'} hours between ${windowLabel}.`
    case 'REST_GAP_MAX_14':
      return `Maximum interval between rest periods is 14 hours. Interval exceeded during ${windowLabel}.`
    case 'REST_SPLIT_MAX_2':
      return `Rest was divided into more than 2 periods in this 24-hour window (${windowLabel}).`
    case 'REST_ONE_BLOCK_MIN_6':
      return `A single continuous rest period of at least 6 hours is required. Longest block was ${longestRest ?? 'under 6'} hours within ${windowLabel}.`
    case 'REST_SECOND_BLOCK_MIN_4':
      return `Split rest: need one continuous rest block ≥ 6h and another ≥ 4h, unless a single continuous block ≥ 10h exists in this window (${windowLabel}).`
    case 'REST_7D_MIN_77':
    case 'STCW_MIN_REST_7D':
      return `Minimum 77 hours rest required in 7 days. Found ${restHours ?? 'insufficient'} hours between ${windowLabel}.`
    case 'REST_7D_MIN_70':
      return `Minimum 70 hours rest (temporary allowance) required in 7 days. Found ${restHours ?? 'insufficient'} hours between ${windowLabel}.`
    case 'MLC_MAX_WORK_24H':
      return `Work must not exceed 14 hours in any 24-hour window. Worked ${workHours ?? 'over limit'} hours between ${windowLabel}.`
    case 'MLC_MAX_WORK_7D':
      return `Work must not exceed 72 hours in 7 days. Worked ${workHours ?? 'over limit'} hours between ${windowLabel}.`
    case 'MLC_MIN_REST_7D':
      return `Minimum 77 hours rest required in 7 days. Found ${restHours ?? 'insufficient'} hours between ${windowLabel}.`
    case 'MLC_MIN_REST_7D_FLOOR70':
      return `Minimum 70 hours rest (temporary allowance) required in 7 days. Found ${restHours ?? 'insufficient'} hours between ${windowLabel}.`
    default:
      return `${code || 'Rest rule'} violated between ${windowLabel}.`
  }
}

// Shared sticky column widths (used in both Update tab previews and Matrix Overview exports)
const SR_STICKY_W = 70 // SR/NO
const NAME_STICKY_W = 170
const RANK_STICKY_W = 170
const VESSEL_STICKY_W = 170
const NAME_STICKY_LEFT = SR_STICKY_W
const RANK_STICKY_LEFT = SR_STICKY_W + NAME_STICKY_W
const VESSEL_STICKY_LEFT = SR_STICKY_W + NAME_STICKY_W + RANK_STICKY_W
const LEFT_FREEZE_W = SR_STICKY_W + NAME_STICKY_W + RANK_STICKY_W + VESSEL_STICKY_W

// Legacy rolling tooltip helpers (kept for reference).
// const rollingWindowLabels: Record<string, string> = {
//   ROLLING_24H: 'Rolling 24h',
//   ROLLING_72H: 'Rolling 72h',
//   ROLLING_7D: 'Rolling 7d',
// }
//
// const formatRollingDateTime = (value?: string | null) => {
//   if (!value) return ''
//   const date = new Date(value)
//   if (Number.isNaN(date.getTime())) return String(value)
//   return date.toLocaleString('en-GB', {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: '2-digit',
//     minute: '2-digit',
//   })
// }
//
// const buildRollingTooltip = (data: RollingExceptionResponse, asOf?: string | null) => {
//   const header = asOf ? `Rolling as of ${formatRollingDateTime(asOf)}` : 'Rolling exceptions'
//   const lines = (data.windows ?? []).map((rollingWindow) => {
//     const label = rollingWindowLabels[rollingWindow.code] ?? rollingWindow.code
//     const range = `${formatRollingDateTime(rollingWindow.windowStart)} -> ${formatRollingDateTime(
//       rollingWindow.windowEnd
//     )}`
//     const status = rollingWindow.violated ? 'VIOLATION' : 'OK'
//     const base = `${label}: ${status} (${range})`
//     if (rollingWindow.exceptions && rollingWindow.exceptions.length) {
//       return `${base}\n${rollingWindow.exceptions.join('; ')}`
//     }
//     return base
//   })
//   return [header, ...lines].join('\n')
// }

// --- Rank sorting helpers (share same spec as SignPage) ---
const RANK_ORDER = [
  'MASTER', 'CHIEF OFFICER', 'SECOND OFFICER', 'THIRD OFFICER', 'DECK CADET',
  'CHIEF ENGINEER', 'SECOND ENGINEER', 'THIRD ENGINEER', 'FOURTH ENGINEER',
  'TRAINEE MARINE ENGINEER', 'ELECTRICAL OFFICER', 'BOSUN', 'PUMPMAN',
  'ABLE SEAMAN', 'ORDINARY SEAMAN', 'TR. SEAMAN', 'OILER', 'WIPER', 'TR WIPER',
  'FITTER', 'CHIEF COOK', 'GENERAL STEWARD',
] as const;

const RANK_REGEX: Record<string, RegExp[]> = {
  MASTER: [/(^|\s)master\b/i, /\bcaptain\b/i, /\b\(captain\)\b/i],
  'CHIEF OFFICER': [/chief\s*officer/i, /\bc\s*\/\s*o\b/i],
  'SECOND OFFICER': [/second\s*officer/i, /\b2\s*\/\s*o\b/i],
  'THIRD OFFICER': [/third\s*officer/i, /\b3\s*\/\s*o\b/i],
  'DECK CADET': [/deck\s*cadet/i],
  'CHIEF ENGINEER': [/chief\s*engineer/i, /\bc\s*\/\s*e\b/i],
  'SECOND ENGINEER': [/second\s*engineer/i, /\b2\s*\/\s*e\b/i],
  'THIRD ENGINEER': [/third\s*engineer/i, /\b3\s*\/\s*e\b/i],
  'FOURTH ENGINEER': [/fourth\s*engineer/i, /\b4\s*\/\s*e\b/i, /fouth\s*engineer/i],
  'TRAINEE MARINE ENGINEER': [/engine\s*cadet/i, /trainee\s*marine\s*engineer/i, /\btme\b/i],
  'ELECTRICAL OFFICER': [/electrician/i, /electrical/i, /\beto\b/i],
  BOSUN: [/\bbosun\b/i, /boatswain/i],
  PUMPMAN: [/pumpman/i],
  'ABLE SEAMAN': [/able\s*seaman/i, /\bab\b(?![a-z])/i],
  'ORDINARY SEAMAN': [/ordinary\s*seaman/i, /\bos\b(?![a-z])/i],
  'TR. SEAMAN': [/trainee.*seaman/i, /\btr\.?\s*seaman\b/i],
  OILER: [/oiler/i, /motorman/i],
  WIPER: [/wiper\b/i],
  'TR WIPER': [/trainee.*wiper/i, /\btr\.?\s*wiper\b/i],
  FITTER: [/fitter\b/i],
  'CHIEF COOK': [/chief\s*cook/i],
  'GENERAL STEWARD': [/steward/i, /messman/i],
};

const safeRankString = (label?: any): string => {
  if (label == null) return '';
  if (typeof label === 'string') return label;
  if (typeof label === 'number') return String(label);
  if (typeof label === 'object') {
    return String(
      label.rank ??
      label.rankName ??
      label.name ??
      label.label ??
      label.title ??
      ''
    );
  }
  return String(label);
};

const getCanonicalRank = (label?: any): string => {
  const s = safeRankString(label).trim();
  if (!s) return 'OTHER';
  for (const [canon, patterns] of Object.entries(RANK_REGEX)) {
    if (patterns.some(rx => rx.test(s))) return canon;
  }
  return 'OTHER';
};

const getRankSuffix = (label?: any): number => {
  const s = safeRankString(label).trim();
  const m = s.match(/-\s*(\d+)\s*$/);
  return m ? Number(m[1]) : 0;
};

const rankTuple = (label?: any): [number, number, number, string] => {
  const s = safeRankString(label);
  const canon = getCanonicalRank(s);
  const groupIdx = canon === 'OTHER' ? 999 : RANK_ORDER.indexOf(canon as any) + 1;
  const suffix = getRankSuffix(s);
  const baseFirstFlag = suffix === 0 ? 0 : 1;
  return [groupIdx, baseFirstFlag, suffix, s];
};

const compareRank = (a?: string, b?: string, dir: 'asc' | 'desc' = 'asc'): number => {
  const A = rankTuple(a);
  const B = rankTuple(b);
  const cmp = (A[0] - B[0]) || (A[1] - B[1]) || (A[2] - B[2]) || A[3].localeCompare(B[3]);
  return dir === 'asc' ? cmp : -cmp;
};

// === CONFIG ===
const ROLLING_WINDOW_DAYS = 7;
const UPDATE_EDITABLE_LOOKBACK_DAYS = ROLLING_WINDOW_DAYS + 1; // +1 day to cover rolling window across midnight

// returns {fromISO, toISO} for last N days ending today (UTC)
const getLastNDaysRange = (n: number) => {
  const to = new Date()
  to.setUTCHours(0, 0, 0, 0)
  const from = new Date(to)
  from.setUTCDate(from.getUTCDate() - (n - 1))
  return {
    fromISO: from.toISOString().slice(0, 10),
    toISO: to.toISOString().slice(0, 10),
  }
}

const validateExceptionWindow = (
  crewIdForCheck: number,
  selected: Set<string>,
  dateKeysForCheck: string[]
): boolean => {
  if (!dateKeysForCheck.length) return true
  const flags = dateKeysForCheck.map((dk) => (selected.has(`${crewIdForCheck}-${dk}`) ? 1 : 0))
  let windowSum = 0
  for (let i = 0; i < flags.length; i++) {
    windowSum += flags[i]
    if (i >= 7) windowSum -= flags[i - 7]
    if (i >= 6 && windowSum > 2) return false
  }
  return true
}

const canAddException = (
  crewIdForCheck: number,
  dateKey: string,
  dateKeysForCheck: string[],
  selected: Set<string>
): boolean => {
  if (!crewIdForCheck) return false
  if (!dateKeysForCheck.length) return false
  const next = new Set(selected)
  next.add(`${crewIdForCheck}-${dateKey}`)
  return validateExceptionWindow(crewIdForCheck, next, dateKeysForCheck)
}

const notify = {
  success: (msg: string) => toast.success(msg, { autoClose: 4000 }),
  error: (msg: string) => toast.error(msg, { autoClose: 6000 }),
  warn: (msg: string) => toast.warn(msg, { autoClose: 4000 }),
  info: (msg: string) => toast.info(msg, { autoClose: 4000 }),
}

const asApiError = (err: any, fallback = 'Something went wrong', hint: any = 'general') =>
  getApiErrorMessage(err, fallback, hint)

const getDatesInRange = (startDate: string, endDate: string): Date[] => {
  const dates: Date[] = []
  // Use UTC to avoid timezone issues when incrementing dates
  const currentDate = new Date(startDate + 'T00:00:00Z')
  const lastDate = new Date(endDate + 'T00:00:00Z')

  while (currentDate <= lastDate) {
    dates.push(new Date(currentDate))
    currentDate.setUTCDate(currentDate.getUTCDate() + 1)
  }
  return dates
}

const VIOLATION_CELL_STYLE = { background: '#ff3b30', color: '#fff', fontWeight: 600 }
const WEEKLY_MIN_REST_HRS = 77

type ReportingAnchorMode = 'CALENDAR_DAY' | 'SIGN_ON_ANCHOR' | 'CUSTOM_ANCHOR'

const REPORTING_MODE_STORAGE_KEY = 'resthour_reporting_anchor_mode'
const REPORTING_TIME_STORAGE_KEY = 'resthour_reporting_anchor_time'

const parseHHmm = (value?: string | null): { hours: number; minutes: number } | null => {
  if (!value) return null
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (Number.isNaN(hours) || Number.isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59)
    return null
  return { hours, minutes }
}

const getHHmmFromIsoUtc = (iso?: string | null): string | null => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const hh = d.getUTCHours().toString().padStart(2, '0')
  const mm = d.getUTCMinutes().toString().padStart(2, '0')
  return `${hh}:${mm}`
}

const formatHHmmInZone = (iso?: string | null, tz?: string | null): string | null => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  try {
    return d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz || 'UTC',
    })
  } catch (err) {
    return null
  }
}

const isValidTz = (tz?: string | null) => {
  if (!tz || !tz.trim()) return false
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

const parseTzOffsetMinutes = (dateKey: string, hhmm: string, timeZone?: string | null): number => {
  if (!timeZone) return 0
  const [yyyy, mm, dd] = dateKey.split('-').map(Number)
  const [h, m] = (hhmm || '00:00').split(':').map(Number)
  const utcGuess = new Date(Date.UTC(yyyy || 1970, (mm || 1) - 1, dd || 1, h || 0, m || 0, 0, 0))
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      timeZoneName: 'short', // yields strings like GMT+05:30
    }).formatToParts(utcGuess)
    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value || 'GMT'
    const match = tzName.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/)
    if (!match) return 0
    const sign = match[1] === '-' ? -1 : 1
    const hours = Number(match[2] || 0)
    const mins = Number(match[3] || 0)
    return sign * (hours * 60 + mins)
  } catch {
    return 0
  }
}

const anchorToUtc = (dateKey: string, hhmm: string, timeZone?: string | null): Date => {
  const [yyyy, mm, dd] = dateKey.split('-').map(Number)
  const [h, m] = (hhmm || '00:00').split(':').map(Number)
  const baseUtcMs = Date.UTC(yyyy || 1970, (mm || 1) - 1, dd || 1, h || 0, m || 0, 0, 0)
  const offsetMinutes = parseTzOffsetMinutes(dateKey, hhmm, timeZone)
  return new Date(baseUtcMs - offsetMinutes * 60 * 1000)
}

function getReportingWindowUtc(
  dateKey: string,
  mode: ReportingAnchorMode,
  signOnIso?: string | null,
  customHHmm?: string,
  timeZone?: string | null
): { startUtc: Date; endUtc: Date; label: string } {
  const base = new Date(`${dateKey}T00:00:00Z`)
  const addAnchor = (hhmm: string | null): { startUtc: Date; endUtc: Date; label: string } => {
    const parsed = parseHHmm(hhmm || '00:00') ?? { hours: 0, minutes: 0 }
    const startUtc = anchorToUtc(dateKey, `${parsed.hours.toString().padStart(2, '0')}:${parsed.minutes
      .toString()
      .padStart(2, '0')}`, timeZone)
    const endUtc = new Date(startUtc.getTime() + 24 * 60 * 60 * 1000)
    const label = `${parsed.hours.toString().padStart(2, '0')}:${parsed.minutes
      .toString()
      .padStart(2, '0')}->${parsed.hours.toString().padStart(2, '0')}:${parsed.minutes
        .toString()
        .padStart(2, '0')}`
    return { startUtc, endUtc, label }
  }

  if (mode === 'SIGN_ON_ANCHOR') {
    const hhmm = getHHmmFromIsoUtc(signOnIso)
    return addAnchor(hhmm)
  }
  if (mode === 'CUSTOM_ANCHOR') {
    return addAnchor(customHHmm ?? '00:00')
  }
  return addAnchor('00:00')
}

const formatHHmm = (dt: Date) =>
  `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`

const intervalsOverlap = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean => {
  return aStart.getTime() < bEnd.getTime() && bStart.getTime() < aEnd.getTime()
}

const overlapDurationMs = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): number => {
  const start = Math.max(aStart.getTime(), bStart.getTime())
  const end = Math.min(aEnd.getTime(), bEnd.getTime())
  return Math.max(0, end - start)
}

const debugMode =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('debug') === '1'

const getReportingWindowLabel = ({
  mode,
  time,
  signOnDateTime,
  signOnTimezone,
}: {
  mode: ReportingAnchorMode
  time?: string | null
  signOnDateTime?: string | null
  signOnTimezone?: string | null
}) => {
  if (mode === 'CUSTOM_ANCHOR' && time) {
    return `${time}?${time}`
  }
  if (mode === 'SIGN_ON_ANCHOR') {
    const hhmm = formatHHmmInZone(signOnDateTime, signOnTimezone || 'UTC') || '00:00'
    return `${hhmm}?${hhmm}`
  }
  return '00:00?00:00'
}

type ViolationSet = {
  minRest24h: { value: number; isViolation: boolean }
  minRest7d: { value: number; isViolation: boolean }
  maxInterval: { value: number; isViolation: boolean }
  maxPeriods: { value: number; isViolation: boolean }
  minSingleRest: { value: number; isViolation: boolean }
}

type MLCViolationSet = {
  maxWork24h: { value: number; isViolation: boolean }
  maxWork7d: { value: number; isViolation: boolean }
  minRest7d?: { value: number; isViolation: boolean }
}

// Simple date picker with per-day indicators (reversible: set USE_CUSTOM_DATE_PICKER=false to restore native inputs)
const USE_CUSTOM_DATE_PICKER = true

type DatePickerWithIndicatorsProps = {
  value: string
  onChange: (val: string) => void
  min?: string
  max?: string
  entryDates?: Set<string>
  placeholder?: string
  className?: string
  forceNative?: boolean
}

const pad2 = (n: number) => n.toString().padStart(2, '0')
const toISO = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`

const DatePickerWithIndicators: FC<DatePickerWithIndicatorsProps> = ({
  value,
  onChange,
  min,
  max,
  entryDates,
  placeholder,
  className,
  forceNative = false,
}) => {
  const [open, setOpen] = useState(false)
  const today = new Date()
  const initialDate = value ? new Date(value + 'T00:00:00Z') : today
  const [viewYear, setViewYear] = useState(initialDate.getUTCFullYear())
  const [viewMonth, setViewMonth] = useState(initialDate.getUTCMonth()) // 0-based
  const ref = React.useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // sync view when value changes externally
  useEffect(() => {
    if (value) {
      const d = new Date(value + 'T00:00:00Z')
      if (!Number.isNaN(d.getTime())) {
        setViewYear(d.getUTCFullYear())
        setViewMonth(d.getUTCMonth())
      }
    }
  }, [value])

  const buildMonthDays = () => {
    const first = new Date(Date.UTC(viewYear, viewMonth, 1))
    const startDay = first.getUTCDay() // 0=Sun
    const daysInMonth = new Date(Date.UTC(viewYear, viewMonth + 1, 0)).getUTCDate()
    const cells: Array<{ date: Date; inMonth: boolean }> = []
    // leading blanks from prev month
    for (let i = 0; i < startDay; i++) {
      const d = new Date(Date.UTC(viewYear, viewMonth, i - startDay + 1))
      cells.push({ date: d, inMonth: false })
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: new Date(Date.UTC(viewYear, viewMonth, d)), inMonth: true })
    }
    // trailing to fill rows (up to 42 cells)
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date
      const d = new Date(last)
      d.setUTCDate(d.getUTCDate() + 1)
      cells.push({ date: d, inMonth: false })
    }
    return cells
  }

  const isDisabled = (iso: string) => {
    if (min && iso < min) return true
    if (max && iso > max) return true
    return false
  }

  if (!USE_CUSTOM_DATE_PICKER || forceNative) {
    return (
      <input
        type='date'
        className={className}
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    )
  }

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <input
        readOnly
        className={className}
        value={value || ''}
        placeholder={placeholder}
        onClick={() => setOpen((o) => !o)}
      />
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 2050,
            background: '#fff',
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
            borderRadius: 8,
            padding: 12,
            marginTop: 4,
            width: 260,
            top: '100%',
            left: 0,
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          <div className='d-flex justify-content-between align-items-center mb-2'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => {
                const m = viewMonth === 0 ? 11 : viewMonth - 1
                const y = viewMonth === 0 ? viewYear - 1 : viewYear
                setViewMonth(m)
                setViewYear(y)
              }}
            >
              ‹
            </button>
            <span className='fw-semibold'>
              {new Date(Date.UTC(viewYear, viewMonth, 1)).toLocaleString('en-GB', {
                month: 'long',
                year: 'numeric',
                timeZone: 'UTC',
              })}
            </span>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => {
                const m = viewMonth === 11 ? 0 : viewMonth + 1
                const y = viewMonth === 11 ? viewYear + 1 : viewYear
                setViewMonth(m)
                setViewYear(y)
              }}
            >
              ›
            </button>
          </div>
          <div className='d-grid' style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
              <div key={d} className='text-center text-muted small'>
                {d}
              </div>
            ))}
            {buildMonthDays().map(({ date, inMonth }, idx) => {
              const iso = toISO(date)
              const disabled = isDisabled(iso)
              const hasEntry = entryDates?.has(iso)
              const isSelected = value === iso
              return (
                <button
                  key={idx}
                  type='button'
                  disabled={disabled || !inMonth}
                  className='btn btn-light p-0 text-center'
                  style={{
                    height: 42,
                    border: isSelected ? '2px solid #0d6efd' : '1px solid #e5e5e5',
                    opacity: disabled ? 0.35 : 1,
                    background: inMonth ? '#fff' : '#f8f9fa',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => {
                    onChange(iso)
                    setOpen(false)
                  }}
                >
                  <div style={{ fontSize: 13, color: '#212529', lineHeight: '18px', marginTop: 0 }}>
                    {date.getUTCDate()}
                  </div>
                  {hasEntry && (
                    <div
                      style={{
                        height: 4,
                        width: '60%',
                        background: '#28a745',
                        borderRadius: 4,
                        marginTop: 2,
                      }}
                    />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * workSlots: 48 booleans (true = WORK, false = REST) for the day, 30-min granularity
 */
const checkRestViolations = (workSlots: boolean[]): ViolationSet => {
  const SLOT_HRS = 0.5

  // --- totals ---
  const totalWorkSlots = workSlots.filter(Boolean).length
  const totalRestHrs = 24 - totalWorkSlots * SLOT_HRS

  // --- max continuous WORK (to enforce "interval between two rest periods <= 14h") ---
  let maxContinuousWork = 0
  let curWork = 0
  for (const w of workSlots) {
    if (w) {
      curWork++
    } else {
      if (curWork > maxContinuousWork) maxContinuousWork = curWork
      curWork = 0
    }
  }
  if (curWork > maxContinuousWork) maxContinuousWork = curWork
  const maxIntervalBetweenRests = maxContinuousWork * SLOT_HRS

  return {
    minRest24h: { value: 10, isViolation: totalRestHrs < 10 },
    minRest7d: { value: 77, isViolation: false },
    maxInterval: { value: 14, isViolation: maxIntervalBetweenRests > 14 },
    // Client policy rules (split/second-block) are anchored to rolling 24h windows;
    // keep them neutral here so only backend preview drives UI highlighting.
    maxPeriods: { value: 2, isViolation: false },
    minSingleRest: { value: 6, isViolation: false },
  }
}

// Compute MLC daily/weekly breaches (72h in 7d; 14h/day)
// When exceptions ON, also compute minRest7d >= 70h
const checkMLCViolations = (
  workSlots: boolean[],
  sevenDayWorkTotal: number,
  sevenDayRestTotal: number,
  allowExceptions: boolean
): MLCViolationSet => {
  const SLOT_HRS = 0.5
  const dailyWork = workSlots.filter(Boolean).length * SLOT_HRS
  const v: MLCViolationSet = {
    maxWork24h: { value: 14, isViolation: dailyWork > 14 },
    maxWork7d: { value: 72, isViolation: sevenDayWorkTotal > 72 },
  }
  if (allowExceptions) {
    v.minRest7d = { value: 70, isViolation: sevenDayRestTotal < 70 }
  }
  return v
}

type CompanyGroupOpt = { id: number; name: string }
type SubcompanyOpt = { id: number; name: string; companyGroupId?: number }
type VesselOpt = { id: number; name: string }

interface UpdateTabProps {
  activeTab: string
  fromDate: string
  toDate: string
  signOnStartISO: string | null
  onChangeFromDate: (value: string) => void
  onChangeToDate: (value: string) => void
  onResetRange: () => void
  apiEntries: any[]
  setApiEntries: React.Dispatch<React.SetStateAction<any[]>>
  activeSignOn: ActiveSignOn | null
  selectedRank: string
  selectedCrew: { id: number; name: string } | null
  crewMembers: any[]
  timeSlots: any[]
  // isWorkingHour: (memberId: number, date: Date, timeIndex: number) => boolean;
  toggleWorkingHour: (memberId: number, date: Date, timeIndex: number) => void
  setWorkingHours: (hours: Set<string>) => void
  showTable: boolean // Add this prop
  // onSaveWorkingHours?: (crewMemberId: number, dateRange: Date[]) => void;
  remarks: Map<string, string>
  onRemarksChange: (date: Date, value: string) => void

  // NEW: patch Matrix Overview immediately after save
  onLocalSummaryChange?: (
    crewId: number,
    summaryByDate: Record<
      string,
      {
        work: number
        rest: number
        compliant: boolean
        violationCount: number
        violations: string[]
        violationCodes?: string[]
      }
    >
  ) => void

  // NEW: refresh summaries from server after save
  onAfterSave?: () => Promise<void> | void
  approvedDates?: Set<string>
  getSummaryId?: (crewId: number, dateISO: string) => number | null
  dirtyRemarkDates?: Set<string>
  onResetRemarkEdits?: () => void
  regulation: Regulation
  allowExceptions: boolean
  exceptionOverrides: Set<string>
  reportingMode: ReportingAnchorMode
  customAnchorHHmm: string
  onReportingModeChange: (mode: ReportingAnchorMode) => void
  onCustomAnchorChange: (hhmm: string) => void
  onToggleExceptionDay: (
    crewId: number,
    dateKey: string,
    dateKeys: string[],
    eligible: boolean
  ) => void
}

const UpdateTab: FC<UpdateTabProps> = ({
  activeTab,
  fromDate,
  toDate,
  signOnStartISO,
  onChangeFromDate,
  onChangeToDate,
  onResetRange,
  apiEntries,
  setApiEntries,
  activeSignOn,
  selectedRank,
  selectedCrew,
  crewMembers,
  timeSlots,
  // isWorkingHour,
  toggleWorkingHour,
  setWorkingHours,
  showTable, // Receive the prop from parent
  // onSaveWorkingHours,
  remarks,
  onRemarksChange,
  onLocalSummaryChange,
  onAfterSave,
  approvedDates,
  getSummaryId,
  dirtyRemarkDates,
  onResetRemarkEdits,
  regulation,
  allowExceptions,
  exceptionOverrides,
  reportingMode,
  customAnchorHHmm,
  onReportingModeChange,
  onCustomAnchorChange,
  onToggleExceptionDay,
}) => {
  const [selectedEntries, setSelectedEntries] = React.useState<{ date: string; slotIndex: number }[]>(
    []
  )
  // Track explicit "turn OFF" actions (W -> R)
  const [pendingDeletes, setPendingDeletes] = React.useState<{ date: string; slotIndex: number }[]>(
    []
  )
  const [previewViolationsByDate, setPreviewViolationsByDate] = useState<Record<string, Set<string>>>({})
  const [previewAnchor, setPreviewAnchor] = useState<{ dateTime: string | null; source: string | null }>({
    dateTime: null,
    source: null,
  })
  const [signOnDateTime, setSignOnDateTime] = useState<string | null>(null)
  const [signOnTimezone, setSignOnTimezone] = useState<string>('UTC')
  const [previewDayTotals, setPreviewDayTotals] = useState<Record<string, { workMinutes: number; restMinutes: number }>>({})
  const [previewWeeklyInsufficient, setPreviewWeeklyInsufficient] = useState<boolean>(false)
  const [previewWindowsCount, setPreviewWindowsCount] = useState<number>(0)
  const [rollingDaySummaries, setRollingDaySummaries] = useState<RollingDaySummary[]>([])
  const previewAbortRef = React.useRef<AbortController | null>(null)
  const previewTimerRef = React.useRef<number | null>(null)
  const previewRequestSeq = React.useRef(0)

  const vesselTz = useMemo(() => {
    if (signOnTimezone && signOnTimezone !== 'UTC') return signOnTimezone
    if (activeSignOn?.timezone && activeSignOn.timezone !== 'UTC') return activeSignOn.timezone
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    } catch {
      return 'UTC'
    }
  }, [signOnTimezone, activeSignOn?.timezone])

  // Cache of effective working slots per day (API + local edits)
  const [effectiveWorkingByDate, setEffectiveWorkingByDate] = React.useState<
    Record<string, Set<number>>
  >({})

  const entryDatesForPickers = useMemo(() => {
    const s = new Set<string>()
    apiEntries.forEach((e) => e.entryDate && s.add(e.entryDate))
    selectedEntries.forEach((e) => s.add(e.date))
    pendingDeletes.forEach((e) => s.add(e.date))
    return s
  }, [apiEntries, selectedEntries, pendingDeletes])

  const [error, setError] = useState('')
  const { currentUser, isSuperAdmin, hasPermission } = useAuth()
  const canonicalRankUpdate = getCanonicalRank(currentUser?.rank?.rank ?? (currentUser?.rank as any)?.rank ?? currentUser?.rank)
  const isRankMaster = canonicalRankUpdate === 'MASTER'
  const isMasterByName = isRankMaster

  const vesselId = currentUser?.vessel?.id
  const crewId = currentUser?.roleEntityId
  const roleId = currentUser?.role?.id
  const rankId = currentUser?.rank?.id
  // Operator flavors:
  // - No companyGroupAdminId => acts like Superadmin
  // - Has companyGroupAdminId => acts like Company Group Admin
  const isOperator = roleId === 6
  const operatorActsLikeSuperadmin = isOperator && !currentUser?.companyGroupAdminId
  const operatorActsLikeGroupAdmin = isOperator && !!currentUser?.companyGroupAdminId


  // --- Double-click & drag (paint) state ---
  const SINGLE_CLICK_DELAY = 220; // ms: delay to see if a double click is coming

  const singleClickTimerRef = React.useRef<number | null>(null);
  // We don't need to store the pending click target; we cancel the timer on the 2nd down.

  type BrushState = {
    active: boolean
    date: string
    targetWorking: boolean  // what we are painting to: true = W, false = R
    lastIndex: number       // last slot index we painted to (for gap fill)
  }
  const brushRef = React.useRef<BrushState>({
    active: false,
    date: '',
    targetWorking: false,
    lastIndex: -1,
  })

  useEffect(() => {
    const endBrush = () => {
      if (brushRef.current.active) {
        brushRef.current.active = false
        brushRef.current.date = ''
        brushRef.current.lastIndex = -1
      }
    }
    window.addEventListener('mouseup', endBrush)
    window.addEventListener('mouseleave', endBrush)
    return () => {
      window.removeEventListener('mouseup', endBrush)
      window.removeEventListener('mouseleave', endBrush)
    }
  }, [])


  // Crew users default to self, but masters (by rank) may target selected crew even with roleId=4
  const effectiveCrewId =
    roleId === 4
      ? (isRankMaster && selectedCrew?.id ? selectedCrew.id : crewId)
      : isRankMaster && selectedCrew?.id
        ? selectedCrew.id
        : crewId

  // stick with current user’s vessel (adjust here if you allow switching vessel)
  const effectiveVesselId = vesselId

  const onboardFromDate = activeSignOn?.onboardFrom?.slice(0, 10) || null
  const onboardToDate =
    activeSignOn?.onboardTo?.slice(0, 10) || new Date().toISOString().slice(0, 10)

  const effectiveFrom = useMemo(() => {
    if (!fromDate) return ''
    const f = onboardFromDate && fromDate < onboardFromDate ? onboardFromDate : fromDate
    if (f && onboardToDate && f > onboardToDate) return ''
    return f
  }, [fromDate, onboardFromDate, onboardToDate])

  const effectiveTo = useMemo(() => {
    if (!toDate) return ''
    let t = toDate
    if (onboardToDate && t > onboardToDate) t = onboardToDate
    if (onboardFromDate && t < onboardFromDate) return ''
    return t
  }, [toDate, onboardFromDate, onboardToDate])

  const updateDateKeys = useMemo(
    () => (effectiveFrom && effectiveTo ? buildDateKeys(effectiveFrom, effectiveTo) : []),
    [effectiveFrom, effectiveTo]
  )

  // helper to read working flag regardless of API field name
  const getWorkingFlag = (e: any) => Boolean(e?.isWorking ?? e?.working)

  // ADD — helper to check if a day is approved/locked
  const isDateLocked = (dateISO: string) =>
    approvedDates?.has(dateISO) === true || (onboardFromDate ? dateISO < onboardFromDate : false)

  // Small utility to warn once per interaction
  const warnLocked = (dateISO: string) => {
    notify?.warn?.(`This date (${dateISO}) is approved and cannot be edited.`)
  }

  const toDateKey = (value: Date) => {
    const yyyy = value.getFullYear()
    const mm = String(value.getMonth() + 1).padStart(2, '0')
    const dd = String(value.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  }

  const floorToHalfHourLocal = (value: Date) => {
    const next = new Date(value.getTime())
    const minutes = next.getMinutes()
    next.setMinutes(minutes < 30 ? 0 : 30, 0, 0)
    return next
  }

  const [filterCompanyGroupId, setFilterCompanyGroupId] = useState<number | ''>('')
  const [filterCompanyAdminId, setFilterCompanyAdminId] = useState<number | ''>('')
  const [filterVesselId, setFilterVesselId] = useState<number | ''>('')

  const [showWorkHours, setShowWorkHours] = useState(false)


  const [isExportOpen, setIsExportOpen] = useState(false)
  const [exportType, setExportType] = useState<'pdf' | 'excel'>('pdf')
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const defaultExcelFrom = useMemo(() => {
    const d = new Date()
    d.setUTCDate(d.getUTCDate() - 29)
    return d.toISOString().slice(0, 10)
  }, [])
  const [excelFrom, setExcelFrom] = useState<string>(defaultExcelFrom)
  const [excelTo, setExcelTo] = useState<string>(todayISO)
  const defaultPdfStart = useMemo(() => {
    const end = new Date(toDate + 'T00:00:00Z')
    const start = new Date(end)
    start.setUTCDate(start.getUTCDate() - 6)
    return start.toISOString().slice(0, 10)
  }, [toDate])
  const [pdfWeekStart, setPdfWeekStart] = useState<string>(defaultPdfStart)

  const formatMatrixRollingDateTime = (value: Date) =>
    value.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

  const [matrixRollingAnchor, setMatrixRollingAnchor] = useState<Date>(() =>
    floorToHalfHourLocal(new Date())
  )

  useEffect(() => {
    if (activeTab !== 'matrix-overview') return

    const tick = () => setMatrixRollingAnchor(floorToHalfHourLocal(new Date()))
    tick()

    const now = new Date()
    const minutes = now.getMinutes()
    const seconds = now.getSeconds()
    const millis = now.getMilliseconds()
    const nextHalf = minutes < 30 ? 30 : 60
    const msToNext = ((nextHalf - minutes) * 60 - seconds) * 1000 - millis

    let intervalId: number | null = null
    const timeoutId = window.setTimeout(() => {
      tick()
      intervalId = window.setInterval(tick, 30 * 60 * 1000)
    }, Math.max(msToNext, 0))

    return () => {
      window.clearTimeout(timeoutId)
      if (intervalId !== null) window.clearInterval(intervalId)
    }
  }, [activeTab])

  const formatRollingTime = (value: Date) =>
    value.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

  const [rollingAnchorLocal, setRollingAnchorLocal] = React.useState<Date>(() =>
    floorToHalfHourLocal(new Date())
  )

  const parseLocalDate = (dateKey: string) => {
    const [yyyy, mm, dd] = dateKey.split('-').map(Number)
    return new Date(yyyy, (mm ?? 1) - 1, dd ?? 1)
  }

  useEffect(() => {
    if (activeTab !== 'update') return

    const tick = () => setRollingAnchorLocal(floorToHalfHourLocal(new Date()))
    tick()

    const now = new Date()
    const minutes = now.getMinutes()
    const seconds = now.getSeconds()
    const millis = now.getMilliseconds()
    const nextHalf = minutes < 30 ? 30 : 60
    const msToNext =
      ((nextHalf - minutes) * 60 - seconds) * 1000 - millis

    let intervalId: number | null = null
    const timeoutId = window.setTimeout(() => {
      tick()
      intervalId = window.setInterval(tick, 30 * 60 * 1000)
    }, Math.max(msToNext, 0))

    return () => {
      window.clearTimeout(timeoutId)
      if (intervalId !== null) window.clearInterval(intervalId)
    }
  }, [activeTab])

  const rollingWindow = useMemo(() => {
    const end = rollingAnchorLocal
    const start = new Date(end.getTime() - ROLLING_WINDOW_DAYS * 24 * 60 * 60 * 1000)
    const slots: Array<{ start: Date; dateKey: string; slotIndex: number }> = []
    const totalSlots = ROLLING_WINDOW_DAYS * 48
    for (let i = 0; i < totalSlots; i++) {
      const slotStart = new Date(start.getTime() + i * 30 * 60 * 1000)
      const dateKey = toDateKey(slotStart)
      const slotIndex =
        slotStart.getHours() * 2 + (slotStart.getMinutes() >= 30 ? 1 : 0)
      slots.push({ start: slotStart, dateKey, slotIndex })
    }
    return { start, end, slots }
  }, [rollingAnchorLocal, toDateKey])

  const rollingDayWindows = useMemo(() => {
    const windows: Array<{
      start: Date
      end: Date
      endKey: string
      slots: Array<{ start: Date; dateKey: string; slotIndex: number }>
    }> = []
    const base = new Date(rollingWindow.start)
    base.setHours(0, 0, 0, 0) // anchor UI display to 00:00 for the day
    for (let d = 0; d < ROLLING_WINDOW_DAYS; d++) {
      const dayStart = new Date(base)
      dayStart.setDate(base.getDate() + d)
      const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000)
      const slots: Array<{ start: Date; dateKey: string; slotIndex: number }> = []
      for (let i = 0; i < 48; i++) {
        const slotStart = new Date(dayStart.getTime() + i * 30 * 60 * 1000)
        const dateKey = toDateKey(slotStart)
        const slotIndex =
          slotStart.getHours() * 2 + (slotStart.getMinutes() >= 30 ? 1 : 0)
        slots.push({ start: slotStart, dateKey, slotIndex })
      }
      windows.push({ start: dayStart, end: dayEnd, endKey: toDateKey(dayEnd), slots })
    }
    return windows
  }, [rollingWindow, toDateKey])

  const rollingHeaderSlots = useMemo(() => {
    if (rollingDayWindows.length) return rollingDayWindows[0].slots
    return rollingWindow.slots.slice(0, 48)
  }, [rollingDayWindows, rollingWindow])

  // UI grid should remain a fixed 00:00–24:00 view; backend calculations still use crew-specific anchors
  const useRollingWindow = false

  // Fetch when dates change
  // Single fetch effect to avoid duplicate calls; runs only when update tab is active and inputs ready
  useEffect(() => {
    if (activeTab !== 'update') return
    if (!effectiveFrom || !effectiveTo) {
      console.warn('[RestHour][update-tab] no effective range (before sign-on?)')
      return
    }
    if (effectiveCrewId && effectiveVesselId) {
      // clear local overrides when selection/range changes so API state shows cleanly
      setSelectedEntries([])
      setPendingDeletes([])
      fetchRestRange('update-tab-fetch')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, effectiveFrom, effectiveTo, effectiveCrewId, effectiveVesselId])

  // Build the effective map whenever API/local edits change
  useEffect(() => {
    const map: Record<string, Set<number>> = {}
    for (const e of apiEntries) {
      if (!map[e.entryDate]) map[e.entryDate] = new Set<number>()
      if (getWorkingFlag(e)) map[e.entryDate].add(e.slotIndex)
    }
    for (const e of selectedEntries) {
      if (!map[e.date]) map[e.date] = new Set<number>()
      map[e.date].add(e.slotIndex)
    }
    for (const d of pendingDeletes) {
      map[d.date]?.delete(d.slotIndex)
    }
    setEffectiveWorkingByDate(map)
  }, [apiEntries, selectedEntries, pendingDeletes])

  // Anchored 7-day windows (ends aligned to the selected toDate)
  const [anchored7d, setAnchored7d] = React.useState<
    Record<string, { windowStart: string; totalRest: number; violated: boolean }>
  >({})
  useEffect(() => {
    if (!effectiveFrom || !effectiveTo) return

    const dateKey = (d: Date) => d.toISOString().slice(0, 10)
    const allDates = getDatesInRange(effectiveFrom, effectiveTo)

    // daily rest from effective working
    const dailyRest: Record<string, number> = {}
    for (const d of allDates) {
      const k = dateKey(d)
      const workCount = effectiveWorkingByDate[k]?.size ?? 0
      dailyRest[k] = 24 - workCount * 0.5
    }

    const blocks: Record<string, { windowStart: string; totalRest: number; violated: boolean }> = {}
    const fm = new Date(effectiveFrom + 'T00:00:00Z')
    let blockEnd = new Date(effectiveTo + 'T00:00:00Z')

    while (true) {
      const blockStart = new Date(blockEnd)
      blockStart.setUTCDate(blockStart.getUTCDate() - 6)
      if (blockStart < fm) break

      let sum = 0
      const cur = new Date(blockStart)
      while (cur <= blockEnd) {
        sum += dailyRest[dateKey(cur)] ?? 24
        cur.setUTCDate(cur.getUTCDate() + 1)
      }
      blocks[dateKey(blockEnd)] = {
        windowStart: dateKey(blockStart),
        totalRest: sum,
        violated: sum < 77,
      }
      blockEnd.setUTCDate(blockEnd.getUTCDate() - 7)
    }

    setAnchored7d(blocks)
  }, [effectiveFrom, effectiveTo, effectiveWorkingByDate])

  // MLC: anchored 7d WORK totals aligned the same way as anchored7d
  const anchored7dWork = useMemo(() => {
    if (!effectiveFrom || !effectiveTo) return {} as Record<string, { totalWork: number }>
    const key = (d: Date) => d.toISOString().slice(0, 10)
    const allDates = getDatesInRange(effectiveFrom, effectiveTo)

    const dailyWork: Record<string, number> = {}
    for (const d of allDates) {
      const k = key(d)
      const workSlots = effectiveWorkingByDate[k]?.size ?? 0
      dailyWork[k] = workSlots * 0.5
    }

    const blocks: Record<string, { totalWork: number }> = {}
    const fm = new Date(effectiveFrom + 'T00:00:00Z')
    let blockEnd = new Date(effectiveTo + 'T00:00:00Z')

    while (true) {
      const blockStart = new Date(blockEnd)
      blockStart.setUTCDate(blockStart.getUTCDate() - 6)
      if (blockStart < fm) break
      let sum = 0
      const cur = new Date(blockStart)
      while (cur <= blockEnd) {
        sum += dailyWork[key(cur)] ?? 0
        cur.setUTCDate(cur.getUTCDate() + 1)
      }
      blocks[key(blockEnd)] = { totalWork: sum }
      blockEnd.setUTCDate(blockEnd.getUTCDate() - 7)
    }
    return blocks
  }, [effectiveFrom, effectiveTo, effectiveWorkingByDate])


  // Build a fast map of daily rest in the visible Update window
  const dailyRestByDate = useMemo(() => {
    if (!effectiveFrom || !effectiveTo) return {}
    const out: Record<string, number> = {}
    const dates = getDatesInRange(effectiveFrom, effectiveTo)
    for (const d of dates) {
      const k = d.toISOString().slice(0, 10)
      const workCount = effectiveWorkingByDate[k]?.size ?? 0
      out[k] = 24 - workCount * 0.5
    }
    return out
  }, [effectiveFrom, effectiveTo, effectiveWorkingByDate])

  const signOnHHmm = useMemo(() => getHHmmFromIsoUtc(activeSignOn?.onboardFrom ?? null), [activeSignOn?.onboardFrom])

  // ADD: robust id readers (handles numbers or strings, root or nested)
  const toNum = (v: any): number | undefined =>
    v === null || v === undefined || v === '' || isNaN(Number(v)) ? undefined : Number(v)

  const pickCompanyGroupId = (m: any): number | undefined =>
    toNum(
      m.companyGroupAdminId ??
      m.companyGroupId ??
      m.companyGroupAdmin?.id ??
      m.cgaid ??
      m.cga?.id ??
      m.vessel?.companyGroupAdminId ??
      m.vessel?.companyGroupAdmin?.id
    )

  const pickCompanyAdminId = (m: any): number | undefined =>
    toNum(
      m.companyAdminId ??
      m.companyId ??
      m.companyAdmin?.id ??
      m.vessel?.companyAdminId ??
      m.vessel?.companyAdmin?.id
    )

  const pickVesselId = (m: any): number | undefined => toNum(m.vessel?.id ?? m.vesselId)



  // ===== THEME (tweak if your brand uses a different primary) =====
  const THEME = {
    primary: [13, 110, 253] as [number, number, number],      // #0D6EFD
    primarySoft: [240, 247, 255] as [number, number, number],  // very light
    accent: [25, 135, 84] as [number, number, number],         // #198754 (success)
    danger: [220, 53, 69] as [number, number, number],         // #DC3545
    dangerSoft: [255, 240, 244] as [number, number, number],   // light red
    grayHead: [231, 238, 255] as [number, number, number],     // pale head bg
  };

  const reportingLabelUpdate = useMemo(
    () =>
      getReportingWindowLabel({
        mode: reportingMode,
        time: reportingMode === 'CUSTOM_ANCHOR' ? customAnchorHHmm : undefined,
        signOnDateTime,
        signOnTimezone: vesselTz,
      }),
    [
      reportingMode,
      customAnchorHHmm,
      signOnDateTime,
      vesselTz,
    ]
  )

  useEffect(() => {
    if (!debugMode) return
    console.log('[UpdateTab][ReportingDay]', {
      mode: reportingMode,
      time: customAnchorHHmm,
      signOnDateTime,
      signOnTimezone,
      windowLabel: reportingLabelUpdate,
    })
  }, [debugMode, reportingMode, customAnchorHHmm, signOnDateTime, signOnTimezone, reportingLabelUpdate])

  // For STCW+Exceptions: only apply exceptions explicitly selected by crew (max 2 per 7d window)
  const stcwExceptionDays = useMemo(() => {
    const s = new Set<string>()
    if (regulation !== 'stcw' || !allowExceptions || !effectiveFrom || !effectiveTo) return s
    if (!effectiveCrewId) return s
    updateDateKeys.forEach((dk) => {
      const rest = dailyRestByDate[dk]
      const key = `${effectiveCrewId}-${dk}`
      if (exceptionOverrides.has(key) && rest >= 6 && rest < 10) s.add(dk)
    })
    return s
  }, [
    regulation,
    allowExceptions,
    dailyRestByDate,
    effectiveFrom,
    effectiveTo,
    updateDateKeys,
    effectiveCrewId,
    exceptionOverrides,
  ])


  // Guard against duplicate/stale fetches
  const latestFetchId = React.useRef(0)
  const lastFetchKeyRef = React.useRef<string | null>(null)

  const fetchRestRange = async (trigger: string, force = false) => {
    const cId = effectiveCrewId
    const vId = effectiveVesselId
    const fromEff = effectiveFrom
    const toEff = effectiveTo
    if (!fromEff || !toEff || !cId || !vId) {
      console.warn('[RestHour][fetch] skip (params missing)', { trigger, fromEff, toEff, cId, vId })
      return
    }
    if (new Date(fromEff) > new Date(toEff)) {
      console.warn('[RestHour][fetch] skip (invalid range)', { trigger, fromEff, toEff })
      return
    }

    const key = `${fromEff}|${toEff}|${cId}|${vId}|${regulation}|${activeTab}`
    if (!force && lastFetchKeyRef.current === key) {
      console.log('[RestHour][fetch] dedup skip (same params)', { trigger, key })
      return
    }
    lastFetchKeyRef.current = key

    const rid = ++latestFetchId.current
    console.log('[RestHour][fetch] start', { rid, trigger, activeTab, from: fromEff, to: toEff, crewId: cId, vesselId: vId })

    try {
      const data = await getRestHourEntriesInRange(cId, vId, fromEff, toEff)
      if (rid !== latestFetchId.current) {
        console.log('[RestHour][fetch] stale response ignored', { rid, current: latestFetchId.current })
        return
      }
      const count = Array.isArray(data) ? data.length : 0
      console.log('[RestHour][fetch] done', { rid, trigger, count })
      // Only update if this response matches latest request
      setApiEntries(Array.isArray(data) ? data : [])
    } catch (error) {
      if (rid !== latestFetchId.current) {
        console.log('[RestHour][fetch] stale error ignored', { rid, current: latestFetchId.current })
        return
      }
      console.error('[RestHour][fetch] failed', { rid, trigger, error })
      // Don't wipe existing data on error
    }
  }

  // REPLACE isWorkingHour with this version (uses selectedEntries + pendingDeletes + apiEntries)
  const isWorkingHour = (_crewId: number, date: Date, slotIndex: number): boolean => {
    const dateStr = date.toISOString().split('T')[0]

    // 1) Pending deletions win ? force REST
    if (pendingDeletes.some((e) => e.date === dateStr && e.slotIndex === slotIndex)) return false

    // 2) Local adds (selectedEntries) ? force WORK
    if (selectedEntries.some((e) => e.date === dateStr && e.slotIndex === slotIndex)) return true

    // 3) Fallback: API state (if present)
    const apiMatch = apiEntries.find((e) => e.entryDate === dateStr && e.slotIndex === slotIndex)
    if (apiMatch) return getWorkingFlag(apiMatch)

    // 4) Default REST
    return false
  }

  const handleSaveChanges = async () => {
    if (!isSuperAdmin && !hasPermission('UPDATE_REST_HOURS') && !hasPermission('CREATE_REST_HOURS')) {
      showErrorToast(null, 'You do not have permission to save rest hours.', 'save')
      return
    }
    const crewID = effectiveCrewId

    if (!vesselId) {
      notify?.warn('Missing vessel scope for saving rest hours.')
      return
    }
    if (!crewID) {
      notify?.warn('Could not resolve which crew member to save for.')
      return
    }

    const addsByDate: Record<string, { slotIndex: number }[]> = {}
    const deletesByDate: Record<string, number[]> = {}
    const lockedTouched = new Set<string>()

    const remarksByDate: Record<string, string> = {}
    // remarks.forEach((val, key) => {
    //   const t = String(val || '').trim()
    //   if (t) remarksByDate[key] = t
    // })
    dirtyRemarkDates?.forEach((d) => {
      remarksByDate[d] = String(remarks.get(d) ?? '')
    })


    for (const e of selectedEntries) {
      if (isDateLocked(e.date)) {
        lockedTouched.add(e.date)
      } else {
        if (!addsByDate[e.date]) addsByDate[e.date] = []
        addsByDate[e.date].push({ slotIndex: e.slotIndex })
      }
    }
    for (const d of pendingDeletes) {
      if (isDateLocked(d.date)) {
        lockedTouched.add(d.date)
      } else {
        if (!deletesByDate[d.date]) deletesByDate[d.date] = []
        deletesByDate[d.date].push(d.slotIndex)
      }
    }

    if (lockedTouched.size) {
      notify?.warn(
        `Cannot edit approved day(s): ${Array.from(lockedTouched).sort().join(', ')}`
      )
    }

    // if (Object.keys(addsByDate).length === 0 && Object.keys(deletesByDate).length === 0) {
    //   notify?.info('No editable changes to save.')
    //   return
    // }
    const hasHourAdds = Object.keys(addsByDate).length > 0
    const hasHourDeletes = Object.keys(deletesByDate).length > 0
    const hasRemarkOnly = Object.keys(remarksByDate).length > 0

    if (!hasHourAdds && !hasHourDeletes && !hasRemarkOnly) {
      notify?.info('No editable changes to save.')
      return
    }


    try {
      // 1) Persist deletes then adds
      for (const [dateStr, slotIndices] of Object.entries(deletesByDate)) {
        await deleteWorkingSlots(crewID, vesselId, dateStr, slotIndices, true)
      }
      if (Object.keys(addsByDate).length > 0) {
        await saveRestHourEntries(crewID, vesselId, addsByDate)
      }

      // 2) PATCH remarks to SUMMARY (all dates with non-empty remarks)
      const failed: string[] = []
      for (const [d, text] of Object.entries(remarksByDate)) {
        let summaryId = getSummaryId?.(crewID, d)
        if (!summaryId) {
          try {
            const recomputed = await recomputeDailySummary(crewID, vesselId, d)
            summaryId = recomputed?.id ?? null
          } catch (createErr) {
            console.error('Failed to create/recompute summary for date', d, createErr)
          }
        }
        if (summaryId) {
          try {
            await patchSummaryRemarks(summaryId, text)
          } catch (e) {
            console.error('Summary-remarks patch failed for', d, e)
            failed.push(d)
          }
        } else {
          // no summary for that date -> can’t patch
          failed.push(d)
        }
      }

      // 2) Refresh Update grid
      await fetchRestRange('after-save', true)

      // 3) Patch Matrix Overview optimistically
      const datesTouched = new Set<string>([
        ...Object.keys(addsByDate),
        ...Object.keys(deletesByDate),
        ...Object.keys(remarksByDate),
      ])

      if (datesTouched.size && onLocalSummaryChange) {
        const patch: Record<string, {
          work: number
          rest: number
          compliant: boolean
          violationCount: number
          violations: string[]
          violationCodes?: string[]
          remarks?: string
        }> = {}
        datesTouched.forEach((d) => {
          patch[d] = { ...buildDaySummary(d), remarks: remarksByDate[d] ?? (remarks.get(d) || '') }
        })
        onLocalSummaryChange(crewID, patch)
      }

      // 4) Reload server summaries (incl. approval flags)
      await onAfterSave?.()

      // 5) Clear local diffs
      setSelectedEntries([])
      setPendingDeletes([])
      if (failed.length) {
        notify?.warn(`Saved hours. Remarks not saved for: ${failed.join(', ')}`)
      } else {
        notify?.success('Working hours & remarks updated successfully.')
      }
    } catch (error) {
      console.error('Save failed:', error)
      showErrorToast(error, 'Failed to update working hours.', 'save')
    }
  }


  const dateRange = React.useMemo(() => {
    const today = new Date().toISOString().split('T')[0]
    const start = fromDate || today
    const end = toDate || today
    return getDatesInRange(start, end)
  }, [fromDate, toDate])

  const filteredCrewMembers = React.useMemo(() => {
    console.log(crewMembers)
    return crewMembers.filter((member) => {
      console.log(member.rankInfo.id)
      console.log(member.crewName)

      const matchesRank = !selectedRank || member.rankInfo.id === Number(selectedRank)
      const matchesName = !selectedCrew || member.crewId === selectedCrew.id
      return matchesRank && matchesName
    })
  }, [crewMembers, selectedRank, selectedCrew])

  useEffect(() => {
    console.log(filteredCrewMembers)
  }, [filteredCrewMembers])



  // Build Matrix-Overview-compatible summary for a given date string (YYYY-MM-DD)
  // const buildDaySummary = (dateStr: string) => {
  //   const date = new Date(dateStr + 'T00:00:00Z')
  //   const slots = timeSlots.map((_, idx) => isWorkingHour(crewId || 0, date, idx))
  //   const work = slots.filter(Boolean).length * 0.5
  //   const rest = 24 - work

  //   const v = checkRestViolations(slots)
  //   const sevenDayViolation = anchored7d[dateStr]?.violated === true

  //   const violations: string[] = []
  //   if (v.minRest24h.isViolation) violations.push('Min Rest/24h < 10h')
  //   if (sevenDayViolation) violations.push('Min Rest/7d < 77h')
  //   if (v.maxInterval.isViolation) violations.push('Max interval between rests > 14h')
  //   if (v.maxPeriods.isViolation) violations.push('More than 2 rest periods/day')
  //   // we can have this below line instead of above if they asked
  //   // if (v.maxPeriods.isViolation) violations.push('10h rest split into > 2 periods')
  //   if (v.minSingleRest.isViolation) violations.push('Min single rest < 6h')

  //   return {
  //     work,
  //     rest,
  //     compliant: violations.length === 0,
  //     violationCount: violations.length,
  //     violations,
  //   }
  // }
  const buildDaySummary = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00Z')
    const slots = timeSlots.map((_, idx) => isWorkingHour(crewId || 0, date, idx))
    const work = slots.filter(Boolean).length * 0.5
    const rest = 24 - work

    const violations: string[] = []

    if (regulation === 'stcw') {
      const v = checkRestViolations(slots)
      const sevenDayViolation = anchored7d[dateStr]?.violated === true

      const exceptionSelected = allowExceptions && stcwExceptionDays.has(dateStr)

      if (!exceptionSelected) {
        // daily 10h rest (exception can waive it if 6–10h on =2 days)
        const dailyBreach = rest < 10
        if (dailyBreach) violations.push('Min Rest/24h < 10h')
        if (sevenDayViolation) violations.push('Min Rest/7d < 77h')
        if (v.maxInterval.isViolation) violations.push('Max interval between rests > 14h')
      }
    } else {
      // MLC
      const end = anchored7d[dateStr]
      const endW = anchored7dWork[dateStr]
      const wkWork = endW?.totalWork ?? 0
      const wkRest = end?.totalRest ?? 168 - wkWork

      const v = checkMLCViolations(slots, wkWork, wkRest, allowExceptions)

      if (work > 14) violations.push('Max Work/24h > 14h')
      if (end && endW && wkWork > 72) violations.push('Max Work/7d > 72h')
      if (allowExceptions && end && endW && wkRest < 70) violations.push('Min Rest/7d < 70h (exception floor)')
    }

    return {
      work,
      rest,
      compliant: violations.length === 0,
      violationCount: violations.length,
      violations,
    }
  }
  const isWorkingSlot = (dateStr: string, timeIndex: number): boolean => {
    if (pendingDeletes.some((e) => e.date === dateStr && e.slotIndex === timeIndex)) return false
    if (selectedEntries.some((e) => e.date === dateStr && e.slotIndex === timeIndex)) return true
    const apiItem = apiEntries.find((e) => e.entryDate === dateStr && e.slotIndex === timeIndex)
    return apiItem ? getWorkingFlag(apiItem) : false
  }

  const rollingTotals7d = useMemo(() => {
    const workSlots = rollingWindow.slots.reduce((acc, slot) => {
      return acc + (isWorkingSlot(slot.dateKey, slot.slotIndex) ? 1 : 0)
    }, 0)
    const totalWork = workSlots * 0.5
    return {
      totalWork,
      totalRest: ROLLING_WINDOW_DAYS * 24 - totalWork,
    }
  }, [rollingWindow, apiEntries, selectedEntries, pendingDeletes])

  // Preview helpers (backend-driven)
  const previewTotalsFor = (dateStr: string) => previewDayTotals[dateStr] ?? null
  const previewHasAny = (dateStr: string) => (previewViolationsByDate[dateStr]?.size ?? 0) > 0
  const [previewViolations, setPreviewViolations] = useState<any[]>([])

  const previewViolationsParsed = useMemo(() => {
    return previewViolations.map((v) => ({
      ...v,
      _ws: v?.windowStart ? new Date(v.windowStart) : null,
      _we: v?.windowEnd ? new Date(v.windowEnd) : null,
    }))
  }, [previewViolations])

  const reportingWindowForDate = React.useCallback(
    (dateStr: string) =>
      getReportingWindowUtc(
        dateStr,
        reportingMode,
        activeSignOn?.onboardFrom ?? null,
        customAnchorHHmm,
        vesselTz
      ),
    [reportingMode, activeSignOn?.onboardFrom, customAnchorHHmm, vesselTz]
  )

  useEffect(() => {
    if (activeSignOn?.onboardFrom) {
      setSignOnDateTime(activeSignOn.onboardFrom)
      setSignOnTimezone(activeSignOn.timezone || 'UTC')
    }
  }, [activeSignOn?.onboardFrom, activeSignOn?.timezone])

  const overlappingViolationsForDate = React.useCallback(
    (dateStr: string) => {
      return previewViolationsParsed.filter((v: any) => {
        if (!v?._ws || !v?._we) return false
        const code = (v.ruleCode ?? '').toUpperCase()
        const impactedCrewDays: string[] | undefined = v.impactedCrewDays
        if (Array.isArray(impactedCrewDays) && impactedCrewDays.includes(dateStr)) return true

        // If ownership is missing, do not fall back to overlap-based matching; avoid next-day bleed.
        // (Optional future fallback: end-day mapping for legacy payloads.)
        return false
      }) as RollingViolationWindow[]
    },
    [previewViolationsParsed]
  )

  const findRollingViolationForRuleOnDate = React.useCallback(
    (ruleCode: string, dateStr: string): RollingViolationWindow | undefined => {
      const target = (ruleCode ?? '').toUpperCase()
      if (!target) return undefined
      const win = reportingWindowForDate(dateStr)
      let best: { v: RollingViolationWindow; overlap: number; startDelta: number } | undefined
      previewViolationsParsed.forEach((v: any) => {
        const code = (v?.ruleCode ?? '').toUpperCase()
        if (code !== target) return
        if (!v?._ws || !v?._we) return
        const overlap = overlapDurationMs(win.startUtc, win.endUtc, v._ws as Date, v._we as Date)
        if (overlap <= 0) return
        const startDelta = Math.abs((v._ws as Date).getTime() - win.startUtc.getTime())
        if (!best || overlap > best.overlap || (overlap === best.overlap && startDelta < best.startDelta)) {
          best = { v, overlap, startDelta }
        }
      })
      return best?.v
    },
    [previewViolationsParsed, reportingWindowForDate]
  )

  const previewRowHasViolation = (dateStr: string) => overlappingViolationsForDate(dateStr).length > 0

  const ROLLING_24H_CODES = new Set([
    'REST_24H_MIN_10',
    'REST_GAP_MAX_14',
    'REST_SPLIT_MAX_2',
    'REST_ONE_BLOCK_MIN_6',
    'REST_SECOND_BLOCK_MIN_4',
    'MLC_MAX_WORK_24H',
  ])

  const getRowViolations = (dateStr: string): RollingViolationWindow[] =>
    overlappingViolationsForDate(dateStr)

  const buildDetailsSummary = (
    violations: RollingViolationWindow[],
    debugMode: boolean,
    opts?: ViolationFormatOptions
  ): { lines: string[]; extraCount: number; tooltip: string } | null => {
    if (!violations || violations.length === 0) return null
    const grouped = groupViolationsForDisplay(violations)
    const lines = grouped.slice(0, 2).map((v) => humanizeViolation(v, opts))
    const extraCount = Math.max(grouped.length - 2, 0)
    const tooltip = grouped
      .map((v) => {
        const friendly = humanizeViolation(v, opts)
        if (!debugMode) return friendly
        const base = `${v.ruleCode || 'RULE'} | ${v.windowStart} -> ${v.windowEnd} | rest=${v.restMinutes ?? '-'}m periods=${v.restPeriods ?? '-'} gap=${v.maxGapMinutes ?? '-'}m`
        return `${friendly}\n${base}`
      })
      .join('\n')
    return { lines, extraCount, tooltip }
  }

  const formatRollingDateTime = (value?: string | Date | null) => {
    if (!value) return ''
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const buildDebugTitle = (v?: RollingViolationWindow | null) => {
    if (!v) return undefined
    const match = (v as any)?._match
    return `${v.ruleCode} | ${v.windowStart} -> ${v.windowEnd} | restMinutes=${v.restMinutes} restPeriods=${v.restPeriods} maxGapMinutes=${v.maxGapMinutes} | match=${match ?? 'overlap'}`
  }

  const groupViolationsForDisplay = (violations: RollingViolationWindow[]): RollingViolationWindow[] => {
    if (!violations || violations.length === 0) return []
    const byRule: Record<string, RollingViolationWindow[]> = {}
    violations.forEach((v) => {
      const code = (v.ruleCode ?? 'UNKNOWN').toUpperCase()
        ; (byRule[code] = byRule[code] || []).push(v)
    })
    const grouped: RollingViolationWindow[] = []
    Object.values(byRule).forEach((list) => {
      const sorted = [...list].sort((a, b) => {
        const aStart = new Date(a.windowStart ?? (a as any)._ws ?? 0).getTime()
        const bStart = new Date(b.windowStart ?? (b as any)._ws ?? 0).getTime()
        return aStart - bStart
      })
      let cur: RollingViolationWindow | null = null
      sorted.forEach((v) => {
        const ws = new Date(v.windowStart ?? (v as any)._ws ?? 0)
        const we = new Date(v.windowEnd ?? (v as any)._we ?? 0)
        if (!cur) {
          cur = { ...v, windowStart: ws.toISOString(), windowEnd: we.toISOString() }
          return
        }
        const curEnd = new Date(cur.windowEnd ?? (cur as any)._we ?? 0)
        // merge overlapping or back-to-back within 30 minutes
        if (ws.getTime() <= curEnd.getTime() + 30 * 60 * 1000) {
          const maxEnd = we.getTime() > curEnd.getTime() ? we : curEnd
          cur = { ...cur, windowEnd: maxEnd.toISOString() }
        } else {
          grouped.push(cur)
          cur = { ...v, windowStart: ws.toISOString(), windowEnd: we.toISOString() }
        }
      })
      if (cur) grouped.push(cur)
    })
    return grouped
  }

  const buildPreviewPayload = () => {
    const slots: { entryDate: string; slotIndex: number; workType?: WorkType }[] = []
    // include adjacent day for rolling 24h boundary context
    const extendedKeys = new Set<string>()
    updateDateKeys.forEach((dk) => {
      extendedKeys.add(dk)
      const dt = new Date(dk + 'T00:00:00Z')
      const prev = new Date(dt); prev.setUTCDate(prev.getUTCDate() - 1)
      const next = new Date(dt); next.setUTCDate(next.getUTCDate() + 1)
      extendedKeys.add(prev.toISOString().slice(0, 10))
      extendedKeys.add(next.toISOString().slice(0, 10))
    })
    // Ensure full 7-day coverage anchored to the visible range:
    // start = minVisible - 6 days, end = maxVisible + 1 day (for rolling 24h boundary)
    let minVisible: string | null = null
    let maxVisible: string | null = null
    if (updateDateKeys.length) {
      minVisible = updateDateKeys[0]
      maxVisible = updateDateKeys[updateDateKeys.length - 1]
      const coverStart = new Date(minVisible + 'T00:00:00Z')
      coverStart.setUTCDate(coverStart.getUTCDate() - 6)
      const coverEnd = new Date(maxVisible + 'T00:00:00Z')
      coverEnd.setUTCDate(coverEnd.getUTCDate() + 1)
      const cursor = new Date(coverStart)
      while (cursor <= coverEnd) {
        extendedKeys.add(cursor.toISOString().slice(0, 10))
        cursor.setUTCDate(cursor.getUTCDate() + 1)
      }
    }
    const allKeys = Array.from(extendedKeys).sort()
    allKeys.forEach((dk) => {
      for (let i = 0; i < timeSlots.length; i++) {
        if (isWorkingSlot(dk, i)) {
          slots.push({ entryDate: dk, slotIndex: i, workType: 'NORMAL' })
        }
      }
    })
    const start = allKeys[0] ?? effectiveFrom
    const end = allKeys[allKeys.length - 1] ?? effectiveTo
    return { slots, start, end, minVisible, maxVisible, extendedKeys: allKeys }
  }

  const runPreview = React.useCallback(async () => {
    if (activeTab !== 'update') return
    if (!effectiveFrom || !effectiveTo || !effectiveCrewId || !effectiveVesselId) return

    // debounce abort
    if (previewAbortRef.current) previewAbortRef.current.abort()
    const controller = new AbortController()
    previewAbortRef.current = controller
    const requestId = ++previewRequestSeq.current

    try {
      const payload = buildPreviewPayload()
      const slots = payload.slots
      const start = payload.start
      const end = payload.end
      const minVisible = payload.minVisible
      const maxVisible = payload.maxVisible
      const extendedKeys = payload.extendedKeys ?? []
      console.debug('[RestHour][preview-range]', {
        visibleFrom: minVisible,
        visibleTo: maxVisible,
        previewFrom: start,
        previewTo: end,
        days: extendedKeys.length,
        slots: extendedKeys.length * 48,
      })
      console.debug('[RestHour][preview] request', {
        from: start,
        to: end,
        crewId: effectiveCrewId,
        vesselId: effectiveVesselId,
        regulation,
        days: updateDateKeys.length,
        totalSlotsSent: slots.length,
      })
      const resp = await previewRestHourViolations(
        {
          start,
          end,
          crewId: effectiveCrewId,
          vesselId: effectiveVesselId,
          regulation,
          slots,
        },
        controller.signal
      )

      if (requestId !== previewRequestSeq.current) {
        console.debug('[RestHour][preview] stale response ignored', { requestId, latest: previewRequestSeq.current })
        return
      }

      setPreviewAnchor({
        dateTime: resp.anchorDateTime ?? null,
        source: resp.anchorSource ?? null,
      })
      if (resp?.activeSignOn?.onboardFrom) {
        setSignOnDateTime(resp.activeSignOn.onboardFrom)
        setSignOnTimezone(resp.activeSignOn.timezone || 'UTC')
      }
      setPreviewWeeklyInsufficient(!!resp.weeklyInsufficientCoverage)
      setPreviewWindowsCount(resp.windowsCount ?? 0)
      const hasWeeklyViolation = (resp.violations ?? []).some(
        (v: any) => typeof v.ruleCode === 'string' && v.ruleCode.toUpperCase() === 'REST_7D_MIN_77'
      )
      const impacted26Feb =
        (resp.violations ?? []).filter((v: any) => (v.impactedDates ?? []).includes('2026-02-26')).map((v: any) => v.ruleCode)
      console.debug('[RestHour][preview-summary]', {
        windowsCount: resp.windowsCount,
        weeklyInsufficientCoverage: resp.weeklyInsufficientCoverage,
        hasWeeklyViolation,
        impacted_2026_02_26: impacted26Feb,
      })

      const totals: Record<string, { workMinutes: number; restMinutes: number }> = {}
      resp.daySummaries?.forEach((ds: any) => {
        totals[ds.date] = {
          workMinutes: ds.workMinutes ?? 0,
          restMinutes: ds.restMinutes ?? 0,
        }
      })
      setPreviewDayTotals(totals)

      const vmap: Record<string, Set<string>> = {}
      resp.violations?.forEach((v: any) => {
        const code = (v?.ruleCode || '').toUpperCase()
        const impactsRaw = Array.isArray(v.impactedCrewDays) ? v.impactedCrewDays : []
        const impacts =
          impactsRaw.length > 0
            ? impactsRaw
            : v.windowEnd
              ? [new Date(v.windowEnd).toISOString().slice(0, 10)]
              : []
        impacts.forEach((d: string) => {
          if (!vmap[d]) vmap[d] = new Set<string>()
          if (v.ruleCode) vmap[d]!.add(v.ruleCode)
        })
      })
      setPreviewViolationsByDate(vmap)
      setPreviewViolations(resp.violations ?? [])
      console.debug('[RestHour][preview] vmap keys', Object.entries(vmap).map(([k, v]) => `${k}:${Array.from(v).join(',')}`))
      const weeklyViolations = (resp.violations ?? []).filter(
        (v: any) => typeof v?.ruleCode === 'string' && v.ruleCode.toUpperCase().includes('7D')
      )
      weeklyViolations.forEach((v: any, idx: number) => {
        console.debug(`[RestHour][preview] weekly#${idx + 1}`, {
          ruleCode: v.ruleCode,
          windowStart: v.windowStart,
          windowEnd: v.windowEnd,
          restMinutes: v.restMinutes,
          impactedDates: v.impactedDates,
        })
      })
      if (!weeklyViolations.length) {
        console.debug('[RestHour][preview] weekly violation: none in payload')
      }
      console.debug('[RestHour][preview] response', {
        violationCount: resp.violations?.length ?? 0,
        sample: resp.violations?.slice(0, 3)?.map((v: any) => ({
          ruleCode: v.ruleCode,
          windowStart: v.windowStart,
          windowEnd: v.windowEnd,
          impactedDates: v.impactedDates,
        })),
      })
    } catch (err: any) {
      if (controller.signal.aborted) return
      if (requestId !== previewRequestSeq.current) return
      console.error('Preview violations failed', err)
    }
  }, [
    activeTab,
    effectiveFrom,
    effectiveTo,
    effectiveCrewId,
    effectiveVesselId,
    updateDateKeys,
    timeSlots,
    regulation,
    isWorkingSlot,
    reportingMode,
    customAnchorHHmm,
  ])

  useEffect(() => {
    if (activeTab !== 'update') {
      setPreviewViolationsByDate({})
      setPreviewDayTotals({})
      setPreviewAnchor({ dateTime: null, source: null })
      setPreviewWeeklyInsufficient(false)
      setPreviewWindowsCount(0)
      previewAbortRef.current?.abort()
      return
    }
    if (!effectiveFrom || !effectiveTo || !effectiveCrewId || !effectiveVesselId) return
    if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    console.debug('[RestHour][preview] debounce schedule 500ms')
    previewTimerRef.current = window.setTimeout(() => {
      console.debug('[RestHour][preview] debounce fire')
      runPreview()
    }, 500)
    return () => {
      if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    }
  }, [
    activeTab,
    effectiveFrom,
    effectiveTo,
    effectiveCrewId,
    effectiveVesselId,
    selectedEntries,
    pendingDeletes,
    apiEntries,
    updateDateKeys,
    runPreview,
  ])

  const rollingExceptionEndKeys = useMemo(() => {
    const s = new Set<string>()
    if (regulation !== 'stcw' || !allowExceptions) return s
    if (!effectiveCrewId) return s
    rollingDayWindows.forEach((day) => {
      const workSlots = day.slots.map((slot) => isWorkingSlot(slot.dateKey, slot.slotIndex))
      const rest = 24 - workSlots.filter(Boolean).length * 0.5
      const key = `${effectiveCrewId}-${day.endKey}`
      if (exceptionOverrides.has(key) && rest >= 6 && rest < 10) s.add(day.endKey)
    })
    return s
  }, [
    rollingDayWindows,
    regulation,
    allowExceptions,
    apiEntries,
    selectedEntries,
    pendingDeletes,
    effectiveCrewId,
    exceptionOverrides,
  ])

  const rollingDateKeys = useMemo(
    () => rollingDayWindows.map((day) => day.endKey),
    [rollingDayWindows]
  )

  const applyCell = (dateStr: string, timeIndex: number, toWork: boolean) => {
    if (isDateLocked(dateStr)) return

    const wasWorking = isWorkingSlot(dateStr, timeIndex)
    if (toWork === wasWorking) return // nothing to do

    console.debug('[RestHour][slot-toggle]', {
      date: dateStr,
      slotIndex: timeIndex,
      newValue: toWork ? 'W' : 'R',
    })

    if (toWork) {
      // make it WORK
      setSelectedEntries((prev) => {
        if (prev.some((e) => e.date === dateStr && e.slotIndex === timeIndex)) return prev
        return [...prev, { date: dateStr, slotIndex: timeIndex }]
      })
      setPendingDeletes((prev) =>
        prev.filter((e) => !(e.date === dateStr && e.slotIndex === timeIndex))
      )
    } else {
      // make it REST
      setSelectedEntries((prev) =>
        prev.filter((e) => !(e.date === dateStr && e.slotIndex === timeIndex))
      )
      // Only add to deletes if it *was* working (API or previously set)
      if (wasWorking) {
        setPendingDeletes((prev) => {
          if (prev.some((e) => e.date === dateStr && e.slotIndex === timeIndex)) return prev
          return [...prev, { date: dateStr, slotIndex: timeIndex }]
        })
      } else {
        // ensure it's not in deletes
        setPendingDeletes((prev) =>
          prev.filter((e) => !(e.date === dateStr && e.slotIndex === timeIndex))
        )
      }
    }
  }

  const cancelPendingSingle = () => {
    if (singleClickTimerRef.current) {
      window.clearTimeout(singleClickTimerRef.current)
      singleClickTimerRef.current = null
    }
  }

  const scheduleSingleToggle = (dateStr: string, timeIndex: number) => {
    if (isDateLocked(dateStr)) {
      warnLocked(dateStr)
      return
    }
    cancelPendingSingle()
    singleClickTimerRef.current = window.setTimeout(() => {
      singleClickTimerRef.current = null
      const target = !isWorkingSlot(dateStr, timeIndex)
      applyCell(dateStr, timeIndex, target)
    }, SINGLE_CLICK_DELAY)
  }

  const startBrush = (dateStr: string, timeIndex: number) => {
    if (isDateLocked(dateStr)) {
      warnLocked(dateStr)
      return
    }
    const target = !isWorkingSlot(dateStr, timeIndex)
    brushRef.current.active = true
    brushRef.current.date = dateStr
    brushRef.current.targetWorking = target
    brushRef.current.lastIndex = timeIndex

    applyCell(dateStr, timeIndex, target)
  }

  const continueBrush = (dateStr: string, timeIndex: number) => {
    if (isDateLocked(dateStr)) return
    const b = brushRef.current
    if (!b.active || b.date !== dateStr) return
    const from = Math.min(b.lastIndex, timeIndex)
    const to = Math.max(b.lastIndex, timeIndex)
    for (let i = from; i <= to; i++) applyCell(dateStr, i, b.targetWorking)
    b.lastIndex = timeIndex
  }


  // ---- UNSAVED CHANGES: clear only local diffs (not backend) ----
  const hasRemarkChanges = (dirtyRemarkDates?.size ?? 0) > 0
  const hasUnsavedChanges = selectedEntries.length > 0 || pendingDeletes.length > 0 || hasRemarkChanges

  const clearUnsavedChanges = () => {
    // Cancel any pending single-click toggle
    cancelPendingSingle?.()

    // Stop any ongoing paint/drag
    if (brushRef.current.active) {
      brushRef.current.active = false
      brushRef.current.date = ''
      brushRef.current.lastIndex = -1
    }

    // Drop ONLY local, unsaved diffs
    setSelectedEntries([])
    setPendingDeletes([])

    //also reset remarks
    if (hasRemarkChanges) onResetRemarkEdits?.()

    // Optional feedback toast
    notify?.info?.('Unsaved changes cleared.')
  }

  // Totals for Update tab (recomputed every render)
  let rollingWorkTotal = 0
  let rollingRestTotal = 0
  let anchoredWorkTotal = 0
  let anchoredRestTotal = 0

  return (
    <div className={`tab-pane fade ${activeTab === 'update' ? 'show active' : ''}`} id='update'>
      <div className='card'>
        <div className='card-body p-0'>
          <div className='px-4 pt-4 pb-2'>
            {roleId === 4 && (
              <div
                className={`alert mb-3 py-2 small ${isRankMaster ? 'alert-info border-info' : 'alert-secondary border-secondary'
                  }`}
              >
                {isRankMaster ? (
                  <>
                    Editing rest hours for:{' '}
                    <strong>
                      {(selectedCrew?.name ?? (currentUser as any)?.fullName ?? (currentUser as any)?.username) ||
                        '—'}
                    </strong>
                  </>
                ) : (
                  <>You can update only your own rest hours.</>
                )}
              </div>
            )}
            <div className='text-muted small'>
              Slots are shown in a standard 00:00–24:00 grid. Compliance and violations are calculated on the backend using your crew-specific 24h window anchored to the first work slot in the visible range.
            </div>
            <div className='text-muted small mt-1'>
              Crew-day anchor: {previewAnchor.dateTime
                ? new Date(previewAnchor.dateTime).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
                : '--'}{' '}
              {previewAnchor.source ? `(${previewAnchor.source})` : ''}
            </div>
            <div className='d-flex flex-wrap align-items-end gap-3 mt-3'>
              <div style={{ minWidth: 220 }}>
                <label className='form-label mb-1 text-dark'>From date</label>
                {USE_CUSTOM_DATE_PICKER ? (
                  <DatePickerWithIndicators
                    className='form-control form-control-sm'
                    value={fromDate}
                    min={signOnStartISO || undefined}
                    max={toDate || undefined}
                    onChange={onChangeFromDate}
                    entryDates={entryDatesForPickers}
                  />
                ) : (
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    value={fromDate}
                    min={signOnStartISO || undefined}
                    max={toDate || undefined}
                    onChange={(e) => onChangeFromDate(e.target.value)}
                  />
                )}
              </div>
              <div style={{ minWidth: 220 }}>
                <label className='form-label mb-1 text-dark'>To date</label>
                {USE_CUSTOM_DATE_PICKER ? (
                  <DatePickerWithIndicators
                    className='form-control form-control-sm'
                    value={toDate}
                    min={signOnStartISO || fromDate || undefined}
                    onChange={onChangeToDate}
                    entryDates={entryDatesForPickers}
                  />
                ) : (
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    value={toDate}
                    min={signOnStartISO || fromDate || undefined}
                    onChange={(e) => onChangeToDate(e.target.value)}
                  />
                )}
              </div>
              <div style={{ minWidth: 260, display: 'none' }} aria-hidden='true'>
                {/* Reporting Day label removed */}
                {/* Reporting Day picker removed */}
                {/* Reporting Day details removed */}
              </div>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={onResetRange}
                title={`Reset to last ${UPDATE_EDITABLE_LOOKBACK_DAYS} days`}
              >
                Reset to recent
              </button>
              <div className='text-muted small ms-auto'>
                Past dates are allowed; range is still limited to the active sign-on window.
              </div>
            </div>
          </div>
          {/* Conditional rendering based on showTable prop and crew members */}
          {filteredCrewMembers.length === 0 ? (
            <div className='text-center py-5'>
              <p className='text-muted'>No crew members found.</p>
            </div>
          ) : (
            <>
              <div
                className='report-table table-responsive'
                style={{
                  overflowX: 'auto',
                  overflowY: 'auto',
                  maxHeight: '600px',
                  overscrollBehavior: 'contain',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <table
                  className='table table-bordered align-middle mb-0'
                  style={{ minWidth: '3200px' }}
                >
                  <thead className='table-header'>
                    {useRollingWindow ? (
                      <>
                        <tr>
                          <th
                            rowSpan={2}
                            className='text-center align-middle'
                            style={{
                              position: 'sticky',
                              left: 0,
                              top: 0,
                              zIndex: 30,
                              backgroundColor: '#f8f9fa',
                              boxShadow: 'inset 0 0 0 9999px #f8f9fa',
                              minWidth: '160px',
                            }}
                          >
                            Rolling Window
                          </th>

                          <th
                            colSpan={48}
                            className='text-center'
                            style={{
                              position: 'sticky',
                              top: 0,
                              minWidth: '60px',
                              backgroundColor: '#f8f9fa',
                              boxShadow: 'inset 0 0 0 9999px #f8f9fa',
                              borderLeft: '1px solid #dee2e6',
                              zIndex: 20,
                            }}
                          >
                            Time (aligned to rolling anchor)
                          </th>

                          <th
                            rowSpan={2}
                            className='text-center align-middle'
                            style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: '100px', zIndex: 20 }}
                          >
                            Total Working Hrs (24h)
                          </th>
                          <th
                            rowSpan={2}
                            className='text-center align-middle'
                            style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: '100px', zIndex: 20 }}
                          >
                            Total Rest Hrs (24h)
                          </th>
                          <th
                            rowSpan={2}
                            className='text-center align-middle'
                            style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: '150px', zIndex: 20 }}
                          >
                            Remarks
                          </th>
                          <th
                            colSpan={6}
                            className='text-center align-middle'
                            style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', zIndex: 20 }}
                          >
                            Rest Regulations
                          </th>
                        </tr>

                        <tr>
                          {rollingHeaderSlots.map((slot) => (
                            <th
                              key={`${slot.dateKey}-${slot.slotIndex}`}
                              className='text-center align-middle fw-normal'
                              style={{
                                position: 'sticky',
                                top: 29,
                                zIndex: 20,
                                minWidth: '30px',
                                backgroundColor: '#f8f9fa',
                                boxShadow: 'inset 0 0 0 9999px #f8f9fa',
                                fontSize: '12px',
                                padding: '4px',
                                borderRight:
                                  slot.start.getMinutes() === 30 ? '1px solid #dee2e6' : 'none',
                              }}
                            >
                              {formatRollingTime(slot.start)}
                            </th>
                          ))}

                          {regulation === 'stcw' ? (
                            <>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                Min Rest/24h
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                Min Rest/7d
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                Max Interval
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                Max Divisions
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                Rest Blocks (&gt;=6h &amp; &gt;=4h)
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 140, fontSize: 12, zIndex: 20 }}
                              >
                                Details
                              </th>
                            </>
                          ) : (
                            <>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 140, fontSize: 12, zIndex: 20 }}
                              >
                                Max Work/24h
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 140, fontSize: 12, zIndex: 20 }}
                              >
                                Rolling (24h)
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 140, fontSize: 12, zIndex: 20 }}
                              >
                                Max Work/7d
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 140, fontSize: 12, zIndex: 20 }}
                              >
                                {allowExceptions ? 'Min Rest/7d (>=70h)' : '--'}
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                --
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                --
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 140, fontSize: 12, zIndex: 20 }}
                              >
                                Details
                              </th>
                            </>
                          )}
                        </tr>
                      </>
                    ) : (
                      <>
                        {/* --- FIRST HEADER ROW --- */}
                        <tr>
                          <th
                            rowSpan={2}
                            className='text-center align-middle'
                            style={{
                              position: 'sticky',
                              left: 0,
                              top: 0,
                              zIndex: 30,
                              backgroundColor: '#f8f9fa',
                              boxShadow: 'inset 0 0 0 9999px #f8f9fa',
                              minWidth: '120px',
                            }}
                          >
                            Date
                          </th>

                          {Array.from({ length: 24 }, (_, i) => (
                            <th
                              key={i}
                              colSpan={2}
                              className='text-center'
                              style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 20,
                                minWidth: '60px',
                                backgroundColor: '#f8f9fa',
                                boxShadow: 'inset 0 0 0 9999px #f8f9fa',
                                borderLeft: i > 0 ? '1px solid #dee2e6' : 'inherit',
                              }}
                            >
                              {i.toString().padStart(2, '0')}:00
                            </th>
                          ))}

                          <th
                            rowSpan={2}
                            className='text-center align-middle'
                            style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: '100px', zIndex: 20 }}
                          >
                            Total Working Hrs
                          </th>
                          <th
                            rowSpan={2}
                            className='text-center align-middle'
                            style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: '100px', zIndex: 20 }}
                          >
                            Total Rest Hrs
                          </th>
                          <th
                            rowSpan={2}
                            className='text-center align-middle'
                            style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: '150px', zIndex: 20 }}
                          >
                            Remarks
                          </th>
                          <th
                            colSpan={6}
                            className='text-center align-middle'
                            style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', zIndex: 20 }}
                          >
                            Rest Regulations
                          </th>
                        </tr>

                        {/* --- SECOND HEADER ROW --- */}
                        <tr>
                          {Array.from({ length: 24 }, (_, i) => (
                            <React.Fragment key={i}>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{
                                  position: 'sticky',
                                  top: 29,
                                  zIndex: 20,
                                  minWidth: '30px',
                                  backgroundColor: '#f8f9fa',
                                  boxShadow: 'inset 0 0 0 9999px #f8f9fa',
                                  fontSize: '12px',
                                  padding: '4px',
                                }}
                              >
                                00
                              </th>

                              <th
                                className='text-center align-middle fw-normal'
                                style={{
                                  position: 'sticky',
                                  top: 29,
                                  zIndex: 20,
                                  minWidth: '30px',
                                  backgroundColor: '#f8f9fa',
                                  boxShadow: 'inset 0 0 0 9999px #f8f9fa',
                                  fontSize: '12px',
                                  padding: '4px',
                                  borderRight: i < 23 ? '1px solid #dee2e6' : 'none',
                                }}
                              >
                                30
                              </th>
                            </React.Fragment>
                          ))}

                          {regulation === 'stcw' ? (
                            <>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                Min Rest/24h
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                Min Rest/7d
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                Max Interval
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                Max Divisions
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                Min Single Rest
                              </th>
                            </>
                          ) : (
                            <>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 140, fontSize: 12, zIndex: 20 }}
                              >
                                Max Work/24h
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 140, fontSize: 12, zIndex: 20 }}
                              >
                                Max Work/7d
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 140, fontSize: 12, zIndex: 20 }}
                              >
                                {allowExceptions ? 'Min Rest/7d (>=70h)' : '--'}
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                --
                              </th>
                              <th
                                className='text-center align-middle fw-normal'
                                style={{ position: 'sticky', top: 29, backgroundColor: '#f8f9fa', boxShadow: 'inset 0 0 0 9999px #f8f9fa', minWidth: 120, fontSize: 12, zIndex: 20 }}
                              >
                                --
                              </th>
                            </>
                          )}
                        </tr>
                      </>
                    )}
                  </thead>
                  <tbody className='table-body'>
                    {useRollingWindow ? (
                      <>
                        {rollingDayWindows.map((day) => {
                          const dateStr = day.endKey
                          const dayWorkSlotsBools = day.slots.map((slot) =>
                            isWorkingSlot(slot.dateKey, slot.slotIndex)
                          )

                          const totalWorkingHrs = dayWorkSlotsBools.filter(Boolean).length * 0.5
                          const totalRestHrs = 24 - totalWorkingHrs
                          rollingWorkTotal += totalWorkingHrs
                          rollingRestTotal += totalRestHrs

                          const exceptionKey = `${effectiveCrewId}-${dateStr}`
                          const canEditException = !isDateLocked(dateStr)
                          const isExceptionEligible =
                            regulation === 'stcw' &&
                            allowExceptions &&
                            totalRestHrs >= 6 &&
                            totalRestHrs < 10 &&
                            canEditException
                          const isExceptionSelected =
                            canEditException && exceptionOverrides.has(exceptionKey)
                          const canAddExceptionInWindow =
                            isExceptionEligible &&
                            canAddException(effectiveCrewId ?? 0, dateStr, rollingDateKeys, exceptionOverrides)
                          const showExceptionToggle =
                            regulation === 'stcw' &&
                            allowExceptions &&
                            canEditException &&
                            (isExceptionSelected || canAddExceptionInWindow)

                          let stcwV: ViolationSet | null = null
                          let stcwDailyWaived = false

                          let mlcV: MLCViolationSet | null = null

                          if (regulation === 'stcw') {
                            stcwV = checkRestViolations(dayWorkSlotsBools)
                            if (stcwV) stcwV.minRest7d.isViolation = rollingTotals7d.totalRest < 77

                            if (allowExceptions && rollingExceptionEndKeys.has(dateStr) && stcwV) {
                              // Exception selected: waive ALL STCW rule violations for the day
                              stcwV.minRest24h.isViolation = false
                              stcwV.minRest7d.isViolation = false
                              stcwV.maxInterval.isViolation = false
                              stcwV.maxPeriods.isViolation = false
                              stcwV.minSingleRest.isViolation = false
                              stcwDailyWaived = true
                            }
                          } else {
                            mlcV = checkMLCViolations(
                              dayWorkSlotsBools,
                              rollingTotals7d.totalWork,
                              rollingTotals7d.totalRest,
                              allowExceptions
                            )
                          }

                          const hasPreview = previewRowHasViolation(dateStr)
                          const isDraftRow =
                            selectedEntries.some((e) => e.date === dateStr) ||
                            pendingDeletes.some((e) => e.date === dateStr) ||
                            (dirtyRemarkDates?.has(dateStr) ?? false)
                          const weeklyCoverageOk = !previewWeeklyInsufficient && previewWindowsCount >= 7
                          const rowViolationsAll = overlappingViolationsForDate(dateStr)
                          const findViolationByCodes = (codes: string[]) =>
                            rowViolationsAll.find((v: any) => codes.includes((v.ruleCode ?? '').toUpperCase()))
                          const ruleV24h = findViolationByCodes(['REST_24H_MIN_10'])
                          const ruleV7d = findViolationByCodes(['REST_7D_MIN_77', 'REST_7D_MIN_70', 'MLC_MIN_REST_7D'])
                          const ruleVGap = findViolationByCodes(['REST_GAP_MAX_14'])
                          const ruleVSplit = findViolationByCodes(['REST_SPLIT_MAX_2'])
                          const ruleVBlock = findViolationByCodes(['REST_ONE_BLOCK_MIN_6'])
                          const ruleVSecond = findViolationByCodes(['REST_SECOND_BLOCK_MIN_4'])
                          const rowRuleCodes = new Set<string>(rowViolationsAll.map((v: any) => (v.ruleCode ?? '').toUpperCase()))
                          const rowViolations = rowViolationsAll
                          const detailsSummary = buildDetailsSummary(rowViolationsAll, debugMode, {
                            reportingTz: vesselTz,
                            reportingMode,
                            customAnchorHHmm,
                            signOnStartISO: signOnDateTime,
                          })
                          const previewCodes: string[] = rowViolationsAll
                            .map((v: any) => v.ruleCode)
                            .filter(Boolean) as string[]
                          if (debugMode) {
                            console.debug('[RestHour][row-debug]', {
                              date: dateStr,
                              matchedCodes: previewCodes,
                            })

                          }
                          const isAnyViolation = rowViolationsAll.length > 0
                          if (debugMode) {
                            console.debug('[RestHour][row-owned]', {
                              date: dateStr,
                              ownedRuleCodes: rowViolationsAll.map((v) => (v.ruleCode ?? '').toUpperCase()),
                              ownedWindowStarts: rowViolationsAll.map((v: any) => v._ws ?? v.windowStart ?? null),
                              regulationRedKeys: {
                                minRest24h: !!ruleV24h,
                                maxGap: !!ruleVGap,
                                split: !!ruleVSplit,
                                single6h: !!ruleVBlock,
                                second4h: !!ruleVSecond,
                                rest7d: !!ruleV7d,
                              },
                              remarksRuleCodes: rowViolationsAll.map((v) => (v.ruleCode ?? '').toUpperCase()),
                            })
                          }

                          const remarksDate = parseLocalDate(dateStr)

                          return (
                            <tr key={dateStr}>
                              <td
                                className='fw-bold text-nowrap text-center align-middle'
                                style={{
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 5,
                                  backgroundColor: isAnyViolation ? '#ffebee' : '#fff',
                                  boxShadow: isAnyViolation ? 'inset 0 0 0 9999px #ffebee' : 'inset 0 0 0 9999px #fff',
                                  borderRight: '1px solid #dee2e6',
                                  ...(isAnyViolation && VIOLATION_CELL_STYLE),
                                }}
                              >
                                <div>Rolling 24h</div>
                                <div className='text-muted fs-7'>
                                  {formatRollingDateTime(day.start)}{" -> "}{formatRollingDateTime(day.end)}
                                </div>
                                {isDateLocked(dateStr) && (
                                  <span className='badge badge-sm bg-success ms-2'>Approved</span>
                                )}
                                {hasPreview && isDraftRow && (
                                  <span className='badge badge-sm bg-danger ms-2'>Preview</span>
                                )}
                              </td>

                              {day.slots.map((slot, slotIndex) => {
                                const isWorking = dayWorkSlotsBools[slotIndex]
                                const slotDateStr = slot.dateKey
                                const isPreSignOn = signOnStartISO ? slotDateStr < signOnStartISO : false

                                return (
                                  <td
                                    key={`${slot.dateKey}-${slot.slotIndex}`}
                                    className='text-center align-middle position-relative'
                                    style={{
                                      minWidth: '30px',
                                      padding: '2px',
                                      cursor: isPreSignOn ? 'not-allowed' : 'pointer',
                                      backgroundColor: isPreSignOn
                                        ? '#f5f5f5'
                                        : isWorking
                                          ? '#fff3cd'
                                          : '#f8f9fa',
                                      borderLeft:
                                        slot.start.getMinutes() === 0
                                          ? '2px solid #dee2e6'
                                          : '1px solid #dee2e6',
                                      userSelect: 'none',
                                      opacity: isPreSignOn ? 0.5 : 1,
                                      pointerEvents: isPreSignOn ? 'none' : 'auto',
                                    }}
                                    onClick={() => {
                                      if (isPreSignOn) return
                                      scheduleSingleToggle(slotDateStr, slot.slotIndex)
                                    }}
                                    onMouseDown={(e) => {
                                      if (isPreSignOn) return
                                      if (e.detail === 2) {
                                        e.preventDefault()
                                        cancelPendingSingle()
                                        startBrush(slotDateStr, slot.slotIndex)
                                      }
                                    }}
                                    onMouseEnter={() => {
                                      if (isPreSignOn) return
                                      continueBrush(slotDateStr, slot.slotIndex)
                                    }}
                                    onDoubleClick={(e) => e.preventDefault()}
                                  >
                                    <div
                                      className='d-flex align-items-center justify-content-center fw-bold'
                                      style={{
                                        height: '20px',
                                        backgroundColor: isWorking ? '#ffc107' : '#e9ecef',
                                        borderRadius: '2px',
                                        border: isWorking ? '1px solid #ffb300' : '1px solid #ced4da',
                                        color: isWorking ? '#212529' : '#6c757d',
                                        fontSize: '11px',
                                      }}
                                    >
                                      {isWorking ? 'W' : 'R'}
                                    </div>
                                  </td>
                                )
                              })}

                              <td className='text-center fw-bold align-middle'>
                                {totalWorkingHrs.toFixed(1)}
                              </td>
                              <td className='text-center fw-bold align-middle'>
                                {totalRestHrs.toFixed(1)}
                              </td>
                              <td className='text-center align-middle' style={{ minWidth: '150px' }}>
                                <input
                                  type='text'
                                  className='form-control form-control-sm text-center'
                                  placeholder='Add remarks (optional)'
                                  value={remarks.get(dateStr) || ''}
                                  onChange={(e) => onRemarksChange(remarksDate, e.target.value)}
                                  title={isDateLocked(dateStr) ? 'Approved day - remarks locked' : undefined}
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    backgroundColor: isDateLocked(dateStr) ? '#f3f3f3' : '#fff',
                                  }}
                                />
                                {showExceptionToggle && (
                                  <div className='mt-1'>
                                    <button
                                      type='button'
                                      className={`btn btn-sm ${isExceptionSelected ? 'btn-info' : 'btn-outline-info'
                                        }`}
                                      style={{ fontSize: '10px', padding: '2px 6px' }}
                                      title={
                                        isExceptionSelected
                                          ? 'Exception selected (click to remove)'
                                          : 'Mark exception (6-10h rest, max 2 per 7d)'
                                      }
                                      onClick={() =>
                                        onToggleExceptionDay(
                                          effectiveCrewId ?? 0,
                                          dateStr,
                                          rollingDateKeys,
                                          isExceptionEligible
                                        )
                                      }
                                    >
                                      {isExceptionSelected ? 'Exception' : 'Mark Exception'}
                                    </button>
                                  </div>
                                )}
                              </td>

                              {regulation === 'stcw' ? (
                                <>
                                  <td
                                    className='text-center align-middle'
                                    style={ruleV24h ? VIOLATION_CELL_STYLE : {}}
                                    title={debugMode && ruleV24h ? buildDebugTitle(ruleV24h) : undefined}
                                  >
                                    10 Hrs
                                    {allowExceptions && stcwDailyWaived && (
                                      <span className='badge bg-info text-white ms-2'>Exception</span>
                                    )}
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    style={ruleV7d ? VIOLATION_CELL_STYLE : {}}
                                    title={
                                      weeklyCoverageOk
                                        ? debugMode && ruleV7d
                                          ? buildDebugTitle(ruleV7d)
                                          : undefined
                                        : 'Insufficient 7-day rolling coverage'
                                    }
                                  >
                                    {weeklyCoverageOk ? `${WEEKLY_MIN_REST_HRS} Hrs` : 'N/A'}
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    style={ruleVGap ? VIOLATION_CELL_STYLE : {}}
                                    title={debugMode && ruleVGap ? buildDebugTitle(ruleVGap) : undefined}
                                  >
                                    14 Hrs
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    style={ruleVSplit ? VIOLATION_CELL_STYLE : {}}
                                    title={debugMode && ruleVSplit ? buildDebugTitle(ruleVSplit) : undefined}
                                  >
                                    2
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    style={ruleVBlock || ruleVSecond ? VIOLATION_CELL_STYLE : {}}
                                    title={
                                      debugMode && (ruleVBlock || ruleVSecond)
                                        ? buildDebugTitle(ruleVBlock || ruleVSecond)
                                        : undefined
                                    }
                                  >
                                    Split Rest (6h+4h)
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    title={detailsSummary?.tooltip}
                                  >
                                    {detailsSummary ? (
                                      <>
                                        {detailsSummary.lines.map((ln, idx) => (
                                          <div key={idx} className='small text-start'>{ln}</div>
                                        ))}
                                        {detailsSummary.extraCount > 0 && (
                                          <div className='text-muted small'>(+{detailsSummary.extraCount} more)</div>
                                        )}
                                      </>
                                    ) : (
                                      '--'
                                    )}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td
                                    className='text-center align-middle'
                                    style={
                                      rowRuleCodes.has('MLC_MAX_WORK_24H') || mlcV!.maxWork24h.isViolation
                                        ? VIOLATION_CELL_STYLE
                                        : {}
                                    }
                                  >
                                    {mlcV!.maxWork24h.value} Hrs
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    style={
                                      rowRuleCodes.has('MLC_MAX_WORK_7D') || mlcV!.maxWork7d.isViolation
                                        ? VIOLATION_CELL_STYLE
                                        : {}
                                    }
                                  >
                                    {mlcV!.maxWork7d.value} Hrs
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    style={
                                      rowRuleCodes.has('MLC_MIN_REST_7D') || rowRuleCodes.has('REST_7D_MIN_70') || (allowExceptions && mlcV!.minRest7d?.isViolation)
                                        ? VIOLATION_CELL_STYLE
                                        : {}
                                    }
                                  >
                                    {allowExceptions ? `${mlcV!.minRest7d!.value} Hrs` : '--'}
                                  </td>
                                  <td className='text-center align-middle'>--</td>
                                  <td className='text-center align-middle'>--</td>
                                  <td
                                    className='text-center align-middle'
                                    title={detailsSummary?.tooltip}
                                  >
                                    {detailsSummary ? (
                                      <>
                                        {detailsSummary.lines.map((ln, idx) => (
                                          <div key={idx} className='small text-start'>{ln}</div>
                                        ))}
                                        {detailsSummary.extraCount > 0 && (
                                          <div className='text-muted small'>(+{detailsSummary.extraCount} more)</div>
                                        )}
                                      </>
                                    ) : (
                                      '--'
                                    )}
                                  </td>
                                </>
                              )}
                            </tr>
                          )
                        })}
                        <tr className='table-light fw-bold'>
                          <td colSpan={1 + rollingHeaderSlots.length} className='text-end text-uppercase'>
                            Totals
                          </td>
                          <td className='text-center'>{rollingWorkTotal.toFixed(1)}</td>
                          <td className='text-center'>{rollingRestTotal.toFixed(1)}</td>
                          <td colSpan={7}></td>
                        </tr>
                      </>
                    ) : (
                      <>
                        {dateRange.map((date) => {
                          // // ... (your existing logic for calculations remains the same)
                          // const selectedMember = filteredCrewMembers[0]
                          // const dailyWorkSlotsBools = timeSlots.map((_, timeIndex) =>
                          //   isWorkingHour(crewId || 0, date, timeIndex)
                          // )
                          // const totalWorkingHrs = dailyWorkSlotsBools.filter(Boolean).length * 0.5
                          // const totalRestHrs = 24 - totalWorkingHrs
                          // const violations = checkRestViolations(dailyWorkSlotsBools)
                          // const dateStr = date.toISOString().split('T')[0]
                          // const is7dWindowEnd = !!anchored7d[dateStr]
                          // const violated7d = anchored7d[dateStr]?.violated ?? false
                          // violations.minRest7d.isViolation = is7dWindowEnd && violated7d
                          // const isAnyViolation = Object.values(violations).some((v) => v.isViolation)
                          const dateStr = date.toISOString().split('T')[0]
                          const dailyWorkSlotsBools = timeSlots.map((_, i) =>
                            isWorkingHour(crewId || 0, date, i)
                          )

                          const previewTotals = previewTotalsFor(dateStr)
                          const totalWorkingHrs = previewTotals
                            ? (previewTotals.workMinutes ?? 0) / 60
                            : dailyWorkSlotsBools.filter(Boolean).length * 0.5
                          const totalRestHrs = previewTotals
                            ? (previewTotals.restMinutes ?? 0) / 60
                            : 24 - dailyWorkSlotsBools.filter(Boolean).length * 0.5
                          anchoredWorkTotal += totalWorkingHrs
                          anchoredRestTotal += totalRestHrs

                          // style & helpers
                          const exceptionKey = `${effectiveCrewId}-${dateStr}`
                          const canEditException = !isDateLocked(dateStr)
                          const isExceptionEligible =
                            regulation === 'stcw' &&
                            allowExceptions &&
                            totalRestHrs >= 6 &&
                            totalRestHrs < 10 &&
                            canEditException
                          const isExceptionSelected =
                            canEditException && exceptionOverrides.has(exceptionKey)
                          const canAddExceptionInWindow =
                            isExceptionEligible &&
                            canAddException(effectiveCrewId ?? 0, dateStr, updateDateKeys, exceptionOverrides)
                          const showExceptionToggle =
                            regulation === 'stcw' &&
                            allowExceptions &&
                            canEditException &&
                            (isExceptionSelected || canAddExceptionInWindow)

                          // STCW branch (pattern-oriented, with optional daily 6h reductions up to 2 days/7d)
                          let stcwV: ViolationSet | null = null
                          let is7dWindowEnd = false
                          let violated7d = false
                          let stcwDailyWaived = false

                          // MLC branch
                          let mlcV: MLCViolationSet | null = null

                          if (regulation === 'stcw') {
                            stcwV = checkRestViolations(dailyWorkSlotsBools)

                            // weekly min rest 77h only at the end of the anchored 7d in Update window (your existing behavior)
                            is7dWindowEnd = !!anchored7d[dateStr]
                            violated7d = anchored7d[dateStr]?.violated ?? false
                            if (stcwV) stcwV.minRest7d.isViolation = is7dWindowEnd && violated7d

                            if (allowExceptions && stcwExceptionDays.has(dateStr) && stcwV) {
                              // Exception selected: waive ALL STCW rule violations for the day
                              stcwV.minRest24h.isViolation = false
                              stcwV.minRest7d.isViolation = false
                              stcwV.maxInterval.isViolation = false
                              stcwV.maxPeriods.isViolation = false
                              stcwV.minSingleRest.isViolation = false
                              stcwDailyWaived = true
                            }
                          } else {
                            // MLC: need anchored weekly (rest & work) at anchored end only
                            const weeklyEnd = anchored7d[dateStr] // carry totalRest
                            const weeklyWorkEnd = anchored7dWork[dateStr]
                            const sevenRest = weeklyEnd?.totalRest ?? null
                            const sevenWork = weeklyWorkEnd?.totalWork ?? null

                            // Only compute 7d rules at the window end (same UX as your STCW weekly flag)
                            const wkWork = sevenWork ?? 0
                            const wkRest = sevenRest ?? 168 - wkWork // fallback, should not hit when both maps in sync

                            mlcV = checkMLCViolations(dailyWorkSlotsBools, wkWork, wkRest, allowExceptions)

                            is7dWindowEnd = !!weeklyEnd && !!weeklyWorkEnd
                          }

                          const hasPreview = previewRowHasViolation(dateStr)
                          const isDraftRow =
                            selectedEntries.some((e) => e.date === dateStr) ||
                            pendingDeletes.some((e) => e.date === dateStr) ||
                            (dirtyRemarkDates?.has(dateStr) ?? false)
                          const rowViolationsAll = overlappingViolationsForDate(dateStr)
                          const findViolationByCodes = (codes: string[]) =>
                            rowViolationsAll.find((v: any) => codes.includes((v.ruleCode ?? '').toUpperCase()))
                          const ruleV24h = findViolationByCodes(['REST_24H_MIN_10'])
                          const ruleV7d = findViolationByCodes(['REST_7D_MIN_77', 'REST_7D_MIN_70', 'MLC_MIN_REST_7D'])
                          const ruleVGap = findViolationByCodes(['REST_GAP_MAX_14'])
                          const ruleVSplit = findViolationByCodes(['REST_SPLIT_MAX_2'])
                          const ruleVBlock = findViolationByCodes(['REST_ONE_BLOCK_MIN_6'])
                          const ruleVSecond = findViolationByCodes(['REST_SECOND_BLOCK_MIN_4'])
                          const rowRuleCodes = new Set<string>(
                            rowViolationsAll.map((v: any) => (v.ruleCode ?? '').toUpperCase())
                          )
                          const summaryCount =
                            (rollingDaySummaries as RollingDaySummary[]).find((d: RollingDaySummary) => d.date === dateStr)?.violationCount ?? 0
                          const weeklyCoverageOk = true
                          const detailsSummary = buildDetailsSummary(rowViolationsAll, debugMode, {
                            reportingTz: vesselTz,
                            reportingMode,
                            customAnchorHHmm,
                            signOnStartISO: signOnDateTime,
                          })
                          const previewCodes: string[] = Array.from(rowRuleCodes)
                          if (debugMode) {
                            console.debug('[RestHour][row-match]', {
                              dateKey: dateStr,
                              codes: previewCodes,
                              ownedWindowStarts: rowViolationsAll.map((v: any) => v._ws ?? v.windowStart ?? null),
                              regulationRedKeys: {
                                minRest24h: !!ruleV24h,
                                maxGap: !!ruleVGap,
                                split: !!ruleVSplit,
                                single6h: !!ruleVBlock,
                                second4h: !!ruleVSecond,
                                rest7d: !!ruleV7d,
                              },
                              remarksRuleCodes: rowViolationsAll.map((v) => (v.ruleCode ?? '').toUpperCase()),
                            })
                          }
                          const isAnyViolation = rowViolationsAll.length > 0 || summaryCount > 0

                          return (
                            <tr key={date.toISOString()}>
                              {/* Date cell */}
                              <td
                                className='fw-bold text-nowrap text-center align-middle'
                                style={{
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 5,
                                  backgroundColor: isAnyViolation ? '#ffebee' : '#fff',
                                  boxShadow: isAnyViolation ? 'inset 0 0 0 9999px #ffebee' : 'inset 0 0 0 9999px #fff',
                                  borderRight: '1px solid #dee2e6',
                                  ...(isAnyViolation && VIOLATION_CELL_STYLE),
                                }}
                              >
                                {date.toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                                {hasPreview && isDraftRow && (
                                  <span className='badge badge-sm bg-info ms-2'>Preview</span>
                                )}
                                {isDateLocked(dateStr) && (
                                  <span className='badge badge-sm bg-success ms-2'>Approved</span>
                                )}
                              </td>

                              {/* Time slot cells */}
                              {timeSlots.map((timeSlot, timeIndex) => {
                                const isWorking = dailyWorkSlotsBools[timeIndex]
                                const dateStr = date.toISOString().split('T')[0]
                                const isPreSignOn = signOnStartISO ? dateStr < signOnStartISO : false

                                return (
                                  <td
                                    key={timeIndex}
                                    className='text-center align-middle position-relative'
                                    style={{
                                      minWidth: '30px',
                                      padding: '2px',
                                      cursor: isPreSignOn ? 'not-allowed' : 'pointer',
                                      backgroundColor: isPreSignOn
                                        ? '#f5f5f5'
                                        : isWorking
                                          ? '#fff3cd'
                                          : '#f8f9fa',
                                      borderLeft:
                                        timeIndex % 2 === 0 ? '2px solid #dee2e6' : '1px solid #dee2e6',
                                      userSelect: 'none',
                                      opacity: isPreSignOn ? 0.5 : 1,
                                      pointerEvents: isPreSignOn ? 'none' : 'auto',
                                    }}
                                    /* 1) single click -> delayed toggle (so double click can cancel) */
                                    onClick={() => {
                                      if (isPreSignOn) return
                                      scheduleSingleToggle(dateStr, timeIndex)
                                    }}
                                    /* 2) second mouse down (detail===2) starts paint immediately */
                                    onMouseDown={(e) => {
                                      if (isPreSignOn) return
                                      if (e.detail === 2) {
                                        e.preventDefault()
                                        cancelPendingSingle()
                                        startBrush(dateStr, timeIndex)
                                      }
                                    }}
                                    /* 3) drag horizontally within the same day row to paint */
                                    onMouseEnter={() => {
                                      if (isPreSignOn) return
                                      continueBrush(dateStr, timeIndex)
                                    }}
                                    /* optional: avoid native dblclick selection flash */
                                    onDoubleClick={(e) => e.preventDefault()}
                                  >
                                    <div
                                      className='d-flex align-items-center justify-content-center fw-bold'
                                      style={{
                                        height: '20px',
                                        backgroundColor: isWorking ? '#ffc107' : '#e9ecef',
                                        borderRadius: '2px',
                                        border: isWorking ? '1px solid #ffb300' : '1px solid #ced4da',
                                        color: isWorking ? '#212529' : '#6c757d',
                                        fontSize: '11px',
                                      }}
                                    >
                                      {isWorking ? 'W' : 'R'}
                                    </div>
                                  </td>
                                )
                              })}

                              {/* MODIFIED: Enforced text-center and align-middle on all data cells */}
                              <td className='text-center fw-bold align-middle'>
                                {totalWorkingHrs.toFixed(1)}
                              </td>
                              <td className='text-center fw-bold align-middle'>
                                {totalRestHrs.toFixed(1)}
                              </td>
                              <td className='text-center align-middle' style={{ minWidth: '150px' }}>
                                <input
                                  type='text'
                                  className='form-control form-control-sm text-center'
                                  placeholder='Add remarks (optional)'
                                  value={remarks.get(dateStr) || ''}
                                  onChange={(e) => onRemarksChange(date, e.target.value)}
                                  // I am commenting this line below to let approved dates also remarks update unlike hours
                                  // disabled={isDateLocked(dateStr)}                 // optional: lock like slots
                                  title={isDateLocked(dateStr) ? 'Approved day — remarks locked' : undefined}
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    backgroundColor: isDateLocked(dateStr) ? '#f3f3f3' : '#fff',
                                  }}
                                />
                                {showExceptionToggle && (
                                  <div className='mt-1'>
                                    <button
                                      type='button'
                                      className={`btn btn-sm ${isExceptionSelected ? 'btn-info' : 'btn-outline-info'
                                        }`}
                                      style={{ fontSize: '10px', padding: '2px 6px' }}
                                      title={
                                        isExceptionSelected
                                          ? 'Exception selected (click to remove)'
                                          : 'Mark exception (6-10h rest, max 2 per 7d)'
                                      }
                                      onClick={() =>
                                        onToggleExceptionDay(
                                          effectiveCrewId ?? 0,
                                          dateStr,
                                          updateDateKeys,
                                          isExceptionEligible
                                        )
                                      }
                                    >
                                      {isExceptionSelected ? 'Exception' : 'Mark Exception'}
                                    </button>
                                  </div>
                                )}
                              </td>
                              {/* Regulations cells */}
                              {regulation === 'stcw' ? (
                                <>
                                  <td
                                    className='text-center align-middle'
                                    style={ruleV24h ? VIOLATION_CELL_STYLE : {}}
                                    title={debugMode && ruleV24h ? buildDebugTitle(ruleV24h) : undefined}
                                  >
                                    10 Hrs
                                    {allowExceptions && stcwDailyWaived && (
                                      <span className='badge bg-info text-white ms-2'>Exception</span>
                                    )}
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    style={weeklyCoverageOk && ruleV7d ? VIOLATION_CELL_STYLE : {}}
                                    title={
                                      weeklyCoverageOk
                                        ? (debugMode && ruleV7d ? buildDebugTitle(ruleV7d) : undefined)
                                        : 'Insufficient 7-day rolling coverage'
                                    }
                                  >
                                    77 Hrs
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    style={ruleVGap ? VIOLATION_CELL_STYLE : {}}
                                    title={debugMode && ruleVGap ? buildDebugTitle(ruleVGap) : undefined}
                                  >
                                    14 Hrs
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    style={ruleVSplit ? VIOLATION_CELL_STYLE : {}}
                                    title={debugMode && ruleVSplit ? buildDebugTitle(ruleVSplit) : undefined}
                                  >
                                    2
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    style={ruleVSecond || ruleVBlock ? VIOLATION_CELL_STYLE : {}}
                                    title={debugMode && (ruleVSecond || ruleVBlock) ? buildDebugTitle(ruleVSecond || ruleVBlock) : undefined}
                                  >
                                    Split Rest (6h+4h)
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    title={detailsSummary?.tooltip}
                                  >
                                    {detailsSummary ? (
                                      <>
                                        {detailsSummary.lines.map((ln, idx) => (
                                          <div key={idx} className='small text-start'>{ln}</div>
                                        ))}
                                        {detailsSummary.extraCount > 0 && (
                                          <div className='text-muted small'>(+{detailsSummary.extraCount} more)</div>
                                        )}
                                      </>
                                    ) : (
                                      '--'
                                    )}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td
                                    className='text-center align-middle'
                                    style={rowRuleCodes.has('MLC_MAX_WORK_24H') || mlcV!.maxWork24h.isViolation || previewCodes.length ? VIOLATION_CELL_STYLE : {}}
                                    title={previewCodes.length ? previewCodes.join(', ') : undefined}
                                  >
                                    {mlcV!.maxWork24h.value} Hrs
                                  </td>
                                  <td className='text-center align-middle'>--</td>
                                  <td
                                    className='text-center align-middle'
                                    style={
                                      rowRuleCodes.has('MLC_MAX_WORK_7D') ||
                                        (is7dWindowEnd && mlcV!.maxWork7d.isViolation) ||
                                        previewCodes.length
                                        ? VIOLATION_CELL_STYLE
                                        : {}
                                    }
                                    title={previewCodes.length ? previewCodes.join(', ') : undefined}
                                  >
                                    {mlcV!.maxWork7d.value} Hrs
                                  </td>
                                  <td
                                    className='text-center align-middle'
                                    style={
                                      rowRuleCodes.has('MLC_MIN_REST_7D') ||
                                        rowRuleCodes.has('REST_7D_MIN_70') ||
                                        (allowExceptions && is7dWindowEnd && mlcV!.minRest7d?.isViolation) ||
                                        previewCodes.length
                                        ? VIOLATION_CELL_STYLE
                                        : {}
                                    }
                                    title={previewCodes.length ? previewCodes.join(', ') : undefined}
                                  >
                                    {allowExceptions ? `${mlcV!.minRest7d!.value} Hrs` : '--'}
                                  </td>
                                  <td className='text-center align-middle' title={previewCodes.length ? previewCodes.join(', ') : undefined}>--</td>
                                  <td className='text-center align-middle' title={previewCodes.length ? previewCodes.join(', ') : undefined}>--</td>
                                  <td
                                    className='text-center align-middle'
                                    title={detailsSummary?.tooltip}
                                  >
                                    {detailsSummary ? (
                                      <>
                                        {detailsSummary.lines.map((ln, idx) => (
                                          <div key={idx} className='small text-start'>{ln}</div>
                                        ))}
                                        {detailsSummary.extraCount > 0 && (
                                          <div className='text-muted small'>(+{detailsSummary.extraCount} more)</div>
                                        )}
                                      </>
                                    ) : (
                                      '--'
                                    )}
                                  </td>
                                </>
                              )}
                            </tr>
                          )
                        })}
                        <tr className='table-light fw-bold'>
                          <td colSpan={1 + timeSlots.length} className='text-end text-uppercase'>
                            Totals
                          </td>
                          <td className='text-center'>{anchoredWorkTotal.toFixed(1)}</td>
                          <td className='text-center'>{anchoredRestTotal.toFixed(1)}</td>
                          <td colSpan={6}></td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer - only show when table is visible */}
              <div className='card-footer'>
                <div className='d-flex justify-content-between align-items-center'>
                  <div className='d-flex align-items-center gap-4'>
                    <div className='d-flex align-items-center'>
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          backgroundColor: '#ffc107',
                          border: '1px solid #ffb300',
                          borderRadius: '2px',
                          marginRight: '8px',
                        }}
                      ></div>
                      <span className='text-muted'>Working Hours</span>
                    </div>
                    <div className='d-flex align-items-center'>
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          border: '1px solid #dee2e6',
                          borderRadius: '2px',
                          marginRight: '8px',
                        }}
                      ></div>
                      <span className='text-muted'>Rest Hours</span>
                    </div>
                  </div>
                  <div className='d-flex gap-2'>
                    <button
                      className='btn btn-outline-secondary btn-sm border'
                      onClick={clearUnsavedChanges}
                      disabled={!hasUnsavedChanges}
                      title={!hasUnsavedChanges ? 'No unsaved changes' : 'Discard local changes'}
                    >
                      Clear Unsaved
                    </button>

                    <button
                      className='btn btn_primary btn-sm'
                      onClick={handleSaveChanges}
                      disabled={!hasUnsavedChanges}
                      title={!hasUnsavedChanges ? 'No unsaved changes' : 'Save local changes'}
                    >
                      Save Changes
                    </button>

                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const RestHour: FC = () => {
  const [crew, setCrew] = useState<Crew[]>([])
  const [remarks, setRemarks] = useState<Map<string, string>>(new Map())
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedVessel, setSelectedVessel] = useState('')
  const [selectedRank, setSelectedRank] = useState('')
  const [selectedCrew, setSelectedCrew] = useState<{ id: number; name: string } | null>(null)
  const [userSelectedCrew, setUserSelectedCrew] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState('')
  const [selectedDate, setSelectedDate] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortConfig, setSortConfig] = useState<{
    key:
    | 'crewName'
    | 'rank'
    | 'vessel'
    | 'date'
    | 'workHours'
    | 'restHours'
    | 'overtime'
    | 'status'
    | null
    direction: 'asc' | 'desc'
  }>({ key: 'rank', direction: 'asc' })
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [activeTab, setActiveTab] = useState('matrix-overview')
  const [isMatrixOverviewModalOpen, setIsMatrixOverviewModalOpen] = useState(false)
  const [matrixOverviewCrewId, setMatrixOverviewCrewId] = useState<number | ''>('')
  const [matrixProfileManualValues, setMatrixProfileManualValues] = useState<
    Record<number, Partial<Record<MatrixProfileFieldKey, string>>>
  >({})
  const [matrixOverviewCrewDetails, setMatrixOverviewCrewDetails] = useState<Record<number, Crew>>({})
  const [matrixOverviewCrewDocForms, setMatrixOverviewCrewDocForms] = useState<
    Record<number, CrewDocumentResponse | null>
  >({})
  const [matrixOverviewCourseCertificates, setMatrixOverviewCourseCertificates] = useState<
    Record<number, CourseCertificateResponse[]>
  >({})
  const [matrixOverviewSeaServices, setMatrixOverviewSeaServices] = useState<
    Record<number, SeaServiceResponse[]>
  >({})
  const [regulation, setRegulation] = useState<Regulation>('stcw')
  const [allowExceptions, setAllowExceptions] = useState(true)
  const [exceptionOverrides, setExceptionOverrides] = useState<Set<string>>(new Set())
  const [filterCompanyGroupId, setFilterCompanyGroupId] = useState<number | ''>('')
  const [filterCompanyAdminId, setFilterCompanyAdminId] = useState<number | ''>('')
  const [filterVesselId, setFilterVesselId] = useState<number | ''>('')
  const [isCrewLoading, setIsCrewLoading] = useState<boolean>(true)
  const [isHoursLoading, setIsHoursLoading] = useState<boolean>(true)
  const fetchHoursSeqRef = useRef<number>(0)
  const fetchHoursAbortRef = useRef<AbortController | null>(null)

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    if (globalState.vessel?.id) {
      setFilterVesselId(Number(globalState.vessel.id))
    } else {
      setFilterVesselId('')
    }

    if (globalState.company) {
      if (globalState.company.type === 'company_group') {
        setFilterCompanyGroupId(Number(globalState.company.id))
        setFilterCompanyAdminId('')
      } else {
        setFilterCompanyAdminId(Number(globalState.company.id))
        setFilterCompanyGroupId('')
      }
    } else {
      setFilterCompanyGroupId('')
      setFilterCompanyAdminId('')
    }
  }, [globalState.vessel, globalState.company])
  // Reporting Day removed; keep fixed defaults for compatibility
  const reportingMode: ReportingAnchorMode = 'CALENDAR_DAY'
  const customAnchorHHmm = '00:00'
  const [fromDate, setFromDate] = useState<string>(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)           // last 30 days
    return d.toISOString().split('T')[0]
  })
  const [toDate, setToDate] = useState<string>(() => new Date().toISOString().split('T')[0])
  const [activeSignOn, setActiveSignOn] = useState<ActiveSignOn | null>(null)
  const [rollingViolations, setRollingViolations] = useState<RollingViolationWindow[]>([])
  const [rollingWindowsByDay, setRollingWindowsByDay] = useState<
    Record<string, { violations: string[]; violationCodes: string[]; windows: RollingViolationWindow[] }>
  >({})
  const [violationDrawerDay, setViolationDrawerDay] = useState<string | null>(null)
  const [violationDrawerItems, setViolationDrawerItems] = useState<RollingViolationWindow[]>([])
  const [showTable, setShowTable] = React.useState(true)
  const [apiEntries, setApiEntries] = useState<any[]>([])
  const [showWorkHours, setShowWorkHours] = useState(false)
  const [hoursMap, setHoursMap] = useState<Record<string, {
    work: number
    rest: number
    compliant: boolean
    violationCount: number
    violations: string[]
    violationCodes?: string[]
    approved: boolean
    summaryId: number | null
    remarks: string
  }>>({})
  const [rollingDaySummaries, setRollingDaySummaries] = useState<RollingDaySummary[]>([])

  const [isExportOpen, setIsExportOpen] = useState(false)
  const [exportType, setExportType] = useState<'pdf' | 'excel'>('pdf')
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const defaultExcelFrom = useMemo(() => {
    const d = new Date()
    d.setUTCDate(d.getUTCDate() - 29)
    return d.toISOString().slice(0, 10)
  }, [])
  const [excelFrom, setExcelFrom] = useState<string>(defaultExcelFrom)
  const [excelTo, setExcelTo] = useState<string>(todayISO)
  const defaultPdfStart = useMemo(() => {
    const end = new Date(toDate + 'T00:00:00Z')
    const start = new Date(end)
    start.setUTCDate(start.getUTCDate() - 6)
    return start.toISOString().slice(0, 10)
  }, [toDate])
  const [pdfWeekStart, setPdfWeekStart] = useState<string>(defaultPdfStart)
  const [pdfFrom, setPdfFrom] = useState<string>(defaultPdfStart)
  const [pdfTo, setPdfTo] = useState<string>(todayISO)

  const [vesselList, setVesselList] = useState<Vessel[]>([]) // ? new
  // const rollingRefreshRequestedRef = React.useRef(false)
  // const [rollingExceptionsData, setRollingExceptionsData] =
  //   useState<RollingExceptionResponse | null>(null)
  // const [rollingAsOf, setRollingAsOf] = useState<string | null>(null)

  // ===== Ranks (for dropdown + safe rendering) =====
  const [rankOptions, setRankOptions] = useState<{ id: number; label: string }[]>([])
  const [rankMap, setRankMap] = useState<Record<number, string>>({})
  const [signedOnCrewList, setSignedOnCrewList] = useState<any[]>([])
  const getRankLabel = (c: any) =>
    rankMap[Number(c?.rankInfo?.id ?? c?.rankId)] ??
    c?.rankInfo?.rank ??
    c?.rank ??
    '—'

  const filteredRankOptions = useMemo(() => {
    const activeRankIds = new Set<number>()
    const activeRankLabels = new Set<string>()

    signedOnCrewList.forEach((record) => {
      const matchVessel = !filterVesselId || Number(record.vesselId) === Number(filterVesselId)
      if (matchVessel) {
        if (record.rank) {
          activeRankIds.add(Number(record.rank))
        }
        if (record.rankInfo?.id) {
          activeRankIds.add(Number(record.rankInfo.id))
        }
        if (record.rankInfo?.rank) {
          activeRankLabels.add(safeRankString(record.rankInfo.rank).trim().toLowerCase())
        }
      }
    })

    return rankOptions.filter((opt) => {
      return activeRankIds.has(opt.id) || activeRankLabels.has(safeRankString(opt.label).trim().toLowerCase())
    })
  }, [rankOptions, signedOnCrewList, filterVesselId, rankMap])

  // ADD — master lists (kept separate from crew-derived fallbacks)
  const [companies, setCompanies] = useState<CompanyGroupOpt[]>([])
  const [subcompanies, setSubcompanies] = useState<SubcompanyOpt[]>([])

  const { currentUser, isSuperAdmin, hasPermission } = useAuth()
  const canReadRestHours = useCrewingCanRead(CREWING_READ_PERMISSIONS.REST_HOURS)
  const permissionRevision = useCrewingPermissionRevision()
  const crewId = currentUser?.roleEntityId
  const vesselId = currentUser?.vessel?.id
  const rankId = currentUser?.rank?.id
  const roleId = currentUser?.role?.id

  const requiresVessel = (roleId ?? 0) === 1 || (roleId ?? 0) === 2 || (roleId ?? 0) === 5 || (roleId ?? 0) === 6
  const ensureVesselSelected = () => {
    if (requiresVessel && typeof filterVesselId !== 'number') {
      notify.warn('Please select a Vessel first in Vessel Filter to export.')
      return false
    }
    return true
  }

  // shared id helpers
  const toNum = (v: any): number | undefined =>
    v === null || v === undefined || v === '' || isNaN(Number(v)) ? undefined : Number(v)

  const pickCompanyGroupId = (m: any): number | undefined =>
    toNum(
      m?.companyGroupAdminId ??
      m?.companyGroupId ??
      m?.companyGroupAdmin?.id ??
      m?.cgaid ??
      m?.cga?.id ??
      m?.vessel?.companyGroupAdminId ??
      m?.vessel?.companyGroupAdmin?.id
    )

  const pickCompanyAdminId = (m: any): number | undefined =>
    toNum(
      m?.companyAdminId ??
      m?.companyId ??
      m?.companyAdmin?.id ??
      m?.vessel?.companyAdminId ??
      m?.vessel?.companyAdmin?.id
    )

  const pickVesselId = (m: any): number | undefined => toNum(m?.vessel?.id ?? m?.vesselId)

  // Rank helpers
  const canonicalRank = getCanonicalRank(currentUser?.rank?.rank ?? (currentUser?.rank as any)?.rank ?? currentUser?.rank)
  const isRankMaster = canonicalRank === 'MASTER'
  const isMasterByName = isRankMaster // alias for existing code paths
  const isCrewNonMaster = roleId === 4 && !isRankMaster

  /** Non-master crew login: forcibly bind selection to logged-in crew (guards stale URL/state). */
  useEffect(() => {
    if (roleId !== 4 || isRankMaster || !crewId) return
    if (selectedCrew && Number(selectedCrew.id) !== Number(crewId)) {
      const fallbackName =
        (currentUser as any)?.fullName ||
        (currentUser as any)?.name ||
        (currentUser as any)?.username ||
        'Me'
      setSelectedCrew({ id: Number(crewId), name: String(fallbackName) })
      setUserSelectedCrew(false)
    }
  }, [roleId, isRankMaster, crewId, selectedCrew, currentUser])

  // Operator flavors:
  // - No companyGroupAdminId => acts like Superadmin
  // - Has companyGroupAdminId => acts like Company Group Admin
  const isOperator = roleId === 6
  const operatorActsLikeSuperadmin = isOperator && !currentUser?.companyGroupAdminId
  const operatorActsLikeGroupAdmin = isOperator && !!currentUser?.companyGroupAdminId

  // Sign-on window (shared)
  const signOnStartISO = activeSignOn?.onboardFrom?.slice(0, 10) || null
  const onboardFromDate = activeSignOn?.onboardFrom?.slice(0, 10) || null
  const onboardToDate =
    activeSignOn?.onboardTo?.slice(0, 10) || new Date().toISOString().slice(0, 10)

  const signOnHHmm = useMemo(
    () => getHHmmFromIsoUtc(activeSignOn?.onboardFrom ?? null),
    [activeSignOn?.onboardFrom]
  )

  const floorToHalfHourLocal = (value: Date) => {
    const next = new Date(value.getTime())
    const minutes = next.getMinutes()
    next.setMinutes(minutes < 30 ? 0 : 30, 0, 0)
    return next
  }

  const formatMatrixRollingDateTime = (value: Date) =>
    value.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

  const matrixReportingTz = useMemo(() => {
    if (activeSignOn?.timezone && activeSignOn.timezone !== 'UTC') return activeSignOn.timezone
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    } catch {
      return 'UTC'
    }
  }, [activeSignOn?.timezone])

  const [matrixRollingAnchor, setMatrixRollingAnchor] = useState<Date>(() =>
    floorToHalfHourLocal(new Date())
  )
  const [matrixLiveRolling, setMatrixLiveRolling] = useState<boolean>(false)
  const lastFetchedRollingAnchorRef = useRef(matrixRollingAnchor.getTime())

  useEffect(() => {
    if (activeTab !== 'matrix-overview') return

    const tick = () => setMatrixRollingAnchor(floorToHalfHourLocal(new Date()))
    tick()

    const now = new Date()
    const minutes = now.getMinutes()
    const seconds = now.getSeconds()
    const millis = now.getMilliseconds()
    const nextHalf = minutes < 30 ? 30 : 60
    const msToNext = ((nextHalf - minutes) * 60 - seconds) * 1000 - millis

    let intervalId: number | null = null
    const timeoutId = window.setTimeout(() => {
      tick()
      intervalId = window.setInterval(tick, 30 * 60 * 1000)
    }, Math.max(msToNext, 0))

    return () => {
      window.clearTimeout(timeoutId)
      if (intervalId !== null) window.clearInterval(intervalId)
    }
  }, [activeTab])

  // Validate Export range: no future dates, from <= to
  const validateExportRange = (fromISO: string, toISO: string, formatName: string = 'export') => {
    if (!fromISO || !toISO) {
      notify.warn(`Please choose both From and To dates for ${formatName} export.`);
      return false;
    }
    if (fromISO > todayISO || toISO > todayISO) {
      notify.warn('Future date selection is not allowed.');
      return false;
    }
    const from = new Date(fromISO + 'T00:00:00Z');
    const to = new Date(toISO + 'T00:00:00Z');
    if (from > to) {
      notify.warn('From date must be on or before To date.');
      return false;
    }
    return true;
  };
  // Removed: Exceptions are now always on
  // useEffect(() => {
  //   if (activeTab !== 'update' && allowExceptions) {
  //     setAllowExceptions(false);
  //   }
  // }, [activeTab, allowExceptions]);


  // recompute default when matrix date range changes
  useEffect(() => {
    const end = new Date(toDate + 'T00:00:00Z')
    const start = new Date(end); start.setUTCDate(start.getUTCDate() - 6)
    setPdfWeekStart(start.toISOString().slice(0, 10))
  }, [fromDate, toDate])


  useEffect(() => {
    if (!currentUser) return

    if (roleId === 4) {
      // Crew — default to own vessel as presentation filter only
      setFilterCompanyGroupId('')
      setFilterCompanyAdminId('')
      setFilterVesselId(currentUser.vessel?.id ?? '')
    } else {
      // Backend enforces tenant scope; do not auto-apply company filters for CGA/CA
      setFilterCompanyGroupId('')
      setFilterCompanyAdminId('')
      setFilterVesselId('')
    }
  }, [currentUser, roleId])

  useEffect(() => {
    ; (async () => {
      try {
        const rs = await getRanksforList?.()
        const opts = Array.isArray(rs)
          ? rs.map((r: any) => ({
            id: Number(r.id),
            label: String(r.rank ?? r.name ?? r.title ?? `Rank #${r.id}`),
          }))
          : []

        if (opts.length) {
          const uniqueOpts: { id: number; label: string }[] = [];
          const seenLabels = new Set<string>();
          for (const opt of opts) {
            if (!seenLabels.has(opt.label)) {
              seenLabels.add(opt.label);
              uniqueOpts.push(opt);
            }
          }
          uniqueOpts.sort((x, y) => compareRank(x.label, y.label, 'asc'));
          setRankOptions(uniqueOpts)
          const m: Record<number, string> = {}
          opts.forEach((o) => {
            m[o.id] = o.label
          })
          setRankMap(m)
          return
        }
        // fallback (if API empty)
        throw new Error('Empty rank list')
      } catch {
        const fallback: Record<number, string> = {}
        crew.forEach((c) => {
          const id = Number(c.rankInfo?.id ?? c.rankId)
          const label = String(c.rankInfo?.rank ?? c.rank ?? '')
          if (id && label) fallback[id] = label
        })
        const fallbackArray = Object.entries(fallback).map(([id, label]) => ({ id: Number(id), label }))
        const uniqueFallback: { id: number; label: string }[] = [];
        const seenFallback = new Set<string>();
        for (const opt of fallbackArray) {
          if (!seenFallback.has(opt.label)) {
            seenFallback.add(opt.label);
            uniqueFallback.push(opt);
          }
        }
        setRankOptions(uniqueFallback.sort((x, y) => compareRank(x.label, y.label, 'asc')))
        setRankMap(fallback)
      }
    })()
    // re-run if the crew data changes (for fallback)
  }, [crew])

  // REMOVE the other fetchCrewHours() useEffects
  useEffect(() => {
    if (!canReadRestHours || !currentUser || !fromDate || !toDate) return
    if (activeTab !== 'matrix-overview') return
    if (isCrewLoading) return
    // Auth, permission and default filter state can settle in separate renders.
    // Coalesce those changes instead of starting and canceling a page of requests for each one.
    const timer = window.setTimeout(() => { void fetchCrewHours() }, 300)
    return () => {
      window.clearTimeout(timer)
      fetchHoursAbortRef.current?.abort()
    }
  }, [
    canReadRestHours,
    permissionRevision,
    currentUser,              // refetch once auth lands
    fromDate, toDate,         // date changes
    filterCompanyGroupId,
    filterCompanyAdminId,
    filterVesselId,
    selectedRank,
    selectedCrew,              // name filter changes
    currentPage,
    rowsPerPage,
    sortConfig,
    rankMap,
    crew,
    regulation,
    reportingMode,
    customAnchorHHmm,
    matrixLiveRolling,
    activeTab,
    isCrewLoading
  ])

  // The main overview effect handles tab/permission changes. Only a new
  // half-hour boundary needs an extra refresh while live rolling is enabled.
  useEffect(() => {
    const anchorTime = matrixRollingAnchor.getTime()
    if (anchorTime === lastFetchedRollingAnchorRef.current) return
    lastFetchedRollingAnchorRef.current = anchorTime
    if (activeTab !== 'matrix-overview') return
    if (!matrixLiveRolling) return
    if (!canReadRestHours || !currentUser || !fromDate || !toDate) return
    void fetchCrewHours()
  }, [matrixRollingAnchor, matrixLiveRolling, activeTab, canReadRestHours, permissionRevision])

  useEffect(() => {
    if (typeof filterCompanyGroupId !== 'number') return

    // Build allowed subcompany ids from raw state (available earlier than memos)
    const allowedIds = new Set(
      (subcompanies || [])
        .filter((sc) => sc.companyGroupId === filterCompanyGroupId)
        .map((sc) => sc.id)
    )

    if (typeof filterCompanyAdminId === 'number' && !allowedIds.has(filterCompanyAdminId)) {
      setFilterCompanyAdminId('') // previously selected subcompany doesn't belong to the new company
    }
  }, [filterCompanyGroupId, filterCompanyAdminId, subcompanies])

  type SummaryCell = {
    work: number
    rest: number
    compliant: boolean
    violationCount: number
    violations: string[]
    violationCodes?: string[]
    approved?: boolean
    summaryId?: number | null
    remarks?: string
  }

  const patchMatrixSummary = (
    crewIdForPatch: number,
    summaryByDate: Record<string, Omit<SummaryCell, 'approved' | 'summaryId'>>
  ) => {
    setHoursMap((prev) => {
      const next = { ...prev }
      for (const [dateKey, s] of Object.entries(summaryByDate)) {
        const key = `${crewIdForPatch}-${dateKey}`
        const prevCell = prev[key] || {
          work: 0,
          rest: 24,
          compliant: true,
          violationCount: 0,
          violations: [],
          approved: false,
          summaryId: null,
          remarks: '',
        }
        // Edited days become unapproved until server says otherwise
        next[key] = {
          ...prevCell,
          ...s,
          approved: false,
          summaryId: prevCell.summaryId ?? null,
        }
      }
      return next
    })
  }

  const matrixRollingCrewId = useMemo(() => {
    if (isRankMaster && selectedCrew?.id) return selectedCrew.id
    if (isCrewNonMaster) return crewId
    return crewId ?? null
  }, [isRankMaster, selectedCrew, isCrewNonMaster, crewId])

  const matrixRollingVesselId = useMemo(() => {
    if (!matrixRollingCrewId) return vesselId
    const match = crew.find((c) => c.crewId === matrixRollingCrewId)
    return match?.vessel?.id ?? vesselId
  }, [crew, vesselId, matrixRollingCrewId])

  // Dates with entries for the selected crew (for Matrix date pickers)
  const matrixPickerEntryDates = useMemo(() => {
    const s = new Set<string>()
    const crewKey =
      selectedCrew?.id ??
      (roleId === 4 && !isCrewNonMaster ? crewId ?? undefined : undefined)
    if (!crewKey) return s
    Object.keys(hoursMap).forEach((k) => {
      if (k.startsWith(`${crewKey}-`)) {
        const parts = k.split('-')
        const date = parts.slice(1).join('-')
        if (date) s.add(date)
      }
    })
    return s
  }, [hoursMap, selectedCrew, roleId, isCrewNonMaster, crewId])

  const matrixRollingFromDate = useMemo(() => {
    if (!fromDate) return ''
    const d = new Date(fromDate + 'T00:00:00Z')
    d.setUTCDate(d.getUTCDate() - (ROLLING_WINDOW_DAYS - 1))
    return d.toISOString().slice(0, 10)
  }, [fromDate])

  const handleRemarksChange = (date: Date, value: string) => {
    const dateString = date.toISOString().split('T')[0]
    setRemarks((prev) => {
      const newRemarks = new Map(prev)
      newRemarks.set(dateString, value)
      return newRemarks
    })
  }

  const toggleExceptionDay = (
    crewIdForToggle: number,
    dateKey: string,
    dateKeysForCheck: string[],
    eligible: boolean
  ) => {
    if (!crewIdForToggle) {
      notify?.warn?.('Select a crew member to set exceptions.')
      return
    }
    const key = `${crewIdForToggle}-${dateKey}`
    setExceptionOverrides((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
        return next
      }
      if (!eligible) {
        notify?.warn?.('Only days with 6-10h rest can be marked as exceptions.')
        return prev
      }
      next.add(key)
      if (!validateExceptionWindow(crewIdForToggle, next, dateKeysForCheck)) {
        notify?.warn?.('Only 2 exceptions are allowed in any 7-day window.')
        return prev
      }
      return next
    })
  }

  useEffect(() => {
    if (!canReadRestHours) {
      setCrew([])
      setSignedOnCrewList([])
      setVesselList([])
      return
    }
    fetchCrew()
    fetchSignedOnCrew()
    fetchVessels()
  }, [canReadRestHours, permissionRevision])

  // Company = Company GROUP Admins (role 5)
  // Subcompany = Company Admins (role 2)
  useEffect(() => {
    ; (async () => {
      try {
        const [groups, admins] = await Promise.all([
          getCompanyAdminList?.().catch(() => []), // ? /company-group-admins
          getCompanyList?.().catch(() => []), // ? /users/company-admins
        ])

        setCompanies(
          Array.isArray(groups)
            ? groups.map((g: any) => ({
              id: Number(g.id),
              name: g.name ?? g.companyGroupAdminName ?? `Company Group #${g.id}`,
            }))
            : []
        )

        setSubcompanies(
          Array.isArray(admins)
            ? admins.map((a: any) => ({
              id: Number(a.id),
              name: a.name ?? a.companyAdminName ?? `Subcompany #${a.id}`,
              companyGroupId: Number(
                a.cgaid ?? a.cga?.id ?? a.companyGroupAdminId ?? a.companyGroupId
              ),
            }))
            : []
        )
      } catch (e) {
        console.error('Failed to load company lists', e)
        setCompanies([])
        setSubcompanies([])
      }
    })()
  }, [])

  // useEffect(() => {
  //   fetchCrewHours()
  // }, [fromDate, toDate])

  useEffect(() => {
    setSelectedCrew(null) // reset crew if rank changes
    setUserSelectedCrew(true) // honor user's filter; don't auto-pick a crew
  }, [selectedRank])

  // Replace ENTIRE fetchCrew with this:
  const fetchCrew = async () => {
    setIsCrewLoading(true)
    try {
      const raw = await getCrewList()

      // normalize ? ONE shape your filters expect
      const normalized: Crew[] = (Array.isArray(raw) ? raw : []).map((r: any) => {
        const toNum = (v: any) =>
          v === null || v === undefined || isNaN(Number(v)) ? undefined : Number(v)

        const rankIdVal = toNum(r?.rankInfo?.id ?? r?.rankId ?? r?.rank?.id) ?? 0
        const rankName = r?.rankInfo?.rank ?? r?.rank ?? ''

        // prefer vessel obj; fallback to root vessel fields if any exist
        const vesselObj = r?.vessel
          ? {
            id: Number(r.vessel.id),
            fleet_name: r.vessel.fleet_name ?? r.vessel.name ?? r.vessel.fleetName ?? '',
          }
          : undefined

        // robust company ids (root or nested via vessel)
        const companyGroupAdminId = toNum(
          r.companyGroupAdminId ??
          r.companyGroupId ??
          r.companyGroupAdmin?.id ??
          r.vessel?.companyGroupAdminId ??
          r.vessel?.companyGroupAdmin?.id
        )

        const companyAdminId = toNum(
          r.companyAdminId ??
          r.companyId ??
          r.companyAdmin?.id ??
          r.vessel?.companyAdminId ??
          r.vessel?.companyAdmin?.id
        )

        return {
          ...r,
          // unify identity:
          id: toNum(r.id) ?? toNum(r.userId) ?? 0,
          userId: toNum(r.userId) ?? toNum(r.id) ?? 0,
          crewId: toNum(r.crewId ?? r.id ?? r.userId) ?? 0,
          crewName: r.crewName ?? r.name ?? r.username ?? '',
          name: r.name ?? r.crewName ?? r.username ?? '',

          // ranks
          rankId: rankIdVal,
          rank: rankName,
          rankInfo: r.rankInfo ?? { id: rankIdVal, rank: rankName },

          // company scope at root for filters
          companyGroupAdminId: companyGroupAdminId ?? 0,
          companyAdminId: companyAdminId,

          // vessel (only id + name needed for filters/dropdowns)
          vessel: vesselObj,

          // ensure these flags exist
          active: Boolean(r.active ?? r.isActive ?? r?.isActive),

          // approval field (kept if present)
          approvalStatus: r.approvalStatus,
        } as Crew
      })

      // HARD FILTER for Rest Hours (business rule):
      // show crew attached to a vessel; keep those without an explicit active flag too (exclude only if active === false)
      const filtered = normalized.filter((c) => c.active !== false && !!c.vessel?.id)

      setCrew(filtered)
    } catch (error) {
      console.error('Failed to fetch crew list:', error)
      showErrorToast(error, 'Failed to load crew list.', 'load')
      setCrew([])
    } finally {
      setIsCrewLoading(false)
    }
  }

  const fetchSignedOnCrew = async () => {
    try {
      const active = await getSignedOnCrewList()
      setSignedOnCrewList(active || [])
    } catch (error) {
      console.error('Failed to fetch signed on crew list:', error)
    }
  }

  const fetchVessels = async () => {
    try {
      const vesselList = await getVesselList()
      setVesselList(vesselList)
    } catch (error) {
      console.error('Failed to fetch vessel list:', error)
      showErrorToast(error, 'Failed to load vessel list.', 'load')
      setVesselList([])
    }
  }

  // Generate time slots in 30-minute intervals (24 hours)
  const generateTimeSlots = (): string[] => {
    const slots: string[] = []
    for (let hour = 0; hour < 24; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`)
      slots.push(`${hour.toString().padStart(2, '0')}:30`)
    }
    return slots
  }

  const timeSlots = generateTimeSlots()
  const [workingHours, setWorkingHours] = useState<Set<string>>(() => {
    const defaultWorkingHours = new Set<string>()
    return defaultWorkingHours
  })
  const [selectedEntries, setSelectedEntries] = useState<{ date: string; slotIndex: number }[]>([])
  const toggleWorkingHour = (memberId: number, date: Date, timeIndex: number): void => {
    const dateString = date.toISOString().split('T')[0]
    const key = `${memberId}-${dateString}-${timeIndex}`
    const newWorkingHours = new Set(workingHours)

    if (newWorkingHours.has(key)) {
      newWorkingHours.delete(key)
    } else {
      newWorkingHours.add(key)
    }

    setWorkingHours(newWorkingHours)
  }

  // Get unique values for filter dropdowns
  const vessels = Array.from(new Set(crew.map((record) => record.vessel)))
  const ranks = Array.from(new Set(crew.map((record) => record.rankInfo)))
  const crewNames = Array.from(new Set(crew.map((member) => member.crewName)))
  // const dates = Array.from(new Set(crew.map(record => record.date)));

  // 1. State for the validation error message
  const [error, setError] = useState('')

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedVessel('')
    setSelectedRank('')
    setSelectedCrew(null)
    setUserSelectedCrew(false)
    setSelectedStatus('')
    setSelectedDate('')
    setFilterCompanyGroupId('')
    setFilterCompanyAdminId('')
    setFilterVesselId('')
  }

  const handleSort = (key: typeof sortConfig.key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages && page !== currentPage) {
      // Set loading in the same click update as the page number. Otherwise the
      // new crew rows briefly render with empty cells before the fetch starts.
      setIsHoursLoading(true)
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setIsHoursLoading(true)
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1) // reset to first page
  }

  const filteredCrewMembers = React.useMemo(() => {
    const isCrew = roleId === 4
    const isMaster = (currentUser?.rank?.rank ?? '').toLowerCase().includes('master')

    const fVES = typeof filterVesselId === 'number' ? filterVesselId : undefined
    const fRank = selectedRank ? Number(selectedRank) : undefined
    const fCrew = selectedCrew?.id ? Number(selectedCrew.id) : undefined
    const fCG =
      typeof filterCompanyGroupId === 'number' && (roleId === 1 || roleId === 6)
        ? filterCompanyGroupId
        : undefined
    const fCA =
      typeof filterCompanyAdminId === 'number' && (roleId === 1 || roleId === 6)
        ? filterCompanyAdminId
        : undefined

    return crew.filter((m) => {
      if (isCrew && !isMaster) return m.crewId === crewId

      const vId = toNum(m.vessel?.id) ?? pickVesselId(m)
      const rId = toNum(m.rankInfo?.id ?? m.rankId)
      const rLabel = rankMap[Number(rId)] ?? m.rankInfo?.rank ?? m.rank ?? ''

      const byVES = !fVES || vId === fVES
      const byRank = !selectedRank || rLabel === selectedRank
      const byName = !fCrew || m.crewId === fCrew

      // Superadmin/operator explicit presentation filters only
      if (fCG || fCA) {
        const cgId = toNum(m.companyGroupAdminId) ?? pickCompanyGroupId(m)
        const caId = toNum(m.companyAdminId) ?? pickCompanyAdminId(m)
        const byCG = !fCG || cgId === fCG
        const byCA = !fCA || caId === fCA
        return byCG && byCA && byVES && byRank && byName
      }

      return byVES && byRank && byName
    })
  }, [
    crew,
    roleId,
    currentUser,
    selectedRank,
    selectedCrew,
    filterCompanyGroupId,
    filterCompanyAdminId,
    filterVesselId,
    crewId,
    rankMap,
  ])

  // Full list of crew options for the Name dropdown (filtered by vessel/rank/company, but NOT by selectedCrew)
  const crewOptions = React.useMemo(() => {
    const isCrew = roleId === 4
    const isMaster = (currentUser?.rank?.rank ?? (currentUser?.rank as any)?.rank ?? currentUser?.rank ?? '').toString().toLowerCase().includes('master')

    const fVES = typeof filterVesselId === 'number' ? filterVesselId : undefined
    const fCG =
      typeof filterCompanyGroupId === 'number' && (roleId === 1 || roleId === 6)
        ? filterCompanyGroupId
        : undefined
    const fCA =
      typeof filterCompanyAdminId === 'number' && (roleId === 1 || roleId === 6)
        ? filterCompanyAdminId
        : undefined

    return crew.filter((m) => {
      if (isCrew && !isMaster) return m.crewId === crewId

      const vId = toNum(m.vessel?.id) ?? pickVesselId(m)
      const rId = toNum(m.rankInfo?.id ?? m.rankId)
      const rLabel = rankMap[Number(rId)] ?? m.rankInfo?.rank ?? m.rank ?? ''

      const byVES = !fVES || vId === fVES
      const byRank = !selectedRank || rLabel === selectedRank

      if (fCG || fCA) {
        const cgId = toNum(m.companyGroupAdminId) ?? pickCompanyGroupId(m)
        const caId = toNum(m.companyAdminId) ?? pickCompanyAdminId(m)
        const byCG = !fCG || cgId === fCG
        const byCA = !fCA || caId === fCA
        return byCG && byCA && byVES && byRank
      }

      return byVES && byRank
    })
  }, [
    crew,
    roleId,
    currentUser,
    selectedRank,
    filterCompanyGroupId,
    filterCompanyAdminId,
    filterVesselId,
    crewId,
    rankMap,
  ])

  // const selectedCrewRecord = useMemo(
  //   () => crew.find((member) => member.crewId === selectedCrew?.id) ?? null,
  //   [crew, selectedCrew]
  // )

  // const rollingCrewId = useMemo(() => {
  //   if (roleId === 4 && !isMasterByName) return crewId ?? undefined
  //   return selectedCrew?.id ?? undefined
  // }, [roleId, isMasterByName, crewId, selectedCrew])
  //
  // const rollingVesselId = useMemo(() => {
  //   if (typeof filterVesselId === 'number') return filterVesselId
  //   if (selectedCrewRecord?.vessel?.id) return selectedCrewRecord.vessel.id
  //   return currentUser?.vessel?.id ?? undefined
  // }, [filterVesselId, selectedCrewRecord, currentUser])
  //
  // const rollingHasViolation = useMemo(() => {
  //   const windows = rollingExceptionsData?.windows ?? []
  //   return windows.some(
  //     (window) => window.violated || (window.exceptions && window.exceptions.length > 0)
  //   )
  // }, [rollingExceptionsData])
  //
  // const rollingAsOfDateKey = useMemo(() => {
  //   if (!rollingAsOf) return null
  //   const date = new Date(rollingAsOf)
  //   if (Number.isNaN(date.getTime())) return null
  //   return date.toLocaleDateString('en-CA')
  // }, [rollingAsOf])
  //
  // const rollingTooltip = useMemo(() => {
  //   if (!rollingExceptionsData || !rollingHasViolation) return ''
  //   return buildRollingTooltip(rollingExceptionsData, rollingAsOf)
  // }, [rollingExceptionsData, rollingHasViolation, rollingAsOf])



  // SORT the role-scoped set that you actually render
  const sortedCrew = useMemo(() => {
    const list = [...filteredCrewMembers]
    if (!sortConfig.key) return list

    return list.sort((a, b) => {
      let valA: string | number = ''
      let valB: string | number = ''

      switch (sortConfig.key) {
        case 'crewName':
          valA = a.crewName || ''
          valB = b.crewName || ''
          break
        case 'rank': {
          const ra =
            rankMap[Number(a?.rankInfo?.id ?? a?.rankId)] ??
            a?.rankInfo?.rank ?? a?.rank ?? '';
          const rb =
            rankMap[Number(b?.rankInfo?.id ?? b?.rankId)] ??
            b?.rankInfo?.rank ?? b?.rank ?? '';
          return compareRank(ra, rb, sortConfig.direction);
        }
        case 'vessel':
          valA = a?.vessel?.fleet_name || ''
          valB = b?.vessel?.fleet_name || ''
          break
        default:
          valA = (a as any)[sortConfig.key!] ?? ''
          valB = (b as any)[sortConfig.key!] ?? ''
          break
      }


      if (typeof valA === 'string') valA = valA.toLowerCase()
      if (typeof valB === 'string') valB = valB.toLowerCase()

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredCrewMembers, sortConfig])

  useEffect(() => {
    if (activeTab !== 'update') return;

    // already selected -> nothing to do
    if (selectedCrew?.id) return;

    const isCrew = roleId === 4;
    const isMaster = canonicalRank === 'MASTER';

    // crew (not master): always pin to self
    if (isCrew && !isMaster && crewId) {
      const fallbackName =
        (currentUser as any)?.fullName ||
        (currentUser as any)?.name ||
        (currentUser as any)?.username ||
        'Me';
      setSelectedCrew({ id: crewId, name: String(fallbackName) });
      setUserSelectedCrew(true);
      return;
    }

    // Update tab: Master defaults to own row when present; else first visible crew
    if (activeTab === 'update' && !selectedCrew && crewOptions.length > 0) {
      if (isMaster && crewId) {
        const selfRow = crewOptions.find((m) => Number(m.crewId) === Number(crewId))
        if (selfRow) {
          setSelectedCrew({ id: selfRow.crewId, name: selfRow.crewName })
          setUserSelectedCrew(false)
          return
        }
      }
      const m = crewOptions[0];
      setSelectedCrew({ id: m.crewId, name: m.crewName });
      setUserSelectedCrew(true);
      return;
    }
  }, [activeTab, selectedCrew, roleId, currentUser, crewId, crewOptions, userSelectedCrew, canonicalRank]);


  // Clamp/compute pagination from the sorted list
  const totalPages = Math.max(1, Math.ceil(sortedCrew.length / rowsPerPage))
  useEffect(() => {
    // reset or clamp when the dataset shrinks, filters change, or page size changes
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [totalPages]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(1)
  }, [
    rowsPerPage,
    filterCompanyGroupId,
    filterCompanyAdminId,
    filterVesselId,
    selectedRank,
    selectedCrew,
    fromDate,
    toDate,
  ])

  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const pagedCrew = useMemo(
    () => sortedCrew.slice(indexOfFirstRecord, indexOfLastRecord),
    [sortedCrew, indexOfFirstRecord, indexOfLastRecord]
  )

  const matrixOverviewCrewRecord = useMemo(() => {
    const preferredId =
      typeof matrixOverviewCrewId === 'number'
        ? matrixOverviewCrewId
        : selectedCrew?.id ?? sortedCrew[0]?.crewId
    if (!preferredId) return sortedCrew[0] ?? null

    const detailed = matrixOverviewCrewDetails[preferredId]
    const baseRecord = detailed ?? (
      sortedCrew.find((member) => member.crewId === preferredId) ??
      crew.find((member) => member.crewId === preferredId) ??
      sortedCrew[0] ??
      null
    )

    if (!baseRecord) return null

    const docForm = matrixOverviewCrewDocForms[preferredId] ?? null
    const courses = matrixOverviewCourseCertificates[preferredId] ?? []
    if (!docForm && courses.length === 0) return baseRecord

    return {
      ...baseRecord,
      ...(docForm ?? {}),
      crewDocuments: {
        ...((baseRecord as any)?.crewDocuments ?? {}),
        ...(docForm ?? {}),
      },
      courses: courses.length > 0 ? courses : (baseRecord as any)?.courses,
    } as any
  }, [
    matrixOverviewCrewId,
    selectedCrew,
    sortedCrew,
    crew,
    matrixOverviewCrewDetails,
    matrixOverviewCrewDocForms,
    matrixOverviewCourseCertificates,
  ])

  const getMatrixRecordId = (record: any) => Number(record?.crewId ?? record?.id ?? 0)

  const getMatrixProfileValue = (record: any, key: MatrixProfileFieldKey) => {
    const recordId = getMatrixRecordId(record)
    const manual = matrixProfileManualValues?.[recordId]?.[key]
    if (manual !== undefined && manual !== null && String(manual).trim() !== '') return String(manual)
    return deriveMatrixProfileFieldValue(record, key)
  }

  const setMatrixProfileManualValue = (recordId: number, key: MatrixProfileFieldKey, value: string) => {
    setMatrixProfileManualValues((prev) => ({
      ...prev,
      [recordId]: {
        ...(prev[recordId] ?? {}),
        [key]: value,
      },
    }))
  }

  useEffect(() => {
    if (!isMatrixOverviewModalOpen) return
    if (typeof matrixOverviewCrewId === 'number') return
    const defaultCrewId = selectedCrew?.id ?? sortedCrew[0]?.crewId
    if (defaultCrewId) {
      setMatrixOverviewCrewId(defaultCrewId)
    }
  }, [isMatrixOverviewModalOpen, matrixOverviewCrewId, selectedCrew, sortedCrew])

  useEffect(() => {
    if (!isMatrixOverviewModalOpen) return
    if (typeof matrixOverviewCrewId !== 'number') return
    if (matrixOverviewCrewDetails[matrixOverviewCrewId]) return

      ; (async () => {
        try {
          const detail = await getCrewUnit(matrixOverviewCrewId)
          setMatrixOverviewCrewDetails((prev) => ({
            ...prev,
            [matrixOverviewCrewId]: detail as unknown as Crew,
          }))
        } catch (error) {
          console.warn('Failed to load crew detail for matrix profile', { crewId: matrixOverviewCrewId, error })
        }
      })()
  }, [isMatrixOverviewModalOpen, matrixOverviewCrewId, matrixOverviewCrewDetails])

  useEffect(() => {
    if (!isMatrixOverviewModalOpen) return
    if (typeof matrixOverviewCrewId !== 'number') return
    if (matrixOverviewCrewDocForms[matrixOverviewCrewId] !== undefined) return

      ; (async () => {
        try {
          const docForm = await getCrewDocument(matrixOverviewCrewId)
          setMatrixOverviewCrewDocForms((prev) => ({
            ...prev,
            [matrixOverviewCrewId]: docForm,
          }))
        } catch (error) {
          console.warn('Failed to load crew document form for matrix profile', { crewId: matrixOverviewCrewId, error })
          setMatrixOverviewCrewDocForms((prev) => ({
            ...prev,
            [matrixOverviewCrewId]: null,
          }))
        }
      })()
  }, [isMatrixOverviewModalOpen, matrixOverviewCrewId, matrixOverviewCrewDocForms])

  useEffect(() => {
    if (!isMatrixOverviewModalOpen) return
    if (typeof matrixOverviewCrewId !== 'number') return
    if (matrixOverviewCourseCertificates[matrixOverviewCrewId]) return

      ; (async () => {
        try {
          const certificates = await getCourseCertificates(matrixOverviewCrewId)
          setMatrixOverviewCourseCertificates((prev) => ({
            ...prev,
            [matrixOverviewCrewId]: Array.isArray(certificates) ? certificates : [],
          }))
        } catch (error) {
          console.warn('Failed to load course certificates for matrix profile', {
            crewId: matrixOverviewCrewId,
            error,
          })
          setMatrixOverviewCourseCertificates((prev) => ({
            ...prev,
            [matrixOverviewCrewId]: [],
          }))
        }
      })()
  }, [isMatrixOverviewModalOpen, matrixOverviewCrewId, matrixOverviewCourseCertificates])

  useEffect(() => {
    if (!isMatrixOverviewModalOpen) return
    if (typeof matrixOverviewCrewId !== 'number') return
    if (matrixOverviewSeaServices[matrixOverviewCrewId]) return

      ; (async () => {
        try {
          const seaServices = await getSeaServices(matrixOverviewCrewId)
          setMatrixOverviewSeaServices((prev) => ({
            ...prev,
            [matrixOverviewCrewId]: Array.isArray(seaServices) ? seaServices : [],
          }))
        } catch (error) {
          console.warn('Failed to load sea service for matrix profile', { crewId: matrixOverviewCrewId, error })
          setMatrixOverviewSeaServices((prev) => ({
            ...prev,
            [matrixOverviewCrewId]: [],
          }))
        }
      })()
  }, [isMatrixOverviewModalOpen, matrixOverviewCrewId, matrixOverviewSeaServices])

  const handleModalOpen = () => {
    setIsModalVisible(true)
  }

  const handleModalClose = () => {
    setIsModalVisible(false)
  }

  const handleModalSubmit = (data: any) => {
    console.log('Rest hour data submitted:', data)
    // Here you would typically add the data to your state or send to API
    // For now, we'll just log it
    notify?.success(`Rest hour entry added for ${data.crewName}.`)
  }

  const getDaySuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1:
        return 'st'
      case 2:
        return 'nd'
      case 3:
        return 'rd'
      default:
        return 'th'
    }
  }

  const generateDateLabels = (from: string, to: string) => {
    if (!from || !to) return []

    const start = new Date(from)
    const end = new Date(to)
    const labels: string[] = []

    let current = new Date(start)
    while (current <= end) {
      const day = current.getDate()
      const monthName = current.toLocaleString('default', { month: 'short' })
      labels.push(`${day}${getDaySuffix(day)} ${monthName}`)
      current.setDate(current.getDate() + 1)
    }

    return labels
  }

  const dateLabels = generateDateLabels(fromDate, toDate)

  // === 7D CHIP POSITION CONFIG ===
  type ChipSide = 'left' | 'right'
  const WEEK_CHIP_SIDE: ChipSide = 'right' // <-- do NOT write `as const`
  const IS_WEEK_CHIP_RIGHT = WEEK_CHIP_SIDE === 'right'

  const WEEK_CHIP_RESERVED = 56 // px reserved for chip
  const APPROVE_BTN_RESERVED = 28 // px reserved for ? button

  // === 7D WINDOW HELPERS FOR MATRIX OVERVIEW ===

  // Build the continuous list of YYYY-MM-DD keys for the selected range
  const dateKeys = useMemo(() => {
    if (!fromDate || !toDate) return []
    const out: string[] = []
    const start = new Date(fromDate + 'T00:00:00Z')
    const end = new Date(toDate + 'T00:00:00Z')
    const cur = new Date(start)
    while (cur <= end) {
      out.push(cur.toLocaleDateString('en-CA')) // YYYY-MM-DD
      cur.setUTCDate(cur.getUTCDate() + 1)
    }
    return out
  }, [fromDate, toDate])

  // Anchored 7d windows end at toDate, then toDate-7, etc. (only full windows)
  const weekEndIdxSet = useMemo(() => {
    const s = new Set<number>()
    if (!dateKeys.length) return s
    for (let ei = dateKeys.length - 1; ei >= 6; ei -= 7) s.add(ei)
    return s
  }, [dateKeys])

  const weekStartIdxSet = useMemo(() => {
    const s = new Set<number>()
    weekEndIdxSet.forEach((ei) => s.add(ei - 6))
    return s
  }, [weekEndIdxSet])

  // Compute per-crew anchored 7d summary for Matrix Overview overlay
  // Keyed by `${crewId}-${endDate}`
  const matrixAnchored7d = useMemo(() => {
    const out: Record<string, { windowStart: string; totalRest: number; violated: boolean }> = {}
    if (!dateKeys.length || filteredCrewMembers.length === 0) return out

    filteredCrewMembers.forEach((member) => {
      weekEndIdxSet.forEach((ei) => {
        const startIdx = ei - 6
        let sum = 0
        for (let di = startIdx; di <= ei; di++) {
          const dk = dateKeys[di]
          const key = `${member.crewId}-${dk}`
          const entry = hoursMap[key]
          // Same assumption as in Update tab if missing: treat as 24h rest
          sum += Number(entry?.rest ?? 24)
        }
        const endKey = dateKeys[ei]
        const startKey = dateKeys[startIdx]
        out[`${member.crewId}-${endKey}`] = {
          windowStart: startKey,
          totalRest: sum,
          violated: sum < 77,
        }
      })
    })

    return out
  }, [filteredCrewMembers, dateKeys, weekEndIdxSet, hoursMap])

  // after matrixAnchored7d (rest totals), also compute 7d WORK totals
  const matrixAnchored7dWork = useMemo(() => {
    const out: Record<string, { windowStart: string; totalWork: number }> = {}
    if (!dateKeys.length || filteredCrewMembers.length === 0) return out

    filteredCrewMembers.forEach((member) => {
      weekEndIdxSet.forEach((ei) => {
        const startIdx = ei - 6
        let sum = 0
        for (let di = startIdx; di <= ei; di++) {
          const dk = dateKeys[di]
          const entry = hoursMap[`${member.crewId}-${dk}`]
          sum += Number(entry?.work ?? 0)
        }
        out[`${member.crewId}-${dateKeys[ei]}`] = {
          windowStart: dateKeys[startIdx],
          totalWork: sum,
        }
      })
    })
    return out
  }, [filteredCrewMembers, dateKeys, weekEndIdxSet, hoursMap])

  // STCW exceptions per crew (selected by crew, rest 6-10h, max 2 per 7d window)
  const matrixExceptionDays = useMemo(() => {
    const out = new Set<string>()
    if (regulation !== 'stcw' || !allowExceptions) return out
    if (!dateKeys.length || filteredCrewMembers.length === 0) return out

    filteredCrewMembers.forEach((member) => {
      const crewId = member.crewId
      dateKeys.forEach((dk) => {
        const key = `${crewId}-${dk}`
        if (!exceptionOverrides.has(key)) return
        const entry = hoursMap[key]
        const rest = Number(entry?.rest ?? 24)
        if (rest >= 6 && rest < 10) out.add(key)
      })
    })

    return out
  }, [regulation, allowExceptions, filteredCrewMembers, dateKeys, hoursMap, exceptionOverrides])


  const matrixRollingViolations = useMemo(() => {
    return rollingWindowsByDay
  }, [rollingWindowsByDay])
  const dayHasDataMap = useMemo(() => {
    const m = new Map<string, boolean>()
    rollingDaySummaries.forEach((d) => m.set(d.date, d.hasData !== false))
    return m
  }, [rollingDaySummaries])

  const openViolationDrawer = (crewId: number, dateKey: string) => {
    const info = matrixRollingViolations[`${crewId}-${dateKey}`]
    const windows = info?.windows ?? []
    setViolationDrawerDay(dateKey)
    setViolationDrawerItems(windows)
  }

  const closeViolationDrawer = () => {
    setViolationDrawerDay(null)
    setViolationDrawerItems([])
  }

  const fetchCrewHours = async () => {
    fetchHoursAbortRef.current?.abort()
    const controller = new AbortController()
    fetchHoursAbortRef.current = controller
    const seq = ++fetchHoursSeqRef.current
    setIsHoursLoading(true)
    try {
      if (!fromDate || !toDate) return
      const isCrew = roleId === 4
      const isMaster = (currentUser?.rank?.rank ?? '').toLowerCase().includes('master')

      const vId =
        typeof filterVesselId === 'number'
          ? filterVesselId
          : (() => {
            const selectedCrewId = selectedCrew && selectedCrew.id > 0 ? selectedCrew.id : undefined
            if (selectedCrewId) {
              const crewObj = crew.find(
                (c) => c.crewId === selectedCrewId || (c as any).id === selectedCrewId
              )
              const v = crewObj ? pickVesselId(crewObj) : undefined
              if (v) return v
            }
            return isCrew ? currentUser?.vessel?.id : undefined
          })()

      const selectedCrewId = selectedCrew && selectedCrew.id > 0 ? selectedCrew.id : undefined
      const cId = isCrewNonMaster ? crewId : selectedCrewId

      const targetCrewList = cId
        ? [{ crewId: cId, vessel: { id: vId } }]
        : pagedCrew

      const allAnchoredMap: Record<string, any> = {}
      const allRollingMap: Record<
        string,
        { violations: string[]; violationCodes: string[]; windows: RollingViolationWindow[] }
      > = {}
      const allDaySummaries: any[] = []
      const allViolations: any[] = []

      await forEachWithConcurrency(
        targetCrewList,
        6,
        async (member: any) => {
          if (controller.signal.aborted) return
          const memberCrewId = member.crewId
          const memberVesselId = pickVesselId(member) ?? vId ?? vesselId
          if (!memberCrewId) return

          try {
            const resp = await getRollingMatrixSummaries(
              fromDate, toDate, memberCrewId, memberVesselId, regulation,
              undefined, undefined, controller.signal
            )
            if (resp?.activeSignOn && memberCrewId === (cId ?? crewId)) {
              setActiveSignOn(resp.activeSignOn)
            }
            if (resp?.daySummaries) allDaySummaries.push(...resp.daySummaries)
            if (resp?.violations) allViolations.push(...resp.violations)

            // rolling-range already calculates calendar-day work/rest totals from
            // persisted slots. Reposting those same slots to rolling-preview added
            // two more requests per crew without changing the overview result.
            const anchoredMap: AnchoredTotalsMap = {}
            ;(resp?.daySummaries ?? []).forEach((day: RollingDaySummary) => {
              if (day.date < fromDate || day.date > toDate) return
              anchoredMap[`${memberCrewId}-${day.date}`] = {
                work: Number(day.workMinutes ?? 0) / 60,
                rest: Number(day.restMinutes ?? 0) / 60,
                compliant: (day.violationCount ?? 0) === 0,
                violationCount: day.violationCount ?? 0,
                violations: [],
                violationCodes: day.violationRuleCodes ?? [],
                approved: false,
                summaryId: null,
                remarks: '',
              }
            })

            const summaries = resp?.summaries ?? []
            summaries.forEach((entry: any) => {
              const key = `${entry.crewId}-${entry.summaryDate}`
              if (!anchoredMap[key]) {
                anchoredMap[key] = {
                  work: Number(entry.totalWorkHours || 0),
                  rest: Number(entry.totalRestHours || 0),
                  compliant: !!entry.compliant,
                  violationCount: entry.violationCount ?? 0,
                  violations: [],
                  violationCodes: [],
                  approved: false,
                  summaryId: null,
                  remarks: '',
                }
              }
              anchoredMap[key].approved = !!entry.approved
              anchoredMap[key].summaryId = entry.id ?? anchoredMap[key].summaryId ?? null
              anchoredMap[key].remarks = entry.remarks ?? anchoredMap[key].remarks ?? ''
            })

            Object.assign(allAnchoredMap, anchoredMap)

            const reportingTz =
              resp?.activeSignOn?.timezone ??
              activeSignOn?.timezone ??
              (() => {
                try {
                  return Intl.DateTimeFormat().resolvedOptions().timeZone
                } catch {
                  return 'UTC'
                }
              })()

            const windows = resp?.violations ?? []
            windows.forEach((win) => {
              const crewKey = (win as any).crewId ?? memberCrewId
              let impacts: string[] = Array.isArray((win as any)?.impactedCrewDays)
                ? (win as any).impactedCrewDays
                : []

              if (!impacts.length && win.windowEnd) {
                impacts = [new Date(win.windowEnd).toISOString().slice(0, 10)]
              }
              const friendly = humanizeViolation(win, {
                reportingTz,
                reportingMode,
                customAnchorHHmm,
                signOnStartISO: resp?.activeSignOn?.onboardFrom ?? null,
              })

              impacts.forEach((d: string) => {
                const key = `${crewKey}-${d}`
                if (!allRollingMap[key]) allRollingMap[key] = { violations: [], violationCodes: [], windows: [] }
                allRollingMap[key].windows.push(win)
                allRollingMap[key].violations.push(friendly)
                allRollingMap[key].violationCodes.push(win.ruleCode)
              })
            })
          } catch (err) {
            if (!controller.signal.aborted) {
              console.error('Failed to fetch crew hours for crewId:', memberCrewId, err)
            }
          }
        }
      )

      if (controller.signal.aborted || seq !== fetchHoursSeqRef.current) return

      setRollingDaySummaries(allDaySummaries)
      setRollingViolations(allViolations)
      setRollingWindowsByDay(allRollingMap)

      // Override violation counts/compliance using rolling map
      Object.entries(allRollingMap).forEach(([key, val]) => {
        if (!allAnchoredMap[key]) return
        const count = val.violationCodes?.length ?? 0
        allAnchoredMap[key].violationCount = count
        allAnchoredMap[key].compliant = count === 0
      })

      // For days not present in rolling map, clear violations
      Object.keys(allAnchoredMap).forEach((key) => {
        if (!allRollingMap[key]) {
          allAnchoredMap[key].violationCount = 0
          allAnchoredMap[key].compliant = true
        }
      })

      setHoursMap(allAnchoredMap)
    } catch (err) {
      console.error('Failed to fetch crew hours:', err)
      showErrorToast(err, 'Failed to load crew rest hour data.', 'load')
    } finally {
      if (!controller.signal.aborted && seq === fetchHoursSeqRef.current) {
        setIsHoursLoading(false)
      }
    }
  }

  // const refreshRollingExceptions = React.useCallback(async () => {
  //   if (!rollingCrewId || !rollingVesselId) return
  //   try {
  //     const data = await getRollingExceptionsCompany(rollingCrewId, rollingVesselId)
  //     setRollingExceptionsData(data)
  //     setRollingAsOf(data?.asOf ?? null)
  //   } catch (err) {
  //     console.error('Rolling exceptions fetch failed:', err)
  //   }
  // }, [rollingCrewId, rollingVesselId])

  const handleAfterSave = React.useCallback(async () => {
    if (activeTab !== 'matrix-overview') return
    await fetchCrewHours()
  }, [activeTab, fetchCrewHours])

  useEffect(() => {
    console.log(hoursMap)
  }, [hoursMap])

  // useEffect(() => {
  //   if (activeTab !== 'matrix-overview') return
  //   if (!rollingCrewId || !rollingVesselId) return
  //   if (rollingRefreshRequestedRef.current) rollingRefreshRequestedRef.current = false
  //   refreshRollingExceptions()
  //   const intervalId = window.setInterval(refreshRollingExceptions, 60000)
  //   return () => window.clearInterval(intervalId)
  // }, [activeTab, rollingCrewId, rollingVesselId, refreshRollingExceptions])

  // REPLACE — build options from API lists; fallback to crew-derived if needed
  const companyGroupOptions = useMemo<CompanyGroupOpt[]>(() => {
    if (companies.length) return companies
    const map = new Map<number, CompanyGroupOpt>()
    crew.forEach((c) => {
      const cgId = pickCompanyGroupId(c)
      if (cgId) map.set(cgId, { id: cgId, name: `Company #${cgId}` })
    })
    return Array.from(map.values())
  }, [companies, crew])

  // Subcompanies strictly under the selected Company Group (fallback from crew if API empty)
  const subcompanyOptions = useMemo<SubcompanyOpt[]>(() => {
    if (subcompanies.length) {
      // already includes sc.companyGroupId from the API
      return typeof filterCompanyGroupId === 'number'
        ? subcompanies.filter((sc) => sc.companyGroupId === filterCompanyGroupId)
        : subcompanies
    }

    const map = new Map<number, SubcompanyOpt>()
    crew.forEach((c) => {
      const caId = pickCompanyAdminId(c)
      const cgId = pickCompanyGroupId(c)
      if (!caId) return
      map.set(caId, {
        id: caId,
        name: (c as any)?.companyAdminName ?? (c as any)?.companyName ?? `Subcompany #${caId}`,
        companyGroupId: cgId,
      })
    })
    const list = Array.from(map.values())
    return typeof filterCompanyGroupId === 'number'
      ? list.filter((sc) => sc.companyGroupId === filterCompanyGroupId)
      : list
  }, [subcompanies, crew, filterCompanyGroupId])

  // FIRST: whether the selected company group has any subcompanies
  const companyHasSubcompanies = useMemo(() => {
    if (typeof filterCompanyGroupId !== 'number') return false
    return subcompanyOptions.some((sc) => sc.companyGroupId === filterCompanyGroupId)
  }, [filterCompanyGroupId, subcompanyOptions])

  // THEN: vessel options that use the above flag
  const vesselOptions = useMemo<VesselOpt[]>(() => {
    const fVES = typeof filterVesselId === 'number' ? filterVesselId : undefined
    const fCG =
      typeof filterCompanyGroupId === 'number' && (roleId === 1 || roleId === 6)
        ? filterCompanyGroupId
        : undefined
    const fCA =
      typeof filterCompanyAdminId === 'number' && (roleId === 1 || roleId === 6)
        ? filterCompanyAdminId
        : undefined

    let apiFiltered = vesselList
    if (fCG || fCA) {
      apiFiltered = vesselList.filter((v: any) => {
        const cgId = toNum(
          v.companyGroupAdminId ?? v.companyGroupId ?? v.companyGroupAdmin?.id ?? v.cgaid ?? v.cga?.id
        )
        const caId = toNum(v.companyAdminId ?? v.companyId ?? v.companyAdmin?.id)
        const byCG = !fCG || cgId === fCG
        const byCA = !fCA || caId === fCA
        return byCG && (!companyHasSubcompanies || byCA)
      })
    }

    const mapped = apiFiltered.map((v) => ({ id: v.id, name: v.fleet_name }))
    if (fVES) {
      return mapped.filter((v) => v.id === fVES)
    }
    if (mapped.length) return mapped

    const set = new Map<number, VesselOpt>()
    crew.forEach((c) => {
      const vId = pickVesselId(c)
      if (vId && c.vessel?.fleet_name) {
        set.set(vId, { id: vId, name: c.vessel.fleet_name })
      }
    })
    return Array.from(set.values())
  }, [vesselList, crew, filterCompanyGroupId, filterCompanyAdminId, filterVesselId, companyHasSubcompanies, roleId])

  const handleApprove = async (summaryId: number, approve: boolean) => {
    if (!isSuperAdmin && !hasPermission('APPROVE_REST_HOURS') && !isRankMaster) {
      showErrorToast(null, 'You do not have permission to approve rest hours.', 'update')
      return
    }
    try {
      await approveRestDay(summaryId, approve)
      // refresh after approval
      await fetchCrewHours()
      notify?.success(approve ? 'Day approved.' : 'Approval removed.')
    } catch (e) {
      console.error('Approval failed', e)
      showErrorToast(e, 'Failed to update day approval.', 'update')
    }
  }

  const [isBulkApproving, setIsBulkApproving] = useState(false)

  const handleApproveVisible = async () => {
    if (!isSuperAdmin && !hasPermission('APPROVE_REST_HOURS') && !isRankMaster) {
      showErrorToast(null, 'You do not have permission to approve rest hours.', 'update')
      return
    }
    // 1. Collect all non-approved summaryIds in the current page grid view
    const ids: number[] = []
    for (const crew of pagedCrew) {
      let current = new Date(fromDate)
      const end = new Date(toDate)
      while (current <= end) {
        const dateKey = current.toLocaleDateString('en-CA')
        const key = `${crew.crewId}-${dateKey}`
        const entry = hoursMap[key]
        if (entry && !entry.approved && entry.summaryId != null) {
          ids.push(entry.summaryId)
        }
        current.setDate(current.getDate() + 1)
      }
    }

    if (ids.length === 0) {
      notify?.info('No pending rest hours to approve in the current view.')
      return
    }

    setIsBulkApproving(true)
    try {
      // 2. Call single-day approvals in parallel
      await Promise.all(ids.map((id) => approveRestDay(id, true)))
      notify?.success(`Successfully approved ${ids.length} day(s).`)
      await fetchCrewHours() // Refresh grid
    } catch (e) {
      console.error('Bulk approval failed', e)
      showErrorToast(e, 'Failed to approve visible rest hours.', 'update')
      await fetchCrewHours()
    } finally {
      setIsBulkApproving(false)
    }
  }

  // Update tab date window (default = last N days, but allow user to expand via calendar)
  const defaultUpdateRange = React.useMemo(
    () => getLastNDaysRange(UPDATE_EDITABLE_LOOKBACK_DAYS),
    []
  )
  const [updateFromISO, setUpdateFromISO] = useState<string>(defaultUpdateRange.fromISO)
  const [updateToISO, setUpdateToISO] = useState<string>(defaultUpdateRange.toISO)

  const handleUpdateFromChange = (value: string) => {
    if (!value) return
    setUpdateFromISO(value)
    setUpdateToISO((prev) => (!prev || value > prev ? value : prev))
  }

  const handleUpdateToChange = (value: string) => {
    if (!value) return
    setUpdateToISO(value)
    setUpdateFromISO((prev) => (!prev || prev > value ? value : prev))
  }

  const resetUpdateRange = () => {
    setUpdateFromISO(defaultUpdateRange.fromISO)
    setUpdateToISO(defaultUpdateRange.toISO)
  }

  // ADD — which crew id Update tab is operating on
  const updateCrewId = isRankMaster && selectedCrew?.id ? selectedCrew.id : crewId

  // Clamp Update tab range to active sign-on (same rule as UpdateTab component)
  const updateEffectiveFrom = useMemo(() => {
    if (!updateFromISO) return ''
    const f = onboardFromDate && updateFromISO < onboardFromDate ? onboardFromDate : updateFromISO
    if (f && onboardToDate && f > onboardToDate) return ''
    return f
  }, [updateFromISO, onboardFromDate, onboardToDate])

  const updateEffectiveTo = useMemo(() => {
    if (!updateToISO) return ''
    let t = updateToISO
    if (onboardToDate && t > onboardToDate) t = onboardToDate
    if (onboardFromDate && t < onboardFromDate) t = onboardFromDate
    return t
  }, [updateToISO, onboardFromDate, onboardToDate])

  // Approved dates in the Update tab window (derive only from live entry data)
  const approvedDatesForUpdateTab = useMemo(() => {
    const s = new Set<string>()
    apiEntries.forEach((e) => {
      if (e.entryDate && (e.isApproved || e.approved)) s.add(e.entryDate)
    })
    return s
  }, [
    apiEntries,
  ])


  useEffect(() => {
    if (activeTab !== 'update' || !updateCrewId) return
    const from = updateEffectiveFrom || updateFromISO
    const to = updateEffectiveTo || updateToISO
    if (!from || !to) return

    const targetVesselId =
      typeof filterVesselId === 'number'
        ? filterVesselId
        : (() => {
            const crewObj = crew.find(
              (c) => c.crewId === updateCrewId || (c as any).id === updateCrewId
            )
            return (crewObj ? pickVesselId(crewObj) : undefined) ?? vesselId
          })()
    if (!targetVesselId) return

    let cancelled = false
    getRollingMatrixSummaries(from, to, updateCrewId, targetVesselId, regulation)
      .then((resp) => {
        if (cancelled) return
        const summaries = resp?.summaries ?? []
        setHoursMap((prev) => {
          const next = { ...prev }
          ;(resp?.daySummaries ?? []).forEach((day: RollingDaySummary) => {
            if (day.date < from || day.date > to) return
            const key = `${updateCrewId}-${day.date}`
            if (!next[key]) {
              next[key] = {
                work: Number(day.workMinutes ?? 0) / 60,
                rest: Number(day.restMinutes ?? 0) / 60,
                compliant: (day.violationCount ?? 0) === 0,
                violationCount: day.violationCount ?? 0,
                violations: [],
                violationCodes: day.violationRuleCodes ?? [],
                approved: false,
                summaryId: null,
                remarks: '',
              }
            }
          })
          summaries.forEach((entry: any) => {
            const key = `${entry.crewId || updateCrewId}-${entry.summaryDate}`
            if (!next[key]) {
              next[key] = {
                work: Number(entry.totalWorkHours || 0),
                rest: Number(entry.totalRestHours || 0),
                compliant: !!entry.compliant,
                violationCount: entry.violationCount ?? 0,
                violations: [],
                violationCodes: [],
                approved: false,
                summaryId: null,
                remarks: '',
              }
            }
            next[key].approved = !!entry.approved
            next[key].summaryId = entry.id ?? next[key].summaryId ?? null
            next[key].remarks = entry.remarks ?? next[key].remarks ?? ''
          })
          return next
        })
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load update tab summaries for crewId:', updateCrewId, err)
        }
      })

    return () => {
      cancelled = true
    }
  }, [
    activeTab,
    updateCrewId,
    filterVesselId,
    crew,
    vesselId,
    updateEffectiveFrom,
    updateEffectiveTo,
    updateFromISO,
    updateToISO,
    regulation,
  ])

  useEffect(() => {
    // Prefill Update tab remarks from summaries for the visible Update window
    if (!updateCrewId) return
    const m = new Map<string, string>()
    const keys =
      updateEffectiveFrom && updateEffectiveTo
        ? buildDateKeys(updateEffectiveFrom, updateEffectiveTo)
        : buildDateKeys(updateFromISO, updateToISO)
    keys.forEach((d) => {
      const cell = hoursMap[`${updateCrewId}-${d}`]
      if (cell?.remarks) m.set(d, String(cell.remarks))
    })
    setRemarks(m)
  }, [hoursMap, updateCrewId, updateEffectiveFrom, updateEffectiveTo, updateFromISO, updateToISO])


  const loadExportHoursMap = async (members: Crew[], start: string, end: string) => {
    const result: AnchoredTotalsMap = {}
    await forEachWithConcurrency(members, 6, async (member) => {
      try {
        const memberVesselId = pickVesselId(member) ?? vesselId
        if (!memberVesselId) return
        const response = await getRollingMatrixSummaries(
          start, end, member.crewId, memberVesselId, regulation
        )
        ;(response.daySummaries ?? []).forEach((day) => {
          if (day.date < start || day.date > end) return
          result[`${member.crewId}-${day.date}`] = {
            work: Number(day.workMinutes ?? 0) / 60,
            rest: Number(day.restMinutes ?? 0) / 60,
            compliant: (day.violationCount ?? 0) === 0,
            violationCount: day.violationCount ?? 0,
            violations: [],
            violationCodes: day.violationRuleCodes ?? [],
            approved: false,
            summaryId: null,
            remarks: '',
          }
        })
        ;(response.summaries ?? []).forEach((summary) => {
          const key = `${member.crewId}-${summary.summaryDate}`
          const day = result[key]
          if (day) {
            day.approved = !!summary.approved
            day.summaryId = summary.id ?? null
            day.remarks = summary.remarks ?? ''
          }
        })
      } catch (error) {
        console.warn(`Failed to load export totals for crew ${member.crewId}`, error)
      }
    })
    return result
  }

  const exportModelFormatToExcel = async () => {
    if (!isSuperAdmin && !hasPermission('EXPORT_REST_HOURS')) {
      showErrorToast(null, 'You do not have permission to export rest hours.', 'general');
      return;
    }
    if (!ensureVesselSelected()) return;
    if (!validateExportRange(excelFrom, excelTo, 'Excel')) return;

    try {
      const dateKeysAll = buildDateKeys(excelFrom, excelTo);
      const targetCrewList = selectedCrew?.id
        ? crew.filter((c) => c.crewId === selectedCrew.id)
        : sortedCrew;

      if (!targetCrewList.length) {
        notify?.warn('No crew members available for export.');
        return;
      }

      const exportHoursMap = await loadExportHoursMap(targetCrewList, excelFrom, excelTo)

      const firstCrew = targetCrewList[0];
      const targetVesselId = (firstCrew?.vessel as any)?.id || (firstCrew as any)?.vesselId || (currentUser?.vessel as any)?.id || (currentUser as any)?.vesselId || filterVesselId;
      const matchedVessel = vesselList.find((v: any) => String(v.id) === String(targetVesselId)) as any;
      const vesselObj = (matchedVessel ?? firstCrew?.vessel ?? currentUser?.vessel ?? {}) as any;

      const shipName = vesselObj?.fleet_name || vesselObj?.name || vesselObj?.vesselName || (currentUser?.vessel as any)?.name || (currentUser?.vessel as any)?.fleet_name || '—';
      const imoNo = vesselObj?.imoNumber || vesselObj?.imo_number || vesselObj?.imo || vesselObj?.imoNo || vesselObj?.vesselImo || (matchedVessel as any)?.imoNumber || (matchedVessel as any)?.imo || (currentUser?.vessel as any)?.imoNumber || (currentUser?.vessel as any)?.imo || '—';
      const flagState = vesselObj?.vesselFlag || vesselObj?.vessel_flag || vesselObj?.flag || vesselObj?.flagState || vesselObj?.flag_state || vesselObj?.flagName || vesselObj?.flag_name || (matchedVessel as any)?.vesselFlag || (matchedVessel as any)?.flag || (matchedVessel as any)?.flagState || (currentUser?.vessel as any)?.vesselFlag || (currentUser?.vessel as any)?.flag || '—';
      const masterName = (currentUser as any)?.fullName || (currentUser as any)?.name || '';

      // Fetch live slot entries directly from backend for all target crew across export range
      const exportEntriesMap: Record<string, boolean> = {};
      await Promise.all(
        targetCrewList.map(async (c: any) => {
          const cId = c.crewId;
          const vId = (c?.vessel as any)?.id || (c as any)?.vesselId || targetVesselId || filterVesselId || 0;
          try {
            const entries = await getRestHourEntriesInRange(cId, vId, excelFrom, excelTo);
            if (Array.isArray(entries)) {
              entries.forEach((e: any) => {
                if (e && e.entryDate != null && e.slotIndex != null) {
                  const isWork = Boolean(
                    e.isWorking ??
                    e.working ??
                    e.isWork ??
                    (e.workType === 'WORK' || e.workType === 'NORMAL') ??
                    (e.work > 0)
                  );
                  const k = `${cId}-${e.entryDate}-${Number(e.slotIndex)}`;
                  exportEntriesMap[k] = isWork;
                }
              });
            }
          } catch (err) {
            console.warn(`Failed to fetch slot entries for crew ${cId}`, err);
          }
        })
      );

      const monthYearLabel = (() => {
        try {
          const d = new Date(excelFrom + 'T00:00:00Z');
          return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
        } catch {
          return excelFrom;
        }
      })();

      const wb = XLSX.utils.book_new();
      const sheetRows: any[] = [];

      // 1. TOP HEADER BOX (ONCE AT TOP)
      sheetRows.push(['Model format for record of hours of work or hours of rest of seafarers¹']);
      sheetRows.push([]);
      sheetRows.push(['SHIP & SEAFARER INFORMATION']);
      sheetRows.push(['Name of ship:', shipName, '', '', '', '', '', '', '', '', '', '', 'IMO number (if any):', imoNo]);
      sheetRows.push(['Flag of ship:', flagState, '', '', '', '', '', '', '', '', '', '', 'Regulation:', regulation.toUpperCase()]);

      const isSingle = Boolean(selectedCrew?.id) || targetCrewList.length === 1;

      if (isSingle) {
        const seafarerName = crewDisplayName(firstCrew);
        const posRank = getRankLabel(firstCrew);
        const isWatch = (firstCrew as any)?.isWatchkeeper || (firstCrew as any)?.watchkeeper ? 'yes [✓]   no [  ]' : 'yes [  ]   no [✓]';
        sheetRows.push(['Seafarer (full name):', seafarerName, '', '', '', '', '', '', '', '', '', '', 'Position / rank:', posRank]);
        sheetRows.push(['Watchkeeper:²', isWatch, '', '', '', '', '', '', '', '', '', '', 'Period Covered:', `${excelFrom} to ${excelTo}`]);
      } else {
        sheetRows.push(['Seafarers:', `All Crew Members (${targetCrewList.length} total)`, '', '', '', '', '', '', '', '', '', '', 'Period Covered:', `${excelFrom} to ${excelTo}`]);
      }
      sheetRows.push(['Month and year:', monthYearLabel]);
      sheetRows.push([]);

      // 2. MIDDLE MATRIX OVERVIEW GRID (24 HOURS SPANNING 48 HALF-HOUR SLOTS)
      const hourSubHeaders = Array.from({ length: 48 }, (_, i) =>
        i % 2 === 0 ? String(Math.floor(i / 2)).padStart(2, '0') : ''
      );
      const matrixHeader = isSingle
        ? ['Date', ...hourSubHeaders, 'Rest Hours (24h)', 'Comments', '24h Period Compliance', '7d Period Compliance']
        : ['Crew Name', 'Rank', 'Date', ...hourSubHeaders, 'Rest Hours (24h)', 'Comments', '24h Period Compliance', '7d Period Compliance'];
      sheetRows.push(matrixHeader);

      targetCrewList.forEach((c) => {
        const cName = crewDisplayName(c);
        const cRank = getRankLabel(c);

        dateKeysAll.forEach((dk) => {
          const cell = exportHoursMap[`${c.crewId}-${dk}`];
          const restHours = cell ? Number(cell.rest || 0).toFixed(1) : '-';
          const remarks = cell?.remarks ? String(cell.remarks) : '';
          const comp24 = cell ? (cell.compliant ? 'Compliant' : 'Violation') : 'Compliant';
          const comp7d = cell?.violations?.length ? `${cell.violationCount} Violations` : 'Compliant';

          const hourSlots = Array.from({ length: 48 }, (_, s) => {
            const key = `${c.crewId}-${dk}-${s}`;
            if (key in exportEntriesMap) {
              return exportEntriesMap[key] ? 'W' : 'R';
            }
            const match = apiEntries.find(
              (e: any) =>
                e.entryDate === dk &&
                Number(e.slotIndex) === s &&
                (e.crewId == null || String(e.crewId) === String(c.crewId))
            );
            if (match) {
              const isWork = Boolean(
                match.isWorking ??
                match.working ??
                match.isWork ??
                (match.workType === 'WORK' || match.workType === 'NORMAL') ??
                (match.work > 0)
              );
              return isWork ? 'W' : 'R';
            }
            return 'R';
          });

          if (isSingle) {
            sheetRows.push([
              dk,
              ...hourSlots,
              `${restHours}h`,
              remarks,
              comp24,
              comp7d,
            ]);
          } else {
            sheetRows.push([
              cName,
              cRank,
              dk,
              ...hourSlots,
              `${restHours}h`,
              remarks,
              comp24,
              comp7d,
            ]);
          }
        });
      });

      // 3. BOTTOM SIGNATURES BOX (ONLY FOR SINGLE CREW EXPORT)
      if (isSingle) {
        sheetRows.push([]);
        sheetRows.push(['SIGNATURES']);
        sheetRows.push(['Signature of Master / Authorized:', '__________________________________________________']);
        sheetRows.push(['Signature of Seafarer:', '__________________________________________________']);
        sheetRows.push([]);
        sheetRows.push(['¹ For completion and use in accordance with procedures established by competent authority under Seafarers Hours of Work and Manning of Ships Convention, 1996 (No. 180).']);
      }

      const ws = XLSX.utils.aoa_to_sheet(sheetRows);

      // Merge pairs of header columns for 00..23 hours (Row 7 = 0-indexed row 6 or 7 depending on header rows)
      const headerRowIndex = isSingle ? 7 : 7;
      const startHourCol = isSingle ? 1 : 3;
      const merges: any[] = ws['!merges'] || [];
      for (let h = 0; h < 24; h++) {
        merges.push({
          s: { r: headerRowIndex, c: startHourCol + h * 2 },
          e: { r: headerRowIndex, c: startHourCol + h * 2 + 1 },
        });
      }
      ws['!merges'] = merges;

      if (isSingle) {
        ws['!cols'] = [
          { wch: 14 }, // Date
          ...Array.from({ length: 48 }, () => ({ wch: 3.5 })), // 48 half-hour slot columns
          { wch: 18 }, // Rest Hours (24h)
          { wch: 30 }, // Comments
          { wch: 22 }, // 24h Compliance
          { wch: 25 }, // 7d Compliance
        ];
      } else {
        ws['!cols'] = [
          { wch: 22 }, // Crew Name
          { wch: 18 }, // Rank
          { wch: 14 }, // Date
          ...Array.from({ length: 48 }, () => ({ wch: 3.5 })), // 48 half-hour slot columns
          { wch: 18 }, // Rest Hours (24h)
          { wch: 30 }, // Comments
          { wch: 22 }, // 24h Compliance
          { wch: 25 }, // 7d Compliance
        ];
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Model Format Record');

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const exportFileName = isSingle
        ? `RestHours_ModelFormat_${crewDisplayName(targetCrewList[0]).replace(/\s+/g, '_')}_${excelFrom}_${excelTo}.xlsx`
        : `RestHours_ModelFormat_AllCrew_${excelFrom}_${excelTo}.xlsx`;

      saveAs(new Blob([wbout], { type: 'application/octet-stream' }), exportFileName);
      notify?.success('Excel exported in Model Format.');
    } catch (err) {
      console.error('Failed to export Excel:', err);
      showErrorToast(err, 'Failed to export Excel report.', 'general');
    }
  };

  const exportWeekToPDF = async (fromISO: string, toISO: string) => {
    if (!isSuperAdmin && !hasPermission('EXPORT_REST_HOURS')) {
      showErrorToast(null, 'You do not have permission to export rest hours.', 'general');
      return;
    }
    if (!ensureVesselSelected()) return;
    if (!validateExportRange(fromISO, toISO, 'PDF')) return;

    try {
      const dateKeysRange = buildDateKeys(fromISO, toISO);
      const targetCrewList = selectedCrew?.id
        ? crew.filter((c) => c.crewId === selectedCrew.id)
        : sortedCrew;

      if (!targetCrewList.length) {
        notify?.warn('No crew members available for PDF export.');
        return;
      }

      const exportHoursMap = await loadExportHoursMap(targetCrewList, fromISO, toISO)

      const firstCrew = targetCrewList[0];
      let allVessels = vesselList;
      if (!allVessels || !allVessels.length) {
        try {
          allVessels = await getVesselList();
        } catch {
          allVessels = [];
        }
      }
      const targetVesselId = (firstCrew?.vessel as any)?.id || (firstCrew as any)?.vesselId || (currentUser?.vessel as any)?.id || (currentUser as any)?.vesselId || filterVesselId;
      const matchedVessel = allVessels.find((v: any) => String(v.id) === String(targetVesselId)) as any;
      const vesselObj = (matchedVessel ?? firstCrew?.vessel ?? currentUser?.vessel ?? {}) as any;

      const shipName = vesselObj?.fleet_name || vesselObj?.name || vesselObj?.vesselName || (currentUser?.vessel as any)?.name || (currentUser?.vessel as any)?.fleet_name || '—';
      const imoNo = vesselObj?.imoNumber || vesselObj?.imo_number || vesselObj?.imo || vesselObj?.imoNo || vesselObj?.vesselImo || (matchedVessel as any)?.imoNumber || (matchedVessel as any)?.imo || (currentUser?.vessel as any)?.imoNumber || (currentUser?.vessel as any)?.imo || '—';
      const flagState = vesselObj?.vesselFlag || vesselObj?.vessel_flag || vesselObj?.flag || vesselObj?.flagState || vesselObj?.flag_state || vesselObj?.flagName || vesselObj?.flag_name || (matchedVessel as any)?.vesselFlag || (matchedVessel as any)?.flag || (matchedVessel as any)?.flagState || (currentUser?.vessel as any)?.vesselFlag || (currentUser?.vessel as any)?.flag || '—';
      const masterName = (currentUser as any)?.fullName || (currentUser as any)?.name || '';

      // Resolve company watermark URL from currentUser or vessel object
      const rawWatermark =
        (currentUser as any)?.companyWatermarkUrl ||
        (currentUser as any)?.watermarkUrl ||
        (currentUser as any)?.company?.watermarkUrl ||
        (currentUser as any)?.company?.companyWatermarkUrl ||
        (firstCrew as any)?.watermarkUrl ||
        (firstCrew as any)?.companyWatermarkUrl ||
        (firstCrew as any)?.company?.watermarkUrl ||
        (vesselObj as any)?.watermarkUrl ||
        (vesselObj as any)?.companyWatermarkUrl ||
        (vesselObj as any)?.company?.watermarkUrl;

      const watermarkUrl = (() => {
        const raw = String(rawWatermark || '').trim();
        if (!raw || /^javascript:/i.test(raw)) return null;
        if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
        const API_URL = process.env.REACT_APP_API_URL || '';
        const normalized = raw.startsWith('/') ? raw : raw.startsWith('uploads/') ? `/${raw}` : raw;
        if (!normalized.startsWith('/')) return normalized;
        if (!API_URL) return normalized;
        try {
          return new URL(normalized, API_URL).toString();
        } catch {
          return normalized;
        }
      })();

      // Fetch live slot entries directly from backend for all target crew across export range
      const exportEntriesMap: Record<string, boolean> = {};
      await Promise.all(
        targetCrewList.map(async (c: any) => {
          const cId = c.crewId;
          const vId = (c?.vessel as any)?.id || (c as any)?.vesselId || targetVesselId || filterVesselId || 0;
          try {
            const entries = await getRestHourEntriesInRange(cId, vId, fromISO, toISO);
            if (Array.isArray(entries)) {
              entries.forEach((e: any) => {
                if (e && e.entryDate != null && e.slotIndex != null) {
                  const isWork = Boolean(
                    e.isWorking ??
                    e.working ??
                    e.isWork ??
                    (e.workType === 'WORK' || e.workType === 'NORMAL') ??
                    (e.work > 0)
                  );
                  const k = `${cId}-${e.entryDate}-${Number(e.slotIndex)}`;
                  exportEntriesMap[k] = isWork;
                }
              });
            }
          } catch (err) {
            console.warn(`Failed to fetch slot entries for crew ${cId}`, err);
          }
        })
      );

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

      // MAIN TITLE
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Model format for record of hours of work or hours of rest of seafarers¹', 14, 16);

      const isSingle = Boolean(selectedCrew?.id) || targetCrewList.length === 1;

      // 1. TOP METADATA TABLE (ONCE AT TOP)
      const metadataBody = isSingle
        ? [
          [`Name of ship:  ${shipName}`, `IMO number (if any):  ${imoNo}`],
          [`Flag of ship:  ${flagState}`, `Regulation:  ${regulation.toUpperCase()}`],
          [`Seafarer (full name):  ${crewDisplayName(firstCrew)}`, `Position / rank:  ${getRankLabel(firstCrew)}`],
          [`Watchkeeper:²  ${(firstCrew as any)?.isWatchkeeper || (firstCrew as any)?.watchkeeper ? 'yes [✓]   no [  ]' : 'yes [  ]   no [✓]'}`, `Period Covered:  ${fromISO} to ${toISO}`],
        ]
        : [
          [`Name of ship:  ${shipName}`, `IMO number (if any):  ${imoNo}`],
          [`Flag of ship:  ${flagState}`, `Regulation:  ${regulation.toUpperCase()}`],
          [`Seafarers:  All Crew Members (${targetCrewList.length} total)`, `Period Covered:  ${fromISO} to ${toISO}`],
        ];

      (doc as any).autoTable({
        startY: 20,
        theme: 'plain',
        tableLineWidth: 0.2,
        tableLineColor: [0, 0, 0],
        styles: { fontSize: 8.5, cellPadding: 2, textColor: [0, 0, 0] },
        body: metadataBody,
      });

      let currentY = (doc as any).lastAutoTable.finalY + 3;

      // 2. MIDDLE MATRIX OVERVIEW TABLE (24 HOURS SPANNING 48 HALF-HOUR SLOTS)
      const hourHeaders24 = Array.from({ length: 24 }, (_, i) => ({
        content: String(i).padStart(2, '0'),
        colSpan: 2,
        styles: { halign: 'center', valign: 'middle' },
      }));

      const tableHead = isSingle
        ? [
          [
            { content: 'Date', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'Hours', colSpan: 48, styles: { halign: 'center', valign: 'middle' } },
            { content: 'Rest (24h)', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'Comments', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'NOT TO BE COMPLETED BY SEAFARER', colSpan: 2, styles: { halign: 'center', valign: 'middle' } },
          ],
          [
            ...hourHeaders24,
            { content: '24h Period', styles: { halign: 'center', valign: 'middle' } },
            { content: '7d Period', styles: { halign: 'center', valign: 'middle' } },
          ],
        ]
        : [
          [
            { content: 'Crew Name', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'Rank', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'Date', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'Hours', colSpan: 48, styles: { halign: 'center', valign: 'middle' } },
            { content: 'Rest (24h)', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'Comments', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'NOT TO BE COMPLETED BY SEAFARER', colSpan: 2, styles: { halign: 'center', valign: 'middle' } },
          ],
          [
            ...hourHeaders24,
            { content: '24h Period', styles: { halign: 'center', valign: 'middle' } },
            { content: '7d Period', styles: { halign: 'center', valign: 'middle' } },
          ],
        ];

      const tableBody: any[] = [];
      targetCrewList.forEach((c) => {
        const cName = crewDisplayName(c);
        const cRank = getRankLabel(c);

        dateKeysRange.forEach((dk) => {
          const cell = exportHoursMap[`${c.crewId}-${dk}`];
          const restHours = cell ? Number(cell.rest || 0).toFixed(1) : '-';
          const remarks = cell?.remarks ? String(cell.remarks) : '';
          const isComp = cell ? cell.compliant : true;
          const comp24 = isComp ? 'Compliant' : 'Violation';
          const comp7d = cell?.violations?.length ? `${cell.violationCount} Violations` : 'Compliant';

          const hourCells = Array.from({ length: 48 }, (_, s) => {
            const key = `${c.crewId}-${dk}-${s}`;
            if (key in exportEntriesMap) {
              return exportEntriesMap[key] ? 'W' : 'R';
            }
            const match = apiEntries.find(
              (e: any) =>
                e.entryDate === dk &&
                Number(e.slotIndex) === s &&
                (e.crewId == null || String(e.crewId) === String(c.crewId))
            );
            if (match) {
              const isWork = Boolean(
                match.isWorking ??
                match.working ??
                match.isWork ??
                (match.workType === 'WORK' || match.workType === 'NORMAL') ??
                (match.work > 0)
              );
              return isWork ? 'W' : 'R';
            }
            return 'R';
          });

          if (isSingle) {
            tableBody.push([
              dk,
              ...hourCells,
              `${restHours}h`,
              remarks,
              comp24,
              comp7d,
            ]);
          } else {
            tableBody.push([
              cName,
              cRank,
              dk,
              ...hourCells,
              `${restHours}h`,
              remarks,
              comp24,
              comp7d,
            ]);
          }
        });
      });

      (doc as any).autoTable({
        head: tableHead,
        body: tableBody,
        startY: currentY,
        theme: 'grid',
        styles: {
          fontSize: 5.2,
          cellPadding: 0.5,
          halign: 'center',
          valign: 'middle',
          textColor: [0, 0, 0],
          lineWidth: 0.1,
          lineColor: [100, 100, 100],
        },
        headStyles: {
          fillColor: [240, 243, 246],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
        },
        columnStyles: isSingle
          ? {
            0: { cellWidth: 16 },
            ...Object.fromEntries(Array.from({ length: 48 }, (_, i) => [i + 1, { cellWidth: 3.5 }])),
            49: { cellWidth: 14 },
            50: { cellWidth: 22 },
            51: { cellWidth: 18 },
            52: { cellWidth: 18 },
          }
          : {
            0: { cellWidth: 22 },
            1: { cellWidth: 18 },
            2: { cellWidth: 14 },
            ...Object.fromEntries(Array.from({ length: 48 }, (_, i) => [i + 3, { cellWidth: 3.0 }])),
            51: { cellWidth: 13 },
            52: { cellWidth: 18 },
            53: { cellWidth: 16 },
            54: { cellWidth: 16 },
          },
        didParseCell: (data: any) => {
          if (data.section === 'body') {
            const hourStartIdx = isSingle ? 1 : 3;
            const hourEndIdx = isSingle ? 48 : 50;
            const comp24Idx = isSingle ? 51 : 53;
            const comp7dIdx = isSingle ? 52 : 54;

            if (data.column.index >= hourStartIdx && data.column.index <= hourEndIdx) {
              if (data.cell.raw === 'W' || data.cell.raw === 'X') {
                data.cell.styles.fillColor = [255, 235, 204];
                data.cell.styles.textColor = [180, 80, 0];
              } else {
                data.cell.styles.fillColor = [240, 248, 255];
                data.cell.styles.textColor = [0, 80, 160];
              }
            } else if (data.column.index === comp24Idx || data.column.index === comp7dIdx) {
              if (String(data.cell.raw).includes('Violation')) {
                data.cell.styles.fillColor = [255, 225, 230];
                data.cell.styles.textColor = [180, 0, 0];
              }
            }
          }
        },
      });

      currentY = (doc as any).lastAutoTable.finalY + 4;

      // 3. BOTTOM SIGNATURES BOX & FOOTNOTE (ONLY FOR SINGLE CREW EXPORT)
      if (isSingle) {
        (doc as any).autoTable({
          startY: currentY,
          theme: 'plain',
          tableLineWidth: 0.2,
          tableLineColor: [0, 0, 0],
          styles: { fontSize: 8.5, cellPadding: 2.5, textColor: [0, 0, 0] },
          body: [
            [
              `Signature of master or authorized person: ___________________________      Signature of seafarer: ___________________________`,
            ],
          ],
        });

        const footnoteY = (doc as any).lastAutoTable.finalY + 3.5;
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(50, 50, 50);
        doc.text(
          '¹ For completion and use in accordance with procedures established by competent authority under Seafarers Hours of Work and Manning of Ships Convention, 1996 (No. 180).',
          14,
          footnoteY
        );
      }

      // 4. APPLY BACKGROUND WATERMARK TO ALL PAGES IF WATERMARK URL IS PRESENT
      if (watermarkUrl) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = watermarkUrl;
          await new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });

          if (img.complete && img.naturalWidth) {
            const totalPages = (doc as any).internal.getNumberOfPages();
            for (let p = 1; p <= totalPages; p++) {
              doc.setPage(p);
              const pw = doc.internal.pageSize.getWidth();
              const ph = doc.internal.pageSize.getHeight();
              const w = 110;
              const h = 110;
              const x = (pw - w) / 2;
              const y = (ph - h) / 2;

              if ((doc as any).setGState && (doc as any).GState) {
                (doc as any).setGState(new (doc as any).GState({ opacity: 0.08 }));
              }
              doc.addImage(img, 'PNG', x, y, w, h, undefined, 'FAST');
              if ((doc as any).setGState && (doc as any).GState) {
                (doc as any).setGState(new (doc as any).GState({ opacity: 1.0 }));
              }
            }
          }
        } catch {
          // Skip watermark if loading fails
        }
      }

      const exportFileName = isSingle
        ? `RestHours_ModelFormat_${crewDisplayName(targetCrewList[0]).replace(/\s+/g, '_')}_${fromISO}_${toISO}.pdf`
        : `RestHours_ModelFormat_AllCrew_${fromISO}_${toISO}.pdf`;

      doc.save(exportFileName);
      notify?.success('PDF exported in Model Format.');
    } catch (err) {
      console.error('Failed to export PDF:', err);
      showErrorToast(err, 'Failed to export PDF report.', 'general');
    }
  };

  // ?? add near other helpers in RestHour
  const updateDateKeys = useMemo(
    () => buildDateKeys(updateFromISO, updateToISO),
    [updateFromISO, updateToISO]
  )

  const exportAnchorSource = useMemo(() => 'Rolling compliance', [])
  const exportAnchorLabel = useMemo(() => 'Rolling windows', [])

  const dirtyRemarkDates = useMemo(() => {
    const s = new Set<string>()
    if (!updateCrewId) return s
    updateDateKeys.forEach((d) => {
      const baseline = (hoursMap[`${updateCrewId}-${d}`]?.remarks ?? '').trim()
      const cur = (remarks.get(d) ?? '').trim()
      if (baseline !== cur) s.add(d)
    })
    return s
  }, [hoursMap, updateCrewId, updateDateKeys, remarks])

  const resetRemarkEdits = () => {
    const next = new Map<string, string>()
    updateDateKeys.forEach((d) => {
      const val = hoursMap[`${updateCrewId}-${d}`]?.remarks ?? ''
      next.set(d, String(val))
    })
    setRemarks(next)
    notify?.info?.('Unsaved remark edits cleared.')
  }


  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            {/* Header */}
            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <h3 className='card-label text-dark fw-bold'>Rest Hour Records</h3>
              <div className='card-toolbar d-flex align-items-center gap-3'>
                {/* Regulation Switch */}
                <div className='d-flex align-items-center gap-2'>
                  <span className='text-muted fw-semibold' style={{ fontSize: 12 }}>Regulation</span>
                  <div className='btn-group btn-group-sm' role='group' aria-label='Regulation switch'>
                    <button
                      type='button'
                      className={`btn btn-sm ${regulation === 'stcw' ? 'btn_primary' : 'btn-light border border-secondary'}`}
                      onClick={() => setRegulation('stcw')}
                      title='Standards of Training, Certification and Watchkeeping'
                    >
                      STCW
                    </button>
                    <button
                      type='button'
                      className={`btn btn-sm ${regulation === 'mlc' ? 'btn_primary' : 'btn-light border border-secondary'}`}
                      onClick={() => setRegulation('mlc')}
                      title='Maritime Labour Convention'
                    >
                      MLC
                    </button>
                  </div>
                </div>
                {/* // Removed: Exceptions toggle (always on) */}
                {/* Exceptions switch — ONLY show on Update tab (no backend support in Matrix) */}
                {/* {activeTab === 'update' && (
  <div className='d-flex align-items-center gap-2'>
    <span className='text-muted fw-semibold' style={{fontSize: 12}}>Exceptions</span>
    <div className='btn-group btn-group-sm' role='group' aria-label='Exceptions switch'>
      <button
        type='button'
        className={`btn btn-sm ${!allowExceptions ? 'btn_primary' : 'btn-light'}`}
        onClick={() => setAllowExceptions(false)}
        title='Apply standard limits'
      >
        Off
      </button>
      <button
        type='button'
        className={`btn btn-sm ${allowExceptions ? 'btn_primary' : 'btn-light'}`}
        onClick={() => setAllowExceptions(true)}
        title='Apply allowed flexibility/collective agreement rules'
      >
        On
      </button>
    </div>
  </div>
)} */}



                {/* Export button */}
                {(isSuperAdmin || hasPermission('EXPORT_REST_HOURS')) && (
                  <button
                    type='button'
                    className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                    style={{
                      backgroundColor: 'rgb(220, 38, 38)',
                      borderColor: 'rgb(220, 38, 38)',
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                      transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                      gap: '6.5px',
                    }}
                    onClick={() => setIsExportOpen(true)}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      style={{ width: '13px', height: '13px' }}
                    >
                      <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'></path>
                      <polyline points='7 10 12 15 17 10'></polyline>
                      <line x1='12' y1='15' x2='12' y2='3'></line>
                    </svg>
                    Export
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className='card-body py-4 bg-white border-top'>
              <div>
                <div className='d-flex flex-wrap align-items-end gap-3'>
                  {/* <div className='row g-3 align-items-end mb-3'> */}
                  {/* ROW 1: Superior filters at the TOP */}
                  {(() => {
                    const rId = roleId
                    const isCrew = rId === 4
                    const masterByName = (currentUser?.rank?.rank ?? '')
                      .toLowerCase()
                      .includes('master')

                    // Crew (master or not): NO filters at all
                    if (isCrew) return null

                    return (
                      <>
                        {/* Global Filter attaches automatically */}
                      </>
                    )
                  })()}
                  {/* Rank — hidden for non-master crew */}
                  {!isCrewNonMaster && (
                    <div style={{ minWidth: 220 }}>
                      <label className='form-label text-muted fw-semibold fs-7 mb-2'>Rank</label>
                      <SearchableSelect
                        className='form-select form-select-sm'
                        value={selectedRank}
                        onChange={(e) => setSelectedRank(e.target.value)}
                      >
                        <option value=''>All Ranks</option>
                        {filteredRankOptions.map((r) => (
                          <option key={r.id} value={r.label}>
                            {r.label}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                  )}
                  {/* Name — hidden for non-master crew */}
                  {!isCrewNonMaster && (
                    <div style={{ minWidth: 220 }}>
                      <label className='form-label text-muted fw-semibold fs-7 mb-2'>Name</label>
                      <SearchableSelect
                        className='form-select form-select-sm'
                        value={selectedCrew?.id ?? ''} // use id for controlled value
                        onChange={(e) => {
                          const val = e.target.value
                          const crewMember = crew.find((c) => c.crewId === Number(val))
                          if (crewMember) {
                            setSelectedCrew({ id: crewMember.crewId, name: crewMember.crewName })
                          } else {
                            setSelectedCrew(null)
                          }
                          setUserSelectedCrew(true)
                        }}
                      >
                        {/* Only show "All" when NOT in Update tab */}
                        {activeTab !== 'update' && <option value=''>All Crew Members</option>}

                        {crewOptions.map((member) => (
                          <option key={member.crewId} value={member.crewId}>
                            {member.crewName}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                  )}

                  {/* From Date Filter */}
                  {activeTab !== 'update' && (
                    <div style={{ minWidth: 220 }}>
                      <label className='form-label text-muted fw-semibold fs-7 mb-2'>From Date</label>
                      {USE_CUSTOM_DATE_PICKER ? (
                        <DatePickerWithIndicators
                          className='form-control form-control-sm'
                          value={fromDate}
                          min={undefined}
                          max={undefined}
                          onChange={(val) => {
                            setFromDate(val)
                          }}
                          entryDates={matrixPickerEntryDates}
                          forceNative={true}
                        />
                      ) : (
                        <input
                          type='date'
                          className='form-control form-control-sm'
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      )}
                    </div>
                  )}

                  {/* To Date Filter */}
                  {activeTab !== 'update' && (
                    <div style={{ minWidth: 220 }}>
                      <label className='form-label text-muted fw-semibold fs-7 mb-2'>To Date</label>
                      {USE_CUSTOM_DATE_PICKER ? (
                        <DatePickerWithIndicators
                          className='form-control form-control-sm'
                          value={toDate}
                          min={undefined}
                          max={undefined}
                          onChange={(val) => {
                            setToDate(val)
                          }}
                          entryDates={matrixPickerEntryDates}
                          forceNative={true}
                        />
                      ) : (
                        <input
                          type='date'
                          className='form-control form-control-sm'
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      )}
                    </div>
                  )}


                  {/* Show Button - Updated with the new onClick handler */}
                  {/* <div className='col-md-auto'>
                    <button
                      type='button'
                      className='btn btn-dark btn-sm'
                      // 3. Use the new handler function
                      onClick={handleShowClick}
                    >
                      Show
                    </button>
                  </div> */}
                </div>
              </div>
              {/* 4. Display the error message if it exists */}
              {error && (

                <div className='alert alert-danger p-2' role='alert'>
                  {error}
                </div>
              )}


              {/* Tabs */}
              <div className='d-flex justify-content-between align-items-center mb-4 mt-4'>
                <div className='btn-group' style={{ marginBottom: '10px' }}>
                  <button
                    className={`btn btn-sm ${activeTab === 'matrix-overview' ? 'btn_primary' : 'btn-light'
                      }`}
                    onClick={() => {
                      setActiveTab('matrix-overview')
                      if (!isCrewNonMaster) {
                        setSelectedCrew(null)
                        setUserSelectedCrew(false)
                      }
                    }}
                  >
                    Matrix Overview
                  </button>
                  {/* Update tab needed for master and other crews also */}
                  {roleId === 4 && (isSuperAdmin || hasPermission('UPDATE_REST_HOURS')) && (
                    <button
                      className={`btn btn-sm ${activeTab === 'update' ? 'btn_primary' : 'btn-light'
                        }`}
                      onClick={() => setActiveTab('update')}
                    >
                      Update
                    </button>
                  )}
                </div>
                {activeTab !== 'update' && (
                  <div className='d-flex align-items-center justify-content-end gap-3 mb-3'>
                    <div className='d-flex'>
                      <button
                        className={`btn btn-sm ${showWorkHours ? 'btn_primary' : ' border btn-outline-primary'
                          } me-2`}
                        onClick={() => setShowWorkHours(true)}
                      >
                        Work Hours
                      </button>
                      <button
                        className={`btn btn-sm ${!showWorkHours ? 'btn_primary' : ' border btn-outline-primary'
                          }`}
                        onClick={() => setShowWorkHours(false)}

                      >
                        Rest Hours
                      </button>
                    </div>
                    {/* Approve Visible Rest Hours Button */}
                    {(roleId === 1 || isRankMaster) && (
                      <button
                        type='button'
                        className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2 ms-1'
                        style={{
                          backgroundColor: 'rgb(217, 119, 6)',
                          borderColor: 'rgb(217, 119, 6)',
                          borderRadius: '9.75px',
                          padding: '8px 13px',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                          transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                          gap: '6.5px',
                        }}
                        onClick={handleApproveVisible}
                        disabled={isBulkApproving}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='14'
                          height='14'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          style={{ width: '13px', height: '13px' }}
                        >
                          <polyline points='9 11 12 14 22 4'></polyline>
                          <path d='M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'></path>
                        </svg>
                        {isBulkApproving ? 'Approving...' : 'Approve Visible Rest Hours'}
                      </button>
                    )}
                    <div className='form-check form-switch m-0'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        role='switch'
                        id='matrixLiveRolling'
                        checked={matrixLiveRolling}
                        onChange={(e) => setMatrixLiveRolling(e.target.checked)}
                      />
                      <label className='form-check-label small text-muted' htmlFor='matrixLiveRolling'>
                        Real-time violations {matrixRollingAnchor ? `(${formatHHmm(matrixRollingAnchor)})` : ''}
                      </label>
                    </div>
                  </div>
                )}
              </div>


              {/* Tab Content */}
              <div className='tab-content'>
                <div
                  className={`tab-pane fade ${activeTab === 'matrix-overview' ? 'show active' : ''
                    }`}
                  id='matrix-overview'
                >
                  {/* Reporting Day UI removed – compliance is pure rolling now */}

                  {/* Table */}
                  <div className='report-table'>
                    <div className='table-responsive table-scroll' style={{ overflowX: 'auto', position: 'relative' }}>
                      {/* LEFT CLIP MASK — prevents date cells from sliding under SR/NO, NAME, RANK, VESSEL */}
                      <div
                        style={{
                          position: 'sticky',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: `${LEFT_FREEZE_W}px`,
                          height: '100%',
                          borderRight: '2px solid #dee2e6',
                          background: '#f8f9fa',
                          zIndex: 8,
                          pointerEvents: 'none',
                        }}
                      />
                      <table
                        className='table table-bordered align-middle'
                        style={{ minWidth: '1500px' }}
                      >
                        <thead className='table-header text-start'>
                          <tr>
                            {/* SR/NO (sticky) */}
                            <th
                              className='text-center text-nowrap'
                              style={{
                                position: 'sticky',
                                left: 0,
                                backgroundColor: '#f8f9fa',
                                boxShadow: 'inset 0 0 0 9999px #f8f9fa',
                                zIndex: 13,
                                width: `${SR_STICKY_W}px`,
                                minWidth: `${SR_STICKY_W}px`,
                                maxWidth: `${SR_STICKY_W}px`,
                                // padding: '6px 8px'
                              }}
                            >
                              <div className='d-flex align-items-center'>
                                SR/NO
                              </div>
                            </th>
                            {/* NAME (sticky) */}
                            <th
                              onClick={() => handleSort('crewName')}
                              className='cursor-pointer text-start'
                              style={{
                                position: 'sticky',
                                left: `${NAME_STICKY_LEFT}px`,
                                backgroundColor: '#f8f9fa',
                                boxShadow: 'inset 0 0 0 9999px #f8f9fa',
                                zIndex: 12,
                                width: `${NAME_STICKY_W}px`,
                                minWidth: `${NAME_STICKY_W}px`,
                                maxWidth: `${NAME_STICKY_W}px`,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: 1.15,
                                // padding: '6px 8px',
                                // borderLeft: '1px solid #dee2e6',
                              }}
                            >
                              <div className='d-flex align-items-center'>
                                NAME
                                <div style={{ transform: 'translateY(-2px)' }}>
                                  <KTSVG
                                    path={`/media/map/sort-col-${sortConfig.key === 'crewName'
                                      ? sortConfig.direction === 'asc'
                                        ? 'up-black'
                                        : 'down-black'
                                      : 'grey'
                                      }.svg`}
                                    className='svg-icon ms-2 custom-sort-icon'
                                  />
                                </div>
                              </div>
                            </th>
                            {/* RANK (sticky) */}
                            <th
                              onClick={() => handleSort('rank')}
                              className='cursor-pointer text-start'
                              style={{
                                position: 'sticky',
                                left: `${RANK_STICKY_LEFT}px`,
                                backgroundColor: '#f8f9fa',
                                boxShadow: 'inset 0 0 0 9999px #f8f9fa',
                                zIndex: 11,
                                width: `${RANK_STICKY_W}px`,
                                minWidth: `${RANK_STICKY_W}px`,
                                maxWidth: `${RANK_STICKY_W}px`,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: 1.15,
                                // padding: '6px 8px',
                                // borderLeft: '1px solid #dee2e6',
                              }}
                            >
                              <div className='d-flex align-items-center'>
                                RANK
                                <div style={{ transform: 'translateY(-2px)' }}>
                                  <KTSVG
                                    path={`/media/map/sort-col-${sortConfig.key === 'rank'
                                      ? sortConfig.direction === 'asc'
                                        ? 'up-black'
                                        : 'down-black'
                                      : 'grey'
                                      }.svg`}
                                    className='svg-icon ms-2 custom-sort-icon'
                                  />
                                </div>
                              </div>
                            </th>

                            {/* VESSEL (sticky, after RANK) */}
                            <th
                              onClick={() => handleSort('vessel')}
                              className='cursor-pointer text-start rh-freeze-end'
                              style={{
                                position: 'sticky',
                                left: `${VESSEL_STICKY_LEFT}px`,
                                backgroundColor: '#f8f9fa',
                                boxShadow: 'inset 0 0 0 9999px #f8f9fa',
                                zIndex: 10,
                                width: `${VESSEL_STICKY_W}px`,
                                minWidth: `${VESSEL_STICKY_W}px`,
                                maxWidth: `${VESSEL_STICKY_W}px`,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: 1.15,
                                // padding: '6px 8px',
                                // borderLeft: '1px solid #dee2e6'
                              }}
                            >
                              <div className='d-flex align-items-center'>
                                VESSEL
                                <div style={{ transform: 'translateY(-2px)' }}>
                                  <KTSVG
                                    path={`/media/map/sort-col-${sortConfig.key === 'vessel'
                                      ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black')
                                      : 'grey'
                                      }.svg`}
                                    className='svg-icon ms-2 custom-sort-icon'
                                  />
                                </div>
                              </div>
                            </th>
                            {/* Date columns */}
                            {dateLabels.map((label, i) => {
                              const isWeekStart = weekStartIdxSet.has(i)
                              const isWeekEnd = weekEndIdxSet.has(i)
                              return (
                                <th
                                  key={i}
                                  className='text-center text-nowrap'
                                  style={{
                                    minWidth: '70px',
                                    // alternate a very light tint for visibility (only from the first full block onward)
                                    backgroundColor: isWeekStart ? '#f6fbff' : '#f8f9fa',
                                    // thick vertical line at 7d start
                                    borderLeft: isWeekStart ? '3px solid #9ec5fe' : '1px solid #d3d3d3',
                                    // thick bottom line at 7d end (visual horizontal cue)
                                    borderBottom: isWeekEnd ? '2px solid #9ec5fe' : '1px solid #d3d3d3',
                                  }}
                                  title={isWeekEnd ? 'End of a 7-day window' : undefined}
                                >
                                  {label}
                                </th>
                              )
                            })}
                          </tr>
                        </thead>
                        <tbody className='table-body text-start'>
                          {(isCrewLoading || isHoursLoading) ? (
                            <TableSkeleton rows={rowsPerPage || 10} cols={dateLabels.length + 4} />
                          ) : sortedCrew.length === 0 ? (
                            <tr>
                              <td colSpan={dateLabels.length + 4} className='text-center text-muted py-3'>
                                No rest hour records found for the selected criteria.
                              </td>
                            </tr>
                          ) : (
                            pagedCrew.map((record, idx) => {
                              const srNo = indexOfFirstRecord + idx + 1
                              return (
                                <tr key={idx}>
                                  {/* SR/NO (sticky) */}
                                  <td
                                    className='text-center'
                                    style={{
                                      position: 'sticky',
                                      left: 0,
                                      zIndex: 23,
                                      backgroundColor: '#f8f9fa',
                                      width: `${SR_STICKY_W}px`,
                                      minWidth: `${SR_STICKY_W}px`,
                                      maxWidth: `${SR_STICKY_W}px`,
                                      padding: '6px 8px',
                                      verticalAlign: 'middle',
                                      // borderBottom: '1px solid #dee2e6',
                                    }}
                                  >
                                    {srNo}
                                  </td>

                                  <td
                                    className='text-dark fw-bold fs-6 text-start'
                                    style={{
                                      position: 'sticky',
                                      left: `${NAME_STICKY_LEFT}px`,
                                      backgroundColor: '#f8f9fa',
                                      zIndex: 22,
                                      width: `${NAME_STICKY_W}px`,
                                      minWidth: `${NAME_STICKY_W}px`,
                                      maxWidth: `${NAME_STICKY_W}px`,
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.15,
                                      padding: '6px 8px',
                                      verticalAlign: 'middle',
                                      // borderLeft: '1px solid #dee2e6',
                                      // borderBottom: '1px solid #dee2e6',

                                    }}

                                  >
                                    <div className='d-flex align-items-center'>
                                      <div className='d-flex justify-content-start flex-column'>
                                        <span className='text-dark fw-bold text-hover-primary mb-1 fs-6'>
                                          {record.crewName}
                                        </span>
                                      </div>
                                    </div>
                                  </td>

                                  <td
                                    className='text-dark fw-bold fs-6 text-start'
                                    style={{
                                      position: 'sticky',
                                      left: `${RANK_STICKY_LEFT}px`,
                                      backgroundColor: '#f8f9fa',
                                      zIndex: 21,
                                      width: `${RANK_STICKY_W}px`,
                                      minWidth: `${RANK_STICKY_W}px`,
                                      maxWidth: `${RANK_STICKY_W}px`,
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.15,
                                      padding: '6px 8px',
                                      verticalAlign: 'middle',
                                      // borderLeft: '1px solid #dee2e6',
                                      // borderBottom: '1px solid #dee2e6',
                                    }}
                                  >
                                    {/* use rankMap fallback so it never shows blank */}
                                    {rankMap[Number(record?.rankInfo?.id ?? record?.rankId)] ??
                                      record?.rankInfo?.rank ??
                                      record?.rank ??
                                      '—'}
                                  </td>

                                  <td
                                    className='text-dark fw-bold fs-6 text-start rh-freeze-end'
                                    style={{
                                      position: 'sticky',
                                      left: `${VESSEL_STICKY_LEFT}px`,
                                      zIndex: 20,
                                      backgroundColor: '#f8f9fa',
                                      width: `${VESSEL_STICKY_W}px`,
                                      minWidth: `${VESSEL_STICKY_W}px`,
                                      maxWidth: `${VESSEL_STICKY_W}px`,
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.15,
                                      padding: '6px 8px',
                                      verticalAlign: 'middle',
                                      // borderLeft: '1px solid #dee2e6',
                                      // borderBottom: '1px solid #dee2e6',
                                    }}
                                    title={record?.vessel?.fleet_name ?? 'On Leave'}
                                  >
                                    {record?.vessel?.fleet_name ?? 'On Leave'}
                                  </td>


                                  {/* Date cells 1-31 */}
                                  {(() => {
                                    if (!fromDate || !toDate) return null

                                    const start = new Date(fromDate)
                                    const end = new Date(toDate)
                                    const cells: JSX.Element[] = []

                                    let current = new Date(start)
                                    let colIndex = 0
                                    while (current <= end) {
                                      const dateKey = current.toLocaleDateString('en-CA') // YYYY-MM-DD
                                      const key = `${record.crewId}-${dateKey}`
                                      const entry = hoursMap[key]
                                      const value = entry
                                        ? showWorkHours
                                          ? entry.work
                                          : entry.rest
                                        : null

                                      // visibility by role:
                                      const rId = roleId
                                      const isShoreLimited = rId === 2 || rId === 5 || (rId === 6 && operatorActsLikeGroupAdmin) // Operator acting like Superadmin is NOT shore-limited
                                      const visible = entry ? !isShoreLimited || entry.approved : false

                                      // Approval rights: superadmin OR rank Master (any suffix)
                                      const canApprove = rId === 1 || isRankMaster
                                      // Show approval badge only when server marks the day approved; do not render approve button for pending days
                                      const showApprovedBadge = entry?.approved === true

                                      // Tooltip text for violations
                                      const hasRemarks = !!entry?.remarks && String(entry.remarks).trim().length > 0
                                      const rollingKey = `${record.crewId}-${dateKey}`
                                      const rollingInfo = matrixRollingViolations[rollingKey]
                                      const hasDataForDay = dayHasDataMap.get(dateKey) ?? true
                                      const rollingViolations = hasDataForDay ? rollingInfo?.violations ?? [] : []
                                      const rollingViolationCodes = hasDataForDay ? rollingInfo?.violationCodes ?? [] : []
                                      const rollingWindows = hasDataForDay ? rollingInfo?.windows ?? [] : []
                                      const isRollingOwned = rollingInfo != null && rollingViolationCodes.length > 0
                                      const isRed = isRollingOwned || (entry?.violationCount ?? 0) > 0
                                      const combinedViolations = rollingViolations
                                      const combinedViolationCodes = rollingViolationCodes
                                      if (debugMode && ['2026-03-10', '2026-03-11', '2026-03-12'].includes(dateKey)) {
                                        console.debug('[RestHour][source-check]', {
                                          dateKey,
                                          restRangeRuleCodes: entry?.violationCodes ?? [],
                                          rollingRangeRuleCodes: rollingViolationCodes ?? [],
                                          usedRuleCodes: combinedViolationCodes ?? [],
                                        })
                                      }
                                      const exceptionKey = `${record.crewId}-${dateKey}`
                                      const isApproved = entry?.approved === true
                                      const restHours = Number(entry?.rest ?? 24)
                                      const isExceptionEligible =
                                        regulation === 'stcw' &&
                                        allowExceptions &&
                                        restHours >= 6 &&
                                        restHours < 10 &&
                                        !isApproved
                                      const isExceptionSelected = exceptionOverrides.has(exceptionKey)
                                      const hasException = matrixExceptionDays.has(exceptionKey)
                                      const canAddExceptionInWindow =
                                        isExceptionEligible &&
                                        canAddException(record.crewId, dateKey, dateKeys, exceptionOverrides)
                                      const showExceptionToggle =
                                        regulation === 'stcw' &&
                                        allowExceptions &&
                                        !isApproved &&
                                        (isExceptionSelected || canAddExceptionInWindow)
                                      const filteredViolations = hasException ? [] : combinedViolations
                                      const filteredViolationCodes = hasException ? [] : combinedViolationCodes
                                      // UI state is driven purely by current reporting-day evaluation (rolling windows + mapped violations)
                                      const hasViolation =
                                        !hasException &&
                                        (isRed ||
                                          filteredViolations.length > 0 ||
                                          filteredViolationCodes.length > 0 ||
                                          (rollingWindows?.length ?? 0) > 0)
                                      const violationTooltip =
                                        filteredViolations.length > 0
                                          ? filteredViolations.join('\n')
                                          : (rollingWindows?.length ?? 0) > 0
                                            ? `${rollingWindows.length} rolling violation(s)\n${windowSummary(rollingWindows[0])}`
                                            : ''
                                      const exceptionTooltip = hasException
                                        ? '\nException applied (rest 6-10h; max 2 per 7d)'
                                        : ''
                                      const rollingWindowTooltip = (() => {
                                        if (!matrixRollingAnchor) return ''
                                        const [yyyy, mm, dd] = dateKey.split('-').map(Number)
                                        if (!yyyy || !mm || !dd) return ''
                                        const end = new Date(
                                          yyyy,
                                          mm - 1,
                                          dd,
                                          matrixRollingAnchor.getHours(),
                                          matrixRollingAnchor.getMinutes(),
                                          0,
                                          0
                                        )
                                        const start = new Date(end.getTime() - 24 * 60 * 60 * 1000)
                                        return `\nRolling 24h: ${formatMatrixRollingDateTime(
                                          start
                                        )} -> ${formatMatrixRollingDateTime(end)}`
                                      })()


                                      cells.push(
                                        <td
                                          key={dateKey}
                                          className='text-center text-nowrap'
                                          // Show day-level tooltips from server + 7d overlay in title
                                          title={[
                                            violationTooltip,
                                            matrixAnchored7d[`${record.crewId}-${dateKey}`]
                                              ? `\n7d window ${matrixAnchored7d[`${record.crewId}-${dateKey}`]
                                                .windowStart
                                              } ? ${dateKey}: ` +
                                              `${Math.round(
                                                matrixAnchored7d[`${record.crewId}-${dateKey}`]
                                                  .totalRest
                                              )}h`
                                              : '',
                                            hasRemarks ? `\nRemarks: ${String(entry.remarks)}` : '',
                                            exceptionTooltip,
                                            rollingWindowTooltip
                                          ].join('')}
                                          style={{
                                            minWidth: weekEndIdxSet.has(colIndex)
                                              ? '150px'
                                              : '70px',
                                            // reserve side padding so the chip never overlaps the badge in the middle
                                            padding: `8px ${(IS_WEEK_CHIP_RIGHT ? WEEK_CHIP_RESERVED : 8) +
                                              (showApprovedBadge ? APPROVE_BTN_RESERVED : 0)
                                              }px 8px ${IS_WEEK_CHIP_RIGHT ? 8 : WEEK_CHIP_RESERVED}px`,
                                            position: 'relative',
                                            // day-level background (server)
                                            backgroundColor: hasViolation ? '#ffcccc' : 'transparent',
                                            // thick vertical line at 7d start for this column
                                            borderLeft: weekStartIdxSet.has(colIndex)
                                              ? '3px solid #9ec5fe'
                                              : '1px solid #d3d3d3',
                                            // thick bottom line at 7d end for this column
                                            borderBottom: weekEndIdxSet.has(colIndex)
                                              ? '1px solid #9ec5fe'
                                              : "1px solid #d3d3d3",

                                            overflow: 'hidden', // ensure chip never pushes layout
                                          }}
                                        >
                                          {hasRemarks && (
                                            <i
                                              className="bi bi-chat-square-text-fill"
                                              title={String(entry!.remarks)}
                                              style={{
                                                // position: 'absolute',
                                                left: 6,
                                                top: 4,
                                                fontSize: 14,
                                                color: '#800080',        // Bootstrap purple
                                                cursor: 'pointer',
                                                zIndex: 3
                                              }}
                                            >
                                              {/* 
 */}
                                            </i>
                                          )}

                                          {rollingWindows.length > 0 && (
                                            <button
                                              type='button'
                                              className='btn btn-sm btn-danger rounded-circle'
                                              style={{
                                                position: 'absolute',
                                                top: 4,
                                                left: showApprovedBadge ? 36 : 8,
                                                width: 28,
                                                height: 28,
                                                padding: 0,
                                                lineHeight: 1.2,
                                                zIndex: 4,
                                              }}
                                              title={`${rollingWindows.length} rolling violation(s)`}
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                openViolationDrawer(record.crewId, dateKey)
                                              }}
                                            >
                                              {rollingWindows.length}
                                            </button>
                                          )}

                                          {/* primary value (work/rest) or pending */}
                                          {visible && value !== null ? (
                                            <div className='d-inline-flex flex-column align-items-center gap-1 ms-4'>
                                              <span
                                                className='badge bg-primary text-white p-2'
                                                style={{ fontSize: '11px' }}
                                              >
                                                {Number(value).toFixed(1)}h
                                              </span>
                                              {hasException && (
                                                <span
                                                  className='badge bg-info text-white'
                                                  style={{ fontSize: '10px', padding: '2px 6px' }}
                                                >
                                                  Exception
                                                </span>
                                              )}
                                            </div>
                                          ) : (
                                            <span className='text-muted'>
                                              {entry ? (entry.approved ? '-' : 'Pending') : '-'}
                                            </span>
                                          )}
                                          {showExceptionToggle && (
                                            <div className='mt-1'>
                                              <button
                                                type='button'
                                                className={`btn btn-sm ${isExceptionSelected ? 'btn-info' : 'btn-outline-info'
                                                  }`}
                                                style={{ fontSize: '10px', padding: '2px 6px' }}
                                                title={
                                                  isExceptionSelected
                                                    ? 'Exception selected (click to remove)'
                                                    : 'Mark exception (6-10h rest, max 2 per 7d)'
                                                }
                                                onClick={() =>
                                                  toggleExceptionDay(
                                                    record.crewId,
                                                    dateKey,
                                                    dateKeys,
                                                    isExceptionEligible
                                                  )
                                                }
                                              >
                                                {isExceptionSelected ? 'Exception' : 'Mark Exception'}
                                              </button>
                                            </div>
                                          )}

                                          {/* approval badge (top-right) */}
                                          {showApprovedBadge && (
                                            <span
                                              className='badge bg-success'
                                              style={{
                                                position: 'absolute',
                                                right: 4,
                                                top: 4,
                                                padding: '4px 6px',
                                                lineHeight: 1,
                                                zIndex: 2,
                                              }}
                                            >
                                              Approved
                                            </span>
                                          )}



                                          {/* 7d chip on END of each anchored 7d window — side, vertically centered */}
                                          {/* {(() => {
                                        const info = matrixAnchored7d[`${record.crewId}-${dateKey}`]
                                        if (!info) return null // only on week-end days
                                        const breached = info.violated
                                        const sideStyle = IS_WEEK_CHIP_RIGHT
                                          ? ({right: 4} as const)
                                          : ({left: 4} as const)

                                        return (
                                          <div
                                            style={{
                                              position: 'absolute',
                                              top: '50%',
                                              transform: 'translateY(-50%)',
                                              ...sideStyle,
                                              fontSize: 10,
                                              padding: '2px 6px',
                                              borderRadius: 6,
                                              border: '1px solid',
                                              borderColor: breached ? '#ff6b6b' : '#7ed491',
                                              backgroundColor: breached ? '#ffebeb' : '#ebfff0',
                                              color: breached ? '#a40000' : '#0b5137',
                                              whiteSpace: 'nowrap',
                                              pointerEvents: 'none', // don't block clicks
                                              zIndex: 1, // under approve button
                                              maxWidth: WEEK_CHIP_RESERVED + 4, // defensive
                                              textOverflow: 'ellipsis',
                                              overflow: 'hidden',
                                              marginRight: '28px',
                                              paddingRight: '4px'
                                            }}
                                            title={`7-day total rest (${info.windowStart} ? ${dateKey})`}
                                          >
                                            {breached ? '?? ' : ''}7d {Math.round(info.totalRest)}h
                                          </div>
                                        )
                                      })()} */}
                                          {/* 7d chip on END of each anchored 7d window — side, vertically centered (regulation-aware) */}
                                          {(() => {
                                            const wkRestInfo = matrixAnchored7d[`${record.crewId}-${dateKey}`];         // { windowStart, totalRest }
                                            const wkWorkInfo = matrixAnchored7dWork?.[`${record.crewId}-${dateKey}`];   // { windowStart, totalWork }
                                            const violationCodes = entry?.violationCodes ?? []

                                            // only render chips on anchored week-end days we have data for
                                            const hasWeekEndData =
                                              (regulation === 'stcw' && !!wkRestInfo) ||
                                              (regulation === 'mlc' && !!wkWorkInfo);
                                            if (!hasWeekEndData) return null;

                                            // breach logic by regulation
                                            const breached =
                                              regulation === 'stcw'
                                                ? violationCodes.includes('STCW_MIN_REST_7D')
                                                : violationCodes.some((code) =>
                                                  code === 'MLC_MAX_WORK_7D' ||
                                                  code === 'MLC_MIN_REST_7D' ||
                                                  code === 'MLC_MIN_REST_7D_FLOOR70'
                                                );

                                            // chip text & tooltip title by regulation
                                            const chipText =
                                              regulation === 'stcw'
                                                ? `7d ${Math.round(wkRestInfo!.totalRest)}h`
                                                : `7d ${Math.round(wkWorkInfo!.totalWork)}h`;

                                            const chipTitle =
                                              regulation === 'stcw'
                                                ? `7-day total rest (${wkRestInfo!.windowStart} ? ${dateKey})`
                                                : `7-day total work (${wkWorkInfo!.windowStart} ? ${dateKey})`;

                                            const sideStyle = IS_WEEK_CHIP_RIGHT ? ({ right: 4 } as const) : ({ left: 4 } as const);

                                            return (
                                              <div
                                                style={{
                                                  position: 'absolute',
                                                  top: '50%',
                                                  transform: 'translateY(-50%)',
                                                  ...sideStyle,
                                                  fontSize: 10,
                                                  padding: '2px 6px',
                                                  borderRadius: 6,
                                                  border: '1px solid',
                                                  borderColor: breached ? '#ff6b6b' : '#7ed491',
                                                  backgroundColor: breached ? '#ffebeb' : '#ebfff0',
                                                  color: breached ? '#a40000' : '#0b5137',
                                                  whiteSpace: 'nowrap',
                                                  pointerEvents: 'none',   // don't block clicks
                                                  zIndex: 1,               // under approve button
                                                  maxWidth: WEEK_CHIP_RESERVED + 4,
                                                  textOverflow: 'ellipsis',
                                                  overflow: 'hidden',
                                                  marginRight: '28px',
                                                  paddingRight: '4px',
                                                }}
                                                title={chipTitle}
                                              >
                                                {breached ? '?? ' : ''}{chipText}
                                              </div>
                                            )
                                          })()}

                                        </td>
                                      )

                                      current.setDate(current.getDate() + 1)
                                      colIndex++
                                    }

                                    return cells
                                  })()}

                                </tr>
                              )
                            }
                            ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className='pagination-wrapper d-flex justify-content-between align-items-center py-3'>
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-2'>Rows per page</span>
                        <SearchableSelect
                          className='form-select'
                          style={{
                            borderRadius: '20px',
                            width: '70px',
                            border: '1px solid #dee2e6',
                            fontSize: '14px',
                            padding: '4px 8px',
                          }}
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </SearchableSelect>
                      </div>
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                          Showing{' '}
                          <strong>
                            {sortedCrew.length === 0 ? 0 : indexOfFirstRecord + 1}-
                            {Math.min(indexOfLastRecord, sortedCrew.length)}
                          </strong>{' '}
                          of <strong>{sortedCrew.length}</strong>
                        </span>

                        <nav>
                          <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px',
                                }}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                ‹
                              </button>
                            </li>

                            {(() => {
                              const pages = []
                              const showPages = 5 // Show 5 page numbers at most
                              let startPage = Math.max(1, currentPage - 2)
                              let endPage = Math.min(totalPages, startPage + showPages - 1)

                              // Adjust start if we're near the end
                              if (endPage - startPage < showPages - 1) {
                                startPage = Math.max(1, endPage - showPages + 1)
                              }

                              if (startPage > 1) {
                                pages.push(
                                  <li key={1} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                      }}
                                      onClick={() => handlePageChange(1)}
                                    >
                                      1
                                    </button>
                                  </li>
                                )

                                if (startPage > 2) {
                                  pages.push(
                                    <li key='ellipsis1' className='page-item disabled'>
                                      <span
                                        className='page-link border-0 text-muted'
                                        style={{ backgroundColor: 'transparent', padding: '4px 8px' }}
                                      >
                                        ...
                                      </span>
                                    </li>
                                  )
                                }
                              }

                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                  <li
                                    key={i}
                                    className={`page-item ${currentPage === i ? 'active' : ''}`}
                                  >
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor:
                                          currentPage === i ? '#F4F9FF' : 'transparent',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                        outline: 'none',
                                        boxShadow: 'none',
                                      }}
                                      onClick={() => handlePageChange(i)}
                                    >
                                      {i}
                                    </button>
                                  </li>
                                )
                              }

                              if (endPage < totalPages) {
                                if (endPage < totalPages - 1) {
                                  pages.push(
                                    <li key='ellipsis2' className='page-item disabled'>
                                      <span
                                        className='page-link border-0 text-muted'
                                        style={{ backgroundColor: 'transparent', padding: '4px 8px' }}
                                      >
                                        ...
                                      </span>
                                    </li>
                                  )
                                }

                                pages.push(
                                  <li key={totalPages} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                      }}
                                      onClick={() => handlePageChange(totalPages)}
                                    >
                                      {totalPages}
                                    </button>
                                  </li>
                                )
                              }

                              return pages
                            })()}

                            <li
                              className={`page-item ${currentPage === totalPages ? 'disabled' : ''
                                }`}
                            >
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px',
                                }}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                              >
                                ›
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Update Tab - Rest Hour Schedule */}
                <UpdateTab
                  activeTab={activeTab}
                  fromDate={updateFromISO}
                  toDate={updateToISO}
                  signOnStartISO={signOnStartISO}
                  onChangeFromDate={handleUpdateFromChange}
                  onChangeToDate={handleUpdateToChange}
                  onResetRange={resetUpdateRange}
                  activeSignOn={activeSignOn}
                  selectedRank={selectedRank}
                  selectedCrew={selectedCrew}
                  crewMembers={crewOptions}
                  timeSlots={timeSlots}
                  // isWorkingHour={isWorkingHour}
                  toggleWorkingHour={toggleWorkingHour}
                  setWorkingHours={setWorkingHours}
                  showTable={showTable}
                  // onSaveWorkingHours={handleSaveWorkingHours} // ADD THIS LINE
                  remarks={remarks}
                  onRemarksChange={handleRemarksChange}
                  onLocalSummaryChange={patchMatrixSummary}
                  onAfterSave={handleAfterSave}
                  approvedDates={approvedDatesForUpdateTab}
                  apiEntries={apiEntries}
                  setApiEntries={setApiEntries}
                  getSummaryId={(cid, dateISO) => hoursMap[`${cid}-${dateISO}`]?.summaryId ?? null}
                  dirtyRemarkDates={dirtyRemarkDates}
                  onResetRemarkEdits={resetRemarkEdits}
                  regulation={regulation}
                  allowExceptions={allowExceptions}
                  exceptionOverrides={exceptionOverrides}
                  reportingMode={reportingMode}
                  customAnchorHHmm='00:00'
                  onReportingModeChange={() => { }}
                  onCustomAnchorChange={() => { }}
                  onToggleExceptionDay={toggleExceptionDay}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Toasts */}
      {violationDrawerDay && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '360px',
            height: '100%',
            background: '#ffffff',
            boxShadow: '-2px 0 12px rgba(0,0,0,0.25)',
            zIndex: 2050,
            overflowY: 'auto',
          }}
        >
          <div className='d-flex align-items-center justify-content-between border-bottom p-3'>
            <div>
              <div className='fw-bold'>Rolling violations</div>
              <div className='text-muted small'>Date: {violationDrawerDay}</div>
            </div>
            <button className='btn btn-sm btn-light' onClick={closeViolationDrawer}>
              Close
            </button>
          </div>
          <div className='p-3'>
            {violationDrawerItems.length === 0 && (
              <div className='text-muted'>No violations for this day.</div>
            )}
            {violationDrawerItems.map((v, idx) => (
              <div key={`${v.ruleCode}-${idx}`} className='mb-3 border rounded p-2'>
                <div className='d-flex justify-content-between'>
                  <span className='fw-semibold'>{humanizeViolation(v, {
                    reportingTz: matrixReportingTz,
                    reportingMode,
                    customAnchorHHmm,
                    signOnStartISO: activeSignOn?.onboardFrom ?? null,
                  })}</span>
                  <span className='badge bg-danger text-white'>{v.framework}</span>
                </div>
                <div className='text-muted small'>{v.ruleCode}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <ToastContainer position='top-center' newestOnTop closeOnClick draggable pauseOnHover />

      {/* Add Rest Hour Modal */}
      <AddRestHourModal
        visible={isModalVisible}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
      />

      {/* Export Modal */}
      {isExportOpen && (
        <>
          <div className='modal fade show' style={{ display: 'block' }} role='dialog' aria-modal='true' tabIndex={-1}>
            <div className='modal-dialog modal-lg modal-dialog-centered'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>Export Model Format</h5>
                  <button type='button' className='btn-close' onClick={() => setIsExportOpen(false)} />
                </div>

                <div className='modal-body'>
                  <div className='mb-4'>
                    <label className='form-label fw-semibold'>Format</label>
                    <div className='d-flex gap-3 mb-3'>
                      <label className='form-check form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='exportType'
                          value='pdf'
                          checked={exportType === 'pdf'}
                          onChange={() => setExportType('pdf')}
                        />
                        <span className='form-check-label'>PDF</span>
                      </label>

                      <label className='form-check form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='exportType'
                          value='excel'
                          checked={exportType === 'excel'}
                          onChange={() => setExportType('excel')}
                        />
                        <span className='form-check-label'>Excel</span>
                      </label>
                    </div>
                  </div>

                  <div className='alert alert-info py-2 small mb-4'>
                    Export Model Format data for your selected date range. Future dates are not allowed.
                  </div>

                  {exportType === 'pdf' ? (
                    <>
                      <div className='row g-3'>
                        <div className='col-md-6'>
                          <label className='form-label'>From Date <span className='text-danger'>*</span></label>
                          <input
                            type='date'
                            className='form-control'
                            value={pdfFrom}
                            onChange={(e) => setPdfFrom(e.target.value)}
                            max={pdfTo || todayISO}
                          />
                        </div>
                        <div className='col-md-6'>
                          <label className='form-label'>To Date <span className='text-danger'>*</span></label>
                          <input
                            type='date'
                            className='form-control'
                            value={pdfTo}
                            onChange={(e) => setPdfTo(e.target.value)}
                            max={todayISO}
                            min={pdfFrom}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className='row g-3'>
                        <div className='col-md-6'>
                          <label className='form-label'>From Date <span className='text-danger'>*</span></label>
                          <input
                            type='date'
                            className='form-control'
                            value={excelFrom}
                            onChange={(e) => setExcelFrom(e.target.value)}
                            max={excelTo || todayISO}
                          />
                        </div>
                        <div className='col-md-6'>
                          <label className='form-label'>To Date <span className='text-danger'>*</span></label>
                          <input
                            type='date'
                            className='form-control'
                            value={excelTo}
                            onChange={(e) => setExcelTo(e.target.value)}
                            max={todayISO}
                            min={excelFrom}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className='modal-footer'>
                  <button className='btn btn-light' onClick={() => setIsExportOpen(false)}>Cancel</button>

                  {exportType === 'pdf' ? (
                    <button
                      className='btn btn_primary'
                      onClick={() => exportWeekToPDF(pdfFrom, pdfTo)}
                      title={requiresVessel && typeof filterVesselId !== 'number' ? 'Select a Vessel first' : ''}
                    >
                      Export PDF
                    </button>
                  ) : (
                    <button
                      className='btn btn_primary'
                      onClick={exportModelFormatToExcel}
                      title={requiresVessel && typeof filterVesselId !== 'number' ? 'Select a Vessel first' : ''}
                    >
                      Export Excel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" onClick={() => setIsExportOpen(false)} />
        </>
      )}


    </div>
  )
}

export default RestHour
